// ── Block renderers for the editor canvas ─────────────────────────────────────

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import type { BlockState } from '../../store/editor.store'
import { useEditorStore } from '../../store/editor.store'
import { mediaApi } from '../../utils/api'
import { useDevice } from '../../contexts/DeviceContext'
import { getSwissPageHtml } from '../../utils/swissPageHtml'
import { mergeSwissData } from '../../types/swissPage'
import { getGlassmorphPageHtml } from '../../utils/glassmorphPageHtml'
import {
  getSwissLandingPageHtml, getBrutalLandingPageHtml, getSerifLandingPageHtml,
  getCinemaLandingPageHtml, getMaximalLandingPageHtml, getY2kLandingPageHtml,
  getOrganicLandingPageHtml, getFilmLandingPageHtml, getBentoLandingPageHtml,
  getBentoPortfolioPageHtml, getCinemaPortfolioPageHtml, getFilmPortfolioPageHtml,
  getMinimalPortfolioPageHtml, getOrganicPortfolioPageHtml,
  getSwissPortfolioPageHtml, getBrutalPortfolioPageHtml, getImmersivePortfolioPageHtml,
  getNeoPortfolioPageHtml, studioPageHtml,
} from '@bst/blocks'

export interface RendererProps {
  block: BlockState
  selected: boolean
  editing?: boolean
  onUpdate?: (data: Record<string, unknown>) => void
}

// ── Inline edit hook ──────────────────────────────────────────────────────────
function useInlineEdit(
  value: string,
  field: string,
  editing: boolean | undefined,
  onUpdate: ((data: Record<string, unknown>) => void) | undefined,
) {
  const ref = useRef<HTMLElement>(null)

  useEffect(() => {
    if (editing && ref.current) {
      ref.current.textContent = value
      ref.current.focus()
      try {
        const range = document.createRange()
        range.selectNodeContents(ref.current)
        range.collapse(false)
        window.getSelection()?.removeAllRanges()
        window.getSelection()?.addRange(range)
      } catch { /* ignore */ }
    }
  }, [editing])

  const onBlur = useCallback((e: React.FocusEvent<HTMLElement>) => {
    const newVal = e.currentTarget.textContent ?? ''
    if (onUpdate) onUpdate({ [field]: newVal })
  }, [field, onUpdate])

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>) => {
    e.stopPropagation()
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); (e.currentTarget as HTMLElement).blur() }
    if (e.key === 'Escape') {
      if (ref.current) ref.current.textContent = value
      ;(e.currentTarget as HTMLElement).blur()
    }
  }, [value])

  return { ref, onBlur, onKeyDown }
}

// ── Full-page iframe block (monolithic landing/portfolio pages) ───────────────
const SCAN_EXCLUDE_IDS = new Set(['clock', 'switcher', 'emblem', 'heroImg', 'geo'])

const FONT_CHOICES = [
  { label: '— по умолчанию —', value: '' },
  { label: 'Inter', value: 'Inter' },
  { label: 'Roboto', value: 'Roboto' },
  { label: 'Montserrat', value: 'Montserrat' },
  { label: 'Unbounded', value: 'Unbounded' },
  { label: 'Space Grotesk', value: 'Space Grotesk' },
  { label: 'Plus Jakarta Sans', value: 'Plus Jakarta Sans' },
  { label: 'Bebas Neue', value: 'Bebas Neue' },
  { label: 'Anton', value: 'Anton' },
  { label: 'Oswald', value: 'Oswald' },
  { label: 'Bricolage Grotesque', value: 'Bricolage Grotesque' },
  { label: 'Playfair Display', value: 'Playfair Display' },
  { label: 'Fraunces', value: 'Fraunces' },
  { label: 'Lora', value: 'Lora' },
  { label: 'DM Serif Display', value: 'DM Serif Display' },
  { label: 'IBM Plex Mono', value: 'IBM Plex Mono' },
  { label: 'JetBrains Mono', value: 'JetBrains Mono' },
  { label: 'Dancing Script', value: 'Dancing Script' },
]

function injectIframeFont(doc: Document, fontName: string) {
  if (!fontName || !doc?.head) return
  const id = `__gf_${fontName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}__`
  if (doc.getElementById(id)) return
  const link = doc.createElement('link') as HTMLLinkElement
  link.id = id; link.rel = 'stylesheet'
  link.href = `https://fonts.googleapis.com/css2?family=${fontName.replace(/ /g, '+')}:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400&display=swap`
  doc.head.appendChild(link)
}

function applyPageTextOverrides(doc: Document, overrides: Record<string, string>) {
  // ── Font overrides ─────────────────────────────────────────────────────────
  const fd = overrides['__fd__'], fb = overrides['__fb__']
  if (fd || fb) {
    try {
      let s = doc.getElementById('__bst_fonts__') as HTMLStyleElement | null
      if (!s) { s = doc.createElement('style') as HTMLStyleElement; s.id = '__bst_fonts__'; doc.head.appendChild(s) }
      const imp = [
        fd ? `@import url('https://fonts.googleapis.com/css2?family=${fd.replace(/ /g,'+')}:wght@400;500;600;700;800&display=swap');` : '',
        fb ? `@import url('https://fonts.googleapis.com/css2?family=${fb.replace(/ /g,'+')}:wght@400;600;700&display=swap');` : '',
      ].filter(Boolean).join('\n')
      const vars = [fd ? `--fd:'${fd}',system-ui;` : '', fb ? `--fb:'${fb}',system-ui;` : ''].filter(Boolean).join('')
      s.textContent = `${imp}\n[data-theme]{${vars}}`
    } catch {}
  }

  Object.entries(overrides).forEach(([id, text]) => {
    try {
      // ── Marquee ─────────────────────────────────────────────────────────────
      if (id === 'marq') {
        // Support both legacy ✦-separated format and new comma-separated format
        const rawList = text.includes('✦') ? text.split('✦') : text.split(',')
        const seen = new Set<string>()
        const words = rawList.map(w => w.trim()).filter(w => { if (!w || seen.has(w)) return false; seen.add(w); return true })
        if (words.length > 0) {
          const chunk = words.map(w => `<span class="mq-w">${w}</span><span class="mq-d">✦</span>`).join('')
          doc.querySelectorAll('.mq-inner').forEach(m => { (m as HTMLElement).innerHTML = chunk.repeat(4) })
        }
        return
      }
      // ── Font special keys ──────────────────────────────────────────────────
      if (id === '__fd__' || id === '__fb__') return
      // ── Image src overrides ───────────────────────────────────────────────
      if (id.endsWith('__src')) {
        const baseKey = id.slice(0, -5)
        const imgEl: HTMLImageElement | null =
          doc.querySelector<HTMLImageElement>(`img[data-img="${baseKey}"]`) ??
          (doc.getElementById(baseKey) as HTMLImageElement | null) ??
          doc.querySelector<HTMLImageElement>(`[data-t="${baseKey}"] img, [data-bind="${baseKey}"] img`)
        if (imgEl) imgEl.src = text
        return
      }
      // ── Image position/crop overrides ─────────────────────────────────────
      if (id.endsWith('__pos')) {
        const baseKey = id.slice(0, -5)
        const imgEl: HTMLImageElement | null =
          doc.querySelector<HTMLImageElement>(`img[data-img="${baseKey}"]`) ??
          (doc.getElementById(baseKey) as HTMLImageElement | null) ??
          doc.querySelector<HTMLImageElement>(`[data-t="${baseKey}"] img, [data-bind="${baseKey}"] img`)
        if (imgEl) { imgEl.style.objectFit = 'cover'; imgEl.style.objectPosition = text }
        return
      }
      // ── Link/href overrides ────────────────────────────────────────────────
      if (id.endsWith('__href')) {
        const baseKey = id.slice(0, -6)
        const base = doc.getElementById(baseKey)
          ?? doc.querySelector<HTMLElement>(`[data-t="${baseKey}"]`)
          ?? doc.querySelector<HTMLElement>(`[data-bind="${baseKey}"]`)
        if (!base) return
        const linkEl = base.closest('a') ?? base.closest('button')
        if (!linkEl) return
        if (linkEl.tagName === 'A') {
          ;(linkEl as HTMLAnchorElement).href = text
          linkEl.setAttribute('target', '_blank')
          linkEl.setAttribute('rel', 'noopener noreferrer')
        } else {
          ;(linkEl as HTMLElement).setAttribute('data-bst-href', text)
          ;(linkEl as HTMLElement).onclick = () => { if (text) window.open(text, '_blank') }
        }
        return
      }
      // ── Per-element font-family override ─────────────────────────────────
      if (id.endsWith('__ff')) {
        const baseKey = id.slice(0, -4)
        const target = doc.getElementById(baseKey) ?? doc.querySelector<HTMLElement>(`[data-t="${baseKey}"]`) ?? doc.querySelector<HTMLElement>(`[data-bind="${baseKey}"]`)
        if (!target) return
        if (text) { target.style.fontFamily = `'${text}', system-ui, sans-serif`; injectIframeFont(doc, text) }
        else target.style.removeProperty('font-family')
        return
      }
      // ── Per-element font-size override ─────────────────────────────────────
      if (id.endsWith('__fs')) {
        const baseKey = id.slice(0, -4)
        const target = doc.getElementById(baseKey) ?? doc.querySelector<HTMLElement>(`[data-t="${baseKey}"]`) ?? doc.querySelector<HTMLElement>(`[data-bind="${baseKey}"]`)
        if (!target) return
        if (text) target.style.fontSize = text
        else target.style.removeProperty('font-size')
        return
      }
      // ── Normal text override ───────────────────────────────────────────────
      const el = doc.getElementById(id)
             ?? doc.querySelector<HTMLElement>(`[data-t="${id}"]`)
             ?? doc.querySelector<HTMLElement>(`[data-bind="${id}"]`)
      if (!el) return
      // ── Stat value (st1v…st4v): parse "120+" → update data-cnt + .suf separately
      if (/^st[1-4]v$/.test(id)) {
        const numSpan = el.querySelector<HTMLElement>('[data-cnt]')
        const sufSpan = el.querySelector<HTMLElement>('.suf')
        if (numSpan && sufSpan) {
          const m = text.match(/^(\d+(?:[.,]\d+)?)(.*)/)
          if (m) {
            numSpan.dataset.cnt = m[1].replace(',', '.')
            numSpan.textContent = m[1]
            sufSpan.textContent = m[2]
          } else {
            el.textContent = text
          }
          return
        }
      }
      // ── numCnt: update data-cnt so counter animation uses the override value
      if (id === 'numCnt') {
        const numVal = parseFloat(text)
        if (!isNaN(numVal)) el.dataset.cnt = String(numVal)
        el.textContent = text
        return
      }
      el.innerHTML = text
    } catch {}
  })
}

function FullPageIframeBlock({
  block,
  getHtml, defaultBrand = '', title, previewWidth = 1440,
  selected, onUpdate,
}: {
  block: BlockState
  getHtml: (brand: string, mode: 'canvas' | 'preview') => string
  defaultBrand?: string
  title: string
  previewWidth?: number
  selected?: boolean
  onUpdate?: (data: Record<string, unknown>) => void
}) {
  const brand = (block.data.brand as string) ?? defaultBrand
  const [height, setHeight] = useState(5000)
  const [containerWidth, setContainerWidth] = useState(900)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const isPreview = typeof window !== 'undefined' && window.location.pathname === '/preview'
  const srcDoc = useMemo(() => getHtml(brand, isPreview ? 'preview' : 'canvas'), [brand, isPreview, getHtml])
  const scale = containerWidth / previewWidth

  const textOverridesRef = useRef<Record<string, string>>({})
  textOverridesRef.current = (block.data.textOverrides as Record<string, string> | undefined) ?? {}

  // Inline click-to-edit state
  const [inlineEdit, setInlineEdit] = useState<{ key: string; origText: string; x: number; y: number; w: number; isLinkable?: boolean; origHref?: string; isImage?: boolean; origSrc?: string; origPos?: { x: number; y: number }; origFontFamily?: string; origFontSize?: string } | null>(null)
  const [editText, setEditText] = useState('')
  const [editHref, setEditHref] = useState('')
  const [editPos, setEditPos] = useState({ x: 50, y: 50 })
  const [editFontFamily, setEditFontFamily] = useState('')
  const [editFontSize, setEditFontSize] = useState('')
  const [imgUploading, setImgUploading] = useState(false)
  const isDraggingCropRef = useRef(false)
  const cropPickerRef = useRef<HTMLDivElement>(null)
  const editInputRef = useRef<HTMLTextAreaElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)

  const handleLoad = useCallback(() => {
    setTimeout(() => {
      try {
        const iframe = iframeRef.current
        const doc = iframe?.contentDocument
        if (!iframe || !doc) return
        // Temporarily shrink iframe so min-height:100vh (= 5000px) doesn't inflate body.scrollHeight
        iframe.style.height = '1px'
        const h = doc.body.scrollHeight
        iframe.style.height = `${h}px`
        if (h > 500) setHeight(h)
      } catch {}
    }, 800)
    // Second pass for slow-loading fonts/images
    setTimeout(() => {
      try {
        const iframe = iframeRef.current
        const doc = iframe?.contentDocument
        if (!iframe || !doc) return
        iframe.style.height = '1px'
        const h = doc.body.scrollHeight
        iframe.style.height = `${h}px`
        if (h > 500) setHeight(h)
      } catch {}
    }, 2200)
    setTimeout(() => {
      try {
        const doc = iframeRef.current?.contentDocument
        if (!doc) return
        const texts: Record<string, string> = {}
        const addEl = (el: Element, key: string) => {
          if (!key || SCAN_EXCLUDE_IDS.has(key)) return
          const text = el.textContent?.trim() ?? ''
          if (text.length >= 2 && text.length <= 400 && el.children.length <= 2) texts[key] = text
        }
        doc.querySelectorAll('[id]').forEach(el => addEl(el, (el as HTMLElement).id))
        doc.querySelectorAll('[data-t]').forEach(el => addEl(el, (el as HTMLElement).dataset.t ?? ''))
        doc.querySelectorAll('[data-bind]').forEach(el => addEl(el, (el as HTMLElement).dataset.bind ?? ''))
        // Special: marquee — read unique words from first .mq-inner
        const mqInner = doc.querySelector('.mq-inner')
        if (mqInner) {
          const allWords = Array.from(mqInner.querySelectorAll('.mq-w')).map(w => (w as HTMLElement).textContent?.trim()).filter(Boolean) as string[]
          const uniqueCount = Math.ceil(allWords.length / 4) || allWords.length
          const unique = allWords.slice(0, uniqueCount)
          if (unique.length > 0) texts['marq'] = unique.join(', ')
        }
        window.dispatchEvent(new CustomEvent('bst:full-page-loaded', {
          detail: { blockId: block.id, texts },
        }))
        applyPageTextOverrides(doc, textOverridesRef.current)
      } catch {}
    }, 1200)
  }, [block.id])

  const overridesKey = JSON.stringify(block.data.textOverrides)
  useEffect(() => {
    const doc = iframeRef.current?.contentDocument
    if (doc?.body) applyPageTextOverrides(doc, textOverridesRef.current)
  }, [overridesKey])

  useEffect(() => {
    if (isPreview || !containerRef.current) return
    const w0 = containerRef.current.getBoundingClientRect().width
    if (w0 > 100) setContainerWidth(w0)
    const ro = new ResizeObserver(es => { const w = es[0]?.contentRect.width; if (w && w > 100) setContainerWidth(w) })
    ro.observe(containerRef.current)
    return () => ro.disconnect()
  }, [isPreview])

  // Focus textarea when popover opens
  useEffect(() => {
    if (inlineEdit) {
      const t = setTimeout(() => { editInputRef.current?.focus(); editInputRef.current?.select() }, 40)
      return () => clearTimeout(t)
    }
  }, [inlineEdit?.key])

  // Clean up when block is deselected
  useEffect(() => {
    if (!selected) { clearHl(); setInlineEdit(null) }
  }, [selected])

  function clearHl() {
    try { iframeRef.current?.contentDocument?.getElementById('__bst_hl')?.remove() } catch {}
  }

  function applyLiveFontPreview(key: string, ff: string, fs: string) {
    const doc = iframeRef.current?.contentDocument
    if (!doc || !key) return
    const target = doc.getElementById(key) ?? doc.querySelector<HTMLElement>(`[data-t="${key}"]`) ?? doc.querySelector<HTMLElement>(`[data-bind="${key}"]`)
    if (!target) return
    if (ff) { target.style.fontFamily = `'${ff}', system-ui, sans-serif`; injectIframeFont(doc, ff) }
    else target.style.removeProperty('font-family')
    if (fs) target.style.fontSize = fs
    else target.style.removeProperty('font-size')
  }

  function applyHl(key: string) {
    try {
      const doc = iframeRef.current?.contentDocument; if (!doc) return
      doc.getElementById('__bst_hl')?.remove()
      const s = doc.createElement('style'); s.id = '__bst_hl'
      s.textContent = `#${CSS.escape(key)},[data-t="${key}"],[data-bind="${key}"],[data-img="${key}"]{outline:2px solid #4ade80!important;outline-offset:3px;border-radius:3px}`
      doc.head.appendChild(s)
    } catch {}
  }

  function findImgKey(el: Element): { key: string; imgEl: HTMLImageElement } | null {
    let cur: Element | null = el
    while (cur && cur.tagName !== 'HTML') {
      if (cur.tagName === 'IMG') {
        const key = (cur as HTMLElement).getAttribute('data-img')
          || (cur as HTMLElement).getAttribute('data-t')
          || (cur as HTMLElement).id
        if (key) return { key, imgEl: cur as HTMLImageElement }
      }
      // Only check direct children for img to avoid matching unrelated images deep in the tree
      const imgChild = Array.from(cur.children).find(c => c.tagName === 'IMG') as HTMLImageElement | undefined
      if (imgChild) {
        const key = (cur as HTMLElement).getAttribute('data-img')
          || (cur as HTMLElement).getAttribute('data-t')
          || imgChild.getAttribute('data-img')
          || imgChild.getAttribute('data-t')
          || imgChild.id
        if (key) return { key, imgEl: imgChild }
      }
      cur = cur.parentElement
    }
    return null
  }

  function findKey(el: Element): string | null {
    let cur: Element | null = el
    while (cur && cur.tagName !== 'HTML') {
      const key = (cur as HTMLElement).id || (cur as HTMLElement).dataset?.t || (cur as HTMLElement).dataset?.bind
      if (key && !SCAN_EXCLUDE_IDS.has(key) && !cur.querySelector('img,video,canvas') && (cur.textContent?.trim().length ?? 0) > 0)
        return key
      cur = cur.parentElement
    }
    return null
  }

  function doCommit() {
    if (inlineEdit && onUpdate) {
      const current = (block.data.textOverrides as Record<string, string> ?? {})
      const updates: Record<string, string> = { ...current }
      let changed = false
      if (inlineEdit.isImage) {
        if (editHref.trim() && editHref !== (inlineEdit.origSrc ?? '')) {
          updates[`${inlineEdit.key}__src`] = editHref.trim(); changed = true
        }
        const posStr = `${editPos.x}% ${editPos.y}%`
        const origPosStr = `${inlineEdit.origPos?.x ?? 50}% ${inlineEdit.origPos?.y ?? 50}%`
        if (posStr !== origPosStr) { updates[`${inlineEdit.key}__pos`] = posStr; changed = true }
      } else {
        if (editText !== inlineEdit.origText) { updates[inlineEdit.key] = editText; changed = true }
        if (inlineEdit.isLinkable && editHref !== (inlineEdit.origHref ?? '')) {
          if (editHref.trim()) { updates[`${inlineEdit.key}__href`] = editHref.trim(); changed = true }
        }
        // Font family
        const ffKey = `${inlineEdit.key}__ff`
        if (editFontFamily !== (inlineEdit.origFontFamily ?? '')) {
          if (editFontFamily) { updates[ffKey] = editFontFamily; changed = true }
          else { delete updates[ffKey]; changed = true }
        }
        // Font size
        const fsKey = `${inlineEdit.key}__fs`
        if (editFontSize !== (inlineEdit.origFontSize ?? '')) {
          if (editFontSize) { updates[fsKey] = editFontSize; changed = true }
          else { delete updates[fsKey]; changed = true }
        }
      }
      if (changed) onUpdate({ textOverrides: updates })
    }
    clearHl()
    setInlineEdit(null)
  }

  async function handleImgFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setImgUploading(true)
    try {
      const url = await mediaApi.upload(file)
      setEditHref(url)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Ошибка загрузки')
    } finally {
      setImgUploading(false)
      e.target.value = ''
    }
  }

  function updateCropPosition(e: React.MouseEvent<HTMLDivElement>) {
    const rect = e.currentTarget.getBoundingClientRect()
    const x = Math.round(Math.max(0, Math.min(100, (e.clientX - rect.left) / rect.width * 100)))
    const y = Math.round(Math.max(0, Math.min(100, (e.clientY - rect.top) / rect.height * 100)))
    setEditPos({ x, y })
    // Live preview in iframe
    const doc = iframeRef.current?.contentDocument
    if (doc && inlineEdit?.key) {
      const img = doc.querySelector<HTMLImageElement>(`img[data-img="${inlineEdit.key}"]`)
        ?? doc.getElementById(inlineEdit.key) as HTMLImageElement | null
      if (img) { img.style.objectFit = 'cover'; img.style.objectPosition = `${x}% ${y}%` }
    }
  }

  function handleCropMouseDown(e: React.MouseEvent<HTMLDivElement>) {
    e.preventDefault()
    isDraggingCropRef.current = true
    updateCropPosition(e)
  }

  function handleCropMouseMove(e: React.MouseEvent<HTMLDivElement>) {
    if (!isDraggingCropRef.current) return
    updateCropPosition(e)
  }

  function handleCropMouseUp() { isDraggingCropRef.current = false }

  function handleOverlayClick(e: React.MouseEvent) {
    e.stopPropagation()
    if (isPreview || !containerRef.current) return
    const cRect = containerRef.current.getBoundingClientRect()
    const visualScale = cRect.width / previewWidth
    const ix = (e.clientX - cRect.left) / visualScale
    const iy = (e.clientY - cRect.top) / visualScale
    const doc = iframeRef.current?.contentDocument; if (!doc) return
    const el = doc.elementFromPoint(ix, iy); if (!el) { doCommit(); return }
    const key = findKey(el)
    if (!key) {
      // Special: image click
      const imgResult = findImgKey(el)
      if (imgResult) {
        const { key: imgKey, imgEl } = imgResult
        if (inlineEdit?.key === imgKey) { doCommit(); return }
        if (inlineEdit && inlineEdit.isImage && editHref !== (inlineEdit.origSrc ?? '') && onUpdate) doCommit()
        const textOverrides = (block.data.textOverrides as Record<string, string> ?? {})
        const origSrc = textOverrides[`${imgKey}__src`] ?? imgEl.getAttribute('src') ?? imgEl.src
        // Read existing focal point
        const existingPos = textOverrides[`${imgKey}__pos`]
        let origPos = { x: 50, y: 50 }
        if (existingPos) {
          const parts = existingPos.split(' ')
          origPos = { x: parseInt(parts[0]) || 50, y: parseInt(parts[1]) || 50 }
        } else {
          const op = imgEl.style.objectPosition
          if (op) {
            const parts = op.split(' ')
            origPos = { x: parseInt(parts[0]) || 50, y: parseInt(parts[1]) || 50 }
          }
        }
        const sx = Math.max(8, Math.min(e.clientX - 200, window.innerWidth - 400))
        const sy = Math.min(e.clientY + 10, window.innerHeight - 340)
        applyHl(imgKey)
        setEditPos(origPos)
        setInlineEdit({ key: imgKey, origText: '', origSrc, origPos, x: sx, y: sy, w: 380, isImage: true })
        setEditText('')
        setEditHref(origSrc)
        return
      }
      // Special: marquee strip click
      const isTick = !!(el.closest?.('.a-tick') ?? el.closest?.('.mq-track') ?? el.closest?.('.mq-inner') ?? el.classList?.contains?.('mq-w') ?? el.classList?.contains?.('mq-d'))
      if (isTick) {
        const existingMarq = (block.data.textOverrides as Record<string, string> ?? {})['marq']
        let marqText = existingMarq ?? ''
        // Normalize legacy format: old saves used ✦ separators instead of commas
        if (marqText.includes('✦')) {
          const rawWords = marqText.split('✦').map(w => w.trim()).filter(Boolean)
          const seen = new Set<string>()
          const deduped: string[] = []
          for (const w of rawWords) { if (!seen.has(w)) { seen.add(w); deduped.push(w) } }
          marqText = deduped.join(', ')
        }
        if (!marqText) {
          const inner = doc.querySelector('.mq-inner')
          if (inner) {
            const allWords = Array.from(inner.querySelectorAll('.mq-w')).map(w => (w as HTMLElement).textContent?.trim()).filter(Boolean) as string[]
            marqText = allWords.slice(0, Math.ceil(allWords.length / 4) || allWords.length).join(', ')
          }
        }
        const sx = Math.max(8, Math.min(e.clientX - 220, window.innerWidth - 468))
        const sy = e.clientY + 10
        applyHl('marq')
        setInlineEdit({ key: 'marq', origText: marqText, x: sx, y: sy, w: 440 })
        setEditText(marqText)
        setEditHref('')
        return
      }
      doCommit(); return
    }

    // Same element → close/save
    if (inlineEdit?.key === key) { doCommit(); return }

    // Save previous edit if dirty before switching
    if (inlineEdit && (editText !== inlineEdit.origText || (inlineEdit.isLinkable && editHref !== (inlineEdit.origHref ?? ''))) && onUpdate) {
      doCommit()
    }

    const target = doc.getElementById(key) ?? doc.querySelector(`[data-t="${key}"]`) ?? doc.querySelector(`[data-bind="${key}"]`)
    const elRect = target?.getBoundingClientRect() ?? { left: ix, top: iy, bottom: iy + 24, width: 260 } as DOMRect

    // Convert iframe rect → screen coords, clamp to viewport
    const rawX = elRect.left * visualScale + cRect.left
    const rawY = elRect.bottom * visualScale + cRect.top + 6
    const screenX = Math.max(8, Math.min(rawX, window.innerWidth - 468))
    const screenY = rawY + 220 > window.innerHeight ? elRect.top * visualScale + cRect.top - 220 : rawY
    const screenW = Math.max(320, Math.min(elRect.width * visualScale, 440))

    const textOverrides = (block.data.textOverrides as Record<string, string> ?? {})
    const existingOverride = textOverrides[key]
    const rawContent = target ? ((target as HTMLElement).innerText?.trim() ?? target.textContent?.trim() ?? '') : ''
    const text = existingOverride ?? rawContent

    // Detect if clickable element is a link/button (for href editing)
    const linkEl = target ? (target.closest('a') ?? target.closest('button')) : null
    const isLinkable = !!linkEl
    let origHref = ''
    if (linkEl) {
      origHref = textOverrides[`${key}__href`] ?? (linkEl.tagName === 'A' ? (linkEl as HTMLAnchorElement).getAttribute('href') ?? '' : '')
    }

    const origFontFamily = textOverrides[`${key}__ff`] ?? ''
    const origFontSize = textOverrides[`${key}__fs`] ?? ''

    applyHl(key)
    setInlineEdit({ key, origText: text, x: screenX, y: screenY, w: screenW, isLinkable, origHref, origFontFamily, origFontSize })
    setEditText(text)
    setEditHref(origHref)
    setEditFontFamily(origFontFamily)
    setEditFontSize(origFontSize)
  }

  if (isPreview) return <iframe ref={iframeRef} srcDoc={srcDoc} style={{ width: '100%', height: '100vh', border: 'none', display: 'block' }} title={title} onLoad={handleLoad} />
  return (
    <div ref={containerRef} style={{ width: '100%', position: 'relative', overflow: 'hidden' }}>
      <div style={{ width: `${containerWidth}px`, height: `${Math.round(height * scale)}px`, overflow: 'hidden' }}>
        <iframe
          ref={iframeRef}
          key={brand || '_'}
          srcDoc={srcDoc}
          style={{ width: `${previewWidth}px`, height: `${height}px`, border: 'none', display: 'block', transform: `scale(${scale})`, transformOrigin: 'top left' }}
          onLoad={handleLoad}
          title={title}
        />
      </div>

      {/* Click-to-edit overlay — pointer-events:auto overrides parent's none */}
      {selected && !isPreview && (
        <div
          style={{ position: 'absolute', inset: 0, zIndex: 5, cursor: 'text', pointerEvents: 'auto' }}
          onClick={handleOverlayClick}
        />
      )}

      {/* Inline text edit popover — pointer-events:auto so parent none doesn't block it */}
      {inlineEdit && (
        <div
          ref={popoverRef}
          style={{ position: 'fixed', left: inlineEdit.x, top: inlineEdit.y, width: inlineEdit.w, zIndex: 9999, background: '#16142a', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, padding: 10, boxShadow: '0 12px 40px rgba(0,0,0,0.6)', pointerEvents: 'auto' }}
          onClick={e => e.stopPropagation()}
        >
          {inlineEdit.isImage ? (
            <>
              <div style={{ fontSize: 11, color: '#9b95c4', marginBottom: 4, fontFamily: 'system-ui' }}>Кадрирование — перетащите фокус</div>
              {/* Focal point picker */}
              <div
                ref={cropPickerRef}
                style={{ position: 'relative', width: '100%', height: 160, overflow: 'hidden', borderRadius: 7, marginBottom: 8, cursor: 'crosshair', userSelect: 'none', border: '1px solid rgba(255,255,255,0.1)', background: '#0a0a14' }}
                onMouseDown={handleCropMouseDown}
                onMouseMove={handleCropMouseMove}
                onMouseUp={handleCropMouseUp}
                onMouseLeave={handleCropMouseUp}
              >
                {editHref && (
                  <img
                    src={editHref}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: `${editPos.x}% ${editPos.y}%`, display: 'block', pointerEvents: 'none', userSelect: 'none' }}
                    alt=""
                    draggable={false}
                  />
                )}
                {/* Grid overlay */}
                <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(255,255,255,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.07) 1px, transparent 1px)', backgroundSize: '33.33% 33.33%', pointerEvents: 'none' }} />
                {/* Focal point dot */}
                <div style={{ position: 'absolute', left: `${editPos.x}%`, top: `${editPos.y}%`, transform: 'translate(-50%, -50%)', width: 22, height: 22, borderRadius: '50%', border: '2.5px solid #fff', background: 'rgba(255,255,255,0.18)', boxShadow: '0 0 0 1px rgba(0,0,0,0.7), 0 2px 10px rgba(0,0,0,0.5)', pointerEvents: 'none', transition: isDraggingCropRef.current ? 'none' : 'left 0.1s, top 0.1s' }} />
                {/* Position label */}
                <div style={{ position: 'absolute', bottom: 5, right: 7, fontSize: 9, color: 'rgba(255,255,255,0.45)', fontFamily: 'monospace', pointerEvents: 'none' }}>{editPos.x}% {editPos.y}%</div>
              </div>
              {/* URL input */}
              <input
                ref={editInputRef as unknown as React.RefObject<HTMLInputElement>}
                type="url"
                value={editHref}
                onChange={e => setEditHref(e.target.value)}
                onKeyDown={e => { e.stopPropagation(); if (e.key === 'Enter') doCommit(); if (e.key === 'Escape') { clearHl(); setInlineEdit(null) } }}
                placeholder="https://..."
                style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 6, color: '#f0eeff', fontSize: 13, padding: '6px 9px', fontFamily: 'system-ui', outline: 'none', marginBottom: 6 }}
              />
              <label style={{ display: 'block', cursor: imgUploading ? 'wait' : 'pointer', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, padding: '7px 12px', textAlign: 'center', fontSize: 12, color: '#c4bfef', fontFamily: 'system-ui' }}>
                {imgUploading ? 'Загрузка...' : '📁 Загрузить файл'}
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImgFileUpload} disabled={imgUploading} />
              </label>
            </>
          ) : (
            <>
              <div style={{ fontSize: 11, color: '#9b95c4', marginBottom: 6, fontFamily: 'system-ui' }}>
                {inlineEdit.key === 'marq' ? 'Бегущая строка · слова через запятую' : 'Редактировать текст'}
              </div>
              <textarea
                ref={editInputRef}
                value={editText}
                onChange={e => setEditText(e.target.value)}
                onKeyDown={e => {
                  e.stopPropagation()
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); doCommit() }
                  if (e.key === 'Escape') { clearHl(); setInlineEdit(null) }
                }}
                style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 6, color: '#f0eeff', fontSize: 13, lineHeight: 1.5, padding: '7px 9px', resize: 'vertical', minHeight: 52, maxHeight: 160, fontFamily: 'system-ui', outline: 'none' }}
              />
              {inlineEdit.isLinkable && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 11, color: '#9b95c4', marginBottom: 4, fontFamily: 'system-ui' }}>Ссылка (URL)</div>
                  <input
                    type="url"
                    value={editHref}
                    onChange={e => setEditHref(e.target.value)}
                    placeholder="https://..."
                    onKeyDown={e => { e.stopPropagation(); if (e.key === 'Enter') doCommit(); if (e.key === 'Escape') { clearHl(); setInlineEdit(null) } }}
                    style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 6, color: '#f0eeff', fontSize: 13, padding: '6px 9px', fontFamily: 'system-ui', outline: 'none' }}
                  />
                </div>
              )}
              {inlineEdit.key !== 'marq' && (
                <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, color: '#9b95c4', marginBottom: 4, fontFamily: 'system-ui' }}>Шрифт</div>
                    <select
                      value={editFontFamily}
                      onChange={e => {
                        const ff = e.target.value
                        setEditFontFamily(ff)
                        applyLiveFontPreview(inlineEdit.key, ff, editFontSize)
                      }}
                      style={{ width: '100%', boxSizing: 'border-box', background: '#1e1b33', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 6, color: '#f0eeff', fontSize: 12, padding: '5px 7px', fontFamily: 'system-ui', outline: 'none', cursor: 'pointer' }}
                    >
                      {FONT_CHOICES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                    </select>
                  </div>
                  <div style={{ width: 76 }}>
                    <div style={{ fontSize: 11, color: '#9b95c4', marginBottom: 4, fontFamily: 'system-ui' }}>Размер</div>
                    <input
                      type="number"
                      min={8}
                      max={200}
                      value={editFontSize ? parseInt(editFontSize) : ''}
                      onChange={e => {
                        const fs = e.target.value ? `${e.target.value}px` : ''
                        setEditFontSize(fs)
                        applyLiveFontPreview(inlineEdit.key, editFontFamily, fs)
                      }}
                      placeholder="px"
                      onKeyDown={e => { e.stopPropagation(); if (e.key === 'Enter') doCommit(); if (e.key === 'Escape') { clearHl(); setInlineEdit(null) } }}
                      style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 6, color: '#f0eeff', fontSize: 13, padding: '5px 7px', fontFamily: 'system-ui', outline: 'none' }}
                    />
                  </div>
                </div>
              )}
            </>
          )}
          <div style={{ display: 'flex', gap: 6, marginTop: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => { clearHl(); setInlineEdit(null) }} style={{ fontSize: 12, padding: '5px 12px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.15)', background: 'transparent', color: '#9b95c4', cursor: 'pointer', fontFamily: 'system-ui' }}>
              Отмена
            </button>
            <button onClick={doCommit} style={{ fontSize: 12, padding: '5px 12px', borderRadius: 6, border: 'none', background: '#4ade80', color: '#0a0e1a', fontWeight: 600, cursor: 'pointer', fontFamily: 'system-ui' }}>
              Сохранить ↵
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Google Fonts loader ───────────────────────────────────────────────────────
const loadedFonts = new Set<string>()
function loadGoogleFont(family: string) {
  if (!family || loadedFonts.has(family)) return
  const name = family.split(',')[0].trim().replace(/['"]/g, '')
  const systemFonts = ['Arial', 'Georgia', 'Verdana', 'system-ui', 'monospace', '-apple-system']
  if (systemFonts.some(f => name.includes(f))) return
  loadedFonts.add(family)
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = `https://fonts.googleapis.com/css2?family=${name.replace(/ /g, '+')}:wght@400;500;600;700;800&display=swap`
  document.head.appendChild(link)
}

// ── Color utilities ───────────────────────────────────────────────────────────
function isColorDark(hex: string): boolean {
  try {
    const h = hex.replace('#', '')
    const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h
    const r = parseInt(full.slice(0, 2), 16)
    const g = parseInt(full.slice(2, 4), 16)
    const b = parseInt(full.slice(4, 6), 16)
    return (r * 299 + g * 587 + b * 114) / 1000 < 128
  } catch { return false }
}

// ── Heading ───────────────────────────────────────────────────────────────────
export function HeadingRenderer({ block, editing, onUpdate }: RendererProps) {
  const { text = 'Заголовок', level = 'h1', align = 'center', fontFamily = '', color = '' } = block.data as Record<string, string>
  const Tag = level as React.ElementType
  const sizes: Record<string, string> = { h1: '48px', h2: '36px', h3: '28px', h4: '22px' }
  if (fontFamily) loadGoogleFont(fontFamily)
  const { ref, onBlur, onKeyDown } = useInlineEdit(text, 'text', editing, onUpdate)
  const style: React.CSSProperties = {
    fontSize: sizes[level] ?? '48px', fontWeight: 700, lineHeight: 1.2, margin: 0,
    fontFamily: fontFamily || undefined, color: color || undefined, outline: 'none',
    cursor: editing ? 'text' : undefined,
  }
  if (editing) {
    return (
      <div style={{ textAlign: align as 'left' | 'center' | 'right', padding: '16px 24px' }}>
        <div ref={ref as React.Ref<HTMLDivElement>} contentEditable suppressContentEditableWarning onBlur={onBlur} onKeyDown={onKeyDown} style={style} />
      </div>
    )
  }
  return (
    <div style={{ textAlign: align as 'left' | 'center' | 'right', padding: '16px 24px' }}>
      <Tag style={style}>{text}</Tag>
    </div>
  )
}

// ── Paragraph ─────────────────────────────────────────────────────────────────
export function ParagraphRenderer({ block, editing, onUpdate }: RendererProps) {
  const { text = 'Введите текст...', align = 'left', fontFamily = '', fontSize = 16, lineHeight = 1.7, color = '#555' } = block.data as Record<string, unknown>
  if (fontFamily) loadGoogleFont(String(fontFamily))
  const { ref, onBlur, onKeyDown } = useInlineEdit(String(text), 'text', editing, onUpdate)
  const pStyle: React.CSSProperties = {
    fontSize: `${Number(fontSize) || 16}px`, lineHeight: Number(lineHeight) || 1.7,
    color: String(color) || '#555', margin: 0, fontFamily: fontFamily ? String(fontFamily) : undefined,
    outline: 'none', cursor: editing ? 'text' : undefined,
  }
  return (
    <div style={{ padding: '12px 24px', textAlign: align as 'left' | 'center' | 'right' }}>
      <p
        ref={ref as React.Ref<HTMLParagraphElement>}
        contentEditable={editing ? true : undefined}
        suppressContentEditableWarning
        onBlur={editing ? onBlur : undefined}
        onKeyDown={editing ? onKeyDown : undefined}
        style={pStyle}
      >
        {!editing && String(text)}
      </p>
    </div>
  )
}

// ── Button ────────────────────────────────────────────────────────────────────
export function ButtonRenderer({ block, editing, onUpdate }: RendererProps) {
  const { text = 'Нажмите', variant = 'primary', align = 'center', fontFamily = '', borderRadius = 8, paddingX = 28, paddingY = 12 } = block.data as Record<string, unknown>
  if (fontFamily) loadGoogleFont(String(fontFamily))
  const { ref, onBlur, onKeyDown } = useInlineEdit(String(text), 'text', editing, onUpdate)
  const variantStyles: Record<string, React.CSSProperties> = {
    primary: { background: '#5b6af0', color: '#fff', border: 'none' },
    secondary: { background: '#1f1f1f', color: '#fff', border: 'none' },
    outline: { background: 'transparent', color: '#5b6af0', border: '2px solid #5b6af0' },
    ghost: { background: 'transparent', color: '#333', border: 'none' },
  }
  const btnStyle: React.CSSProperties = {
    padding: `${Number(paddingY) || 12}px ${Number(paddingX) || 28}px`,
    fontSize: '15px', fontWeight: 600,
    borderRadius: `${Number(borderRadius) || 8}px`,
    cursor: editing ? 'text' : 'default',
    fontFamily: fontFamily ? String(fontFamily) : undefined,
    outline: 'none',
    ...(variantStyles[String(variant)] ?? variantStyles.primary),
  }
  return (
    <div style={{ padding: '16px 24px', textAlign: align as 'left' | 'center' | 'right' }}>
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        contentEditable={editing ? true : undefined}
        suppressContentEditableWarning
        onBlur={editing ? onBlur : undefined}
        onKeyDown={editing ? onKeyDown : undefined}
        style={btnStyle}
      >
        {!editing && String(text)}
      </button>
    </div>
  )
}

// ── Hero Centered ─────────────────────────────────────────────────────────────
export function HeroCenteredRenderer({ block, editing, onUpdate }: RendererProps) {
  const {
    title = 'Создайте сайт\nнового поколения',
    subtitle = 'Быстро, красиво, без кода.',
    primaryButton = { text: 'Начать бесплатно' },
    secondaryButton = { text: 'Смотреть демо' },
  } = block.data as Record<string, unknown>
  const { ref, onBlur, onKeyDown } = useInlineEdit(String(title), 'title', editing, onUpdate)
  return (
    <div
      style={{
        padding: '80px 40px', textAlign: 'center',
        background: 'linear-gradient(135deg, #0d0d0d 0%, #1a1a2e 100%)',
        color: '#fff', minHeight: '400px',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '24px',
      }}
    >
      <h1
        ref={ref as React.Ref<HTMLHeadingElement>}
        contentEditable={editing ? true : undefined}
        suppressContentEditableWarning
        onBlur={editing ? onBlur : undefined}
        onKeyDown={editing ? onKeyDown : undefined}
        style={{ fontSize: '52px', fontWeight: 800, lineHeight: 1.15, letterSpacing: '-1px', whiteSpace: 'pre-line', margin: 0, outline: 'none', cursor: editing ? 'text' : undefined }}
      >
        {!editing && String(title)}
      </h1>
      {!editing && <p style={{ fontSize: '18px', color: '#aaa', maxWidth: '560px', lineHeight: 1.6, margin: 0 }}>{String(subtitle)}</p>}
      {!editing && (
        <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
          <button style={{ padding: '13px 28px', fontSize: '15px', fontWeight: 600, background: '#5b6af0', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'default' }}>
            {(primaryButton as { text: string }).text}
          </button>
          <button style={{ padding: '13px 28px', fontSize: '15px', fontWeight: 600, background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', cursor: 'default' }}>
            {(secondaryButton as { text: string }).text}
          </button>
        </div>
      )}
    </div>
  )
}

// ── Slider ────────────────────────────────────────────────────────────────────
export function SliderRenderer({ block }: RendererProps) {
  const { slides = [], effect = 'slide', showArrows = true } =
    block.data as { slides: Array<{ title: string }>; effect: string; showDots: boolean; showArrows: boolean }
  const slideArr = Array.isArray(slides) && slides.length > 0
    ? slides
    : [{ title: 'Слайд 1' }, { title: 'Слайд 2' }, { title: 'Слайд 3' }]

  return (
    <div style={{ background: 'var(--bg-2)', padding: '40px 24px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ textAlign: 'center', padding: '40px 20px', minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '12px' }}>
        <div style={{ fontSize: '12px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '2px' }}>Слайдер · Режим: {effect}</div>
        <div style={{ fontSize: '28px', fontWeight: 700, color: 'var(--text-primary)' }}>{slideArr[0]?.title ?? 'Слайд 1'}</div>
        <div style={{ display: 'flex', gap: '6px', fontSize: '12px', color: 'var(--text-secondary)' }}>
          {slideArr.map((_, i) => <span key={i}>{i === 0 ? '●' : '○'}</span>)}
        </div>
      </div>
      {showArrows && (
        <>
          <div style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', width: '36px', height: '36px', borderRadius: '50%', background: 'var(--bg-3)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-primary)', cursor: 'default' }}>‹</div>
          <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', width: '36px', height: '36px', borderRadius: '50%', background: 'var(--bg-3)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-primary)', cursor: 'default' }}>›</div>
        </>
      )}
    </div>
  )
}

// ── Countdown ─────────────────────────────────────────────────────────────────
export function CountdownRenderer({ block }: RendererProps) {
  const { targetDate, showDays = true, showHours = true, showMinutes = true, showSeconds = true } =
    block.data as { targetDate?: string; showDays?: boolean; showHours?: boolean; showMinutes?: boolean; showSeconds?: boolean }
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])
  const target = targetDate ? new Date(targetDate).getTime() : Date.now() + 7 * 24 * 60 * 60 * 1000
  const diff = Math.max(0, target - now)
  const d = Math.floor(diff / 86400000)
  const h = Math.floor((diff % 86400000) / 3600000)
  const m = Math.floor((diff % 3600000) / 60000)
  const s = Math.floor((diff % 60000) / 1000)
  const parts = [
    showDays !== false && { val: String(d).padStart(2, '0'), label: 'дней' },
    showHours !== false && { val: String(h).padStart(2, '0'), label: 'часов' },
    showMinutes !== false && { val: String(m).padStart(2, '0'), label: 'минут' },
    showSeconds !== false && { val: String(s).padStart(2, '0'), label: 'секунд' },
  ].filter(Boolean) as { val: string; label: string }[]
  return (
    <div style={{ padding: '40px 24px', textAlign: 'center', background: 'var(--bg-2)' }}>
      <div style={{ display: 'inline-flex', gap: '16px' }}>
        {parts.map(({ val, label }, i) => (
          <div key={i} style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1 }}>{val}</div>
            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '1px', marginTop: '4px' }}>{label}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Form ──────────────────────────────────────────────────────────────────────
export function FormRenderer({ block }: RendererProps) {
  const { submitText = 'Отправить' } = block.data as { submitText?: string }
  return (
    <div style={{ padding: '40px 24px', maxWidth: '480px', margin: '0 auto' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {['Имя', 'Email', 'Сообщение'].map((label) => (
          <div key={label}>
            <div style={{ fontSize: '12px', fontWeight: 500, color: '#555', marginBottom: '4px' }}>{label}</div>
            <div
              style={{
                padding: '9px 12px',
                border: '1px solid #e0e0e0',
                borderRadius: '6px',
                fontSize: '14px',
                color: '#aaa',
                background: '#fafafa',
              }}
            >
              {label === 'Сообщение' ? '   ' : ''}
            </div>
          </div>
        ))}
        <button
          style={{
            marginTop: '8px',
            padding: '11px', fontSize: '14px', fontWeight: 600,
            background: '#5b6af0', color: '#fff', border: 'none',
            borderRadius: '8px', cursor: 'default',
          }}
        >
          {submitText}
        </button>
      </div>
    </div>
  )
}

// ── Image ─────────────────────────────────────────────────────────────────────
export function ImageRenderer({ block }: RendererProps) {
  const { src = '', alt = '', caption = '', objectFit = 'cover', aspectRatio = '16/9' } =
    block.data as Record<string, string>
  return (
    <div style={{ padding: '8px 24px' }}>
      <div
        style={{
          aspectRatio,
          background: src ? undefined : 'var(--bg-3)',
          borderRadius: '8px',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid var(--border)',
        }}
      >
        {src ? (
          <img src={src} alt={alt} style={{ width: '100%', height: '100%', objectFit: objectFit as 'cover' | 'contain' }} />
        ) : (
          <div style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: '32px', marginBottom: '8px' }}>🖼</div>
            <div style={{ fontSize: '12px' }}>Изображение</div>
          </div>
        )}
      </div>
      {caption && <p style={{ fontSize: '12px', color: '#888', textAlign: 'center', marginTop: '8px' }}>{caption}</p>}
    </div>
  )
}

// ── Video ─────────────────────────────────────────────────────────────────────
export function VideoRenderer({ block }: RendererProps) {
  const { url = '', title = 'Видео', aspectRatio = '16/9' } = block.data as Record<string, string>
  const isYoutube = url.includes('youtube') || url.includes('youtu.be')
  const isVimeo = url.includes('vimeo')
  return (
    <div style={{ padding: '8px 24px' }}>
      <div
        style={{
          aspectRatio,
          background: 'var(--bg-3)',
          borderRadius: '8px',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid var(--border)',
          position: 'relative',
        }}
      >
        <div style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
          <div style={{ fontSize: '40px', marginBottom: '8px' }}>▶</div>
          <div style={{ fontSize: '12px' }}>{isYoutube ? 'YouTube' : isVimeo ? 'Vimeo' : title}</div>
        </div>
      </div>
    </div>
  )
}

// ── Features ──────────────────────────────────────────────────────────────────
export function FeaturesRenderer({ block }: RendererProps) {
  const {
    title = 'Почему выбирают нас',
    subtitle = 'Всё что нужно для успешного сайта',
    columns = 3,
    items = [],
    background = '#fff',
  } = block.data as { title: string; subtitle: string; columns: number; items: Array<{ icon: string; title: string; description: string }>; background: string }

  const dark = isColorDark(background)
  const headingColor = dark ? '#ffffff' : '#1a1a1a'
  const bodyColor = dark ? 'rgba(255,255,255,0.65)' : '#666'

  const defaultItems = [
    { icon: '⚡', title: 'Быстро', description: 'Сайт загружается за менее чем 1 секунду' },
    { icon: '🎨', title: 'Красиво', description: 'Профессиональный дизайн без дизайнера' },
    { icon: '📱', title: 'Адаптивно', description: 'Идеально на любом устройстве' },
  ]

  const feats = Array.isArray(items) && items.length > 0 ? items : defaultItems
  const desktopCols = Number(columns) || 3
  const { cols } = useDevice()

  return (
    <div style={{ padding: '60px 40px', background }}>
      <div style={{ textAlign: 'center', marginBottom: '48px' }}>
        <h2 style={{ fontSize: '36px', fontWeight: 700, color: headingColor, marginBottom: '12px' }}>{title}</h2>
        <p style={{ fontSize: '16px', color: bodyColor, maxWidth: '560px', margin: '0 auto' }}>{subtitle}</p>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(cols(desktopCols), feats.length)}, 1fr)`, gap: '32px', maxWidth: '960px', margin: '0 auto' }}>
        {feats.map((item, i) => (
          <div key={i} style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '36px', marginBottom: '16px' }}>{item.icon}</div>
            <h3 style={{ fontSize: '18px', fontWeight: 600, color: headingColor, marginBottom: '8px' }}>{item.title}</h3>
            <p style={{ fontSize: '14px', color: bodyColor, lineHeight: 1.6 }}>{item.description}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Testimonials ──────────────────────────────────────────────────────────────
export function TestimonialsRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const title = String(d.title ?? 'Отзывы клиентов')
  const items = (Array.isArray(d.items) ? d.items : []) as Array<{ name: string; role: string; company?: string; text: string; avatar?: string }>
  const background = String(d.background ?? '#f9f9f9')
  const dark = isColorDark(background)
  const headingColor = dark ? '#ffffff' : '#1a1a1a'
  const cardBg = dark ? 'rgba(255,255,255,0.07)' : '#ffffff'
  const cardBorder = dark ? 'rgba(255,255,255,0.12)' : 'transparent'
  const textColor = dark ? 'rgba(255,255,255,0.8)' : '#444'
  const nameColor = dark ? '#ffffff' : '#1a1a1a'
  const roleColor = dark ? 'rgba(255,255,255,0.5)' : '#888'
  const avatarBg = dark ? 'rgba(255,255,255,0.15)' : '#e8e8e8'
  const avatarColor = dark ? '#fff' : '#666'

  type ReviewItem = { name: string; role: string; company?: string; text: string; avatar?: string }
  const defaultItems: ReviewItem[] = [
    { name: 'Анна К.', role: 'Маркетолог', text: 'BST помог нам запустить сайт за один день. Результат превзошёл ожидания!', avatar: '' },
    { name: 'Дмитрий С.', role: 'Предприниматель', text: 'Наконец-то конструктор, который не ограничивает в дизайне. Рекомендую!', avatar: '' },
    { name: 'Мария В.', role: 'Дизайнер', text: 'Интуитивный интерфейс и красивые блоки — всё что нужно для работы.', avatar: '' },
  ]

  const reviews: ReviewItem[] = items.length > 0 ? items : defaultItems
  const { cols } = useDevice()

  return (
    <div style={{ padding: '60px 40px', background }}>
      <h2 style={{ textAlign: 'center', fontSize: '32px', fontWeight: 700, color: headingColor, marginBottom: '40px' }}>{title}</h2>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols(3)}, 1fr)`, gap: '24px', maxWidth: '960px', margin: '0 auto' }}>
        {reviews.map((r, i) => (
          <div key={i} style={{ background: cardBg, borderRadius: '12px', padding: '24px', boxShadow: dark ? 'none' : '0 2px 12px rgba(0,0,0,0.06)', border: `1px solid ${cardBorder}` }}>
            <p style={{ fontSize: '14px', color: textColor, lineHeight: 1.7, marginBottom: '20px' }}>"{r.text}"</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: avatarBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 600, color: avatarColor, flexShrink: 0 }}>
                {r.name[0]}
              </div>
              <div>
                <div style={{ fontSize: '13px', fontWeight: 600, color: nameColor }}>{r.name}</div>
                <div style={{ fontSize: '11px', color: roleColor }}>{r.role}{r.company ? `, ${r.company}` : ''}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── FAQ ───────────────────────────────────────────────────────────────────────
export function FAQRenderer({ block }: RendererProps) {
  const { title = 'Часто задаваемые вопросы', items = [] } =
    block.data as { title: string; items: Array<{ question: string; answer: string }> }
  const [openIdx, setOpenIdx] = useState<number | null>(0)

  const defaultItems = [
    { question: 'Сколько стоит BST?', answer: 'Есть бесплатный план. Платные тарифы от 790 ₽/мес.' },
    { question: 'Нужны ли навыки программирования?', answer: 'Нет — всё делается визуально, drag-and-drop.' },
    { question: 'Можно ли подключить свой домен?', answer: 'Да, на тарифе Personal и выше.' },
  ]

  const faqs = Array.isArray(items) && items.length > 0 ? items : defaultItems

  return (
    <div style={{ padding: '60px 40px', maxWidth: '720px', margin: '0 auto' }}>
      <h2 style={{ fontSize: '32px', fontWeight: 700, color: '#1a1a1a', marginBottom: '32px', textAlign: 'center' }}>{title}</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {faqs.map((item, i) => (
          <div key={i} style={{ border: '1px solid #e8e8e8', borderRadius: '10px', overflow: 'hidden' }}>
            <div
              onClick={() => setOpenIdx(openIdx === i ? null : i)}
              style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: openIdx === i ? '#fafafa' : '#fff', cursor: 'pointer' }}
            >
              <span style={{ fontSize: '15px', fontWeight: 500, color: '#1a1a1a' }}>{item.question}</span>
              <span style={{ color: '#888', fontSize: '18px', flexShrink: 0, marginLeft: '12px', transform: openIdx === i ? 'rotate(45deg)' : 'none', transition: 'transform 0.2s', display: 'inline-block' }}>+</span>
            </div>
            {openIdx === i && (
              <div style={{ padding: '0 20px 16px', fontSize: '14px', color: '#555', lineHeight: 1.7 }}>{item.answer}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Pricing ───────────────────────────────────────────────────────────────────
export function PricingRenderer({ block }: RendererProps) {
  const { title = 'Тарифные планы', plans = [] } =
    block.data as { title: string; plans: Array<{ name: string; price: string; period: string; features: string[]; highlighted?: boolean; popular?: boolean; buttonText: string }> }

  const defaultPlans = [
    { name: 'Free', price: '0', period: 'мес', features: ['1 сайт', 'Поддомен .mybst.ru', '500 МБ хранилища', 'Базовые блоки'], highlighted: false, buttonText: 'Начать бесплатно' },
    { name: 'Personal', price: '790', period: 'мес', features: ['3 сайта', 'Свой домен', '5 ГБ хранилища', 'Все блоки', 'Формы'], highlighted: true, buttonText: 'Выбрать Personal' },
    { name: 'Business', price: '1 990', period: 'мес', features: ['10 сайтов', 'CMS', 'E-commerce', 'Аналитика', 'Коллаборация'], highlighted: false, buttonText: 'Выбрать Business' },
  ]

  const items = Array.isArray(plans) && plans.length > 0 ? plans : defaultPlans
  const { cols } = useDevice()

  return (
    <div style={{ padding: '60px 40px', background: '#f9f9f9' }}>
      <h2 style={{ textAlign: 'center', fontSize: '36px', fontWeight: 700, color: '#1a1a1a', marginBottom: '48px' }}>{title}</h2>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols(3)}, 1fr)`, gap: '24px', maxWidth: '960px', margin: '0 auto' }}>
        {items.map((plan, i) => {
          const isHighlighted = !!(plan.highlighted ?? plan.popular)
          return (
          <div key={i} style={{ background: '#fff', borderRadius: '16px', padding: '32px 28px', border: isHighlighted ? '2px solid #5b6af0' : '1px solid #e8e8e8', position: 'relative' }}>
            {isHighlighted && (
              <div style={{ position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)', background: '#5b6af0', color: '#fff', fontSize: '11px', fontWeight: 600, padding: '4px 12px', borderRadius: '20px' }}>
                Популярный
              </div>
            )}
            <div style={{ marginBottom: '24px' }}>
              <div style={{ fontSize: '14px', fontWeight: 500, color: '#888', marginBottom: '8px' }}>{plan.name}</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                <span style={{ fontSize: '40px', fontWeight: 800, color: '#1a1a1a' }}>{plan.price} ₽</span>
                <span style={{ fontSize: '13px', color: '#888' }}>/{plan.period}</span>
              </div>
            </div>
            <ul style={{ listStyle: 'none', marginBottom: '28px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {plan.features.map((f, fi) => (
                <li key={fi} style={{ fontSize: '13px', color: '#444', display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span style={{ color: '#5b6af0', fontSize: '14px' }}>✓</span> {f}
                </li>
              ))}
            </ul>
            <button style={{ width: '100%', padding: '11px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, background: isHighlighted ? '#5b6af0' : 'transparent', color: isHighlighted ? '#fff' : '#5b6af0', border: isHighlighted ? 'none' : '1px solid #5b6af0', cursor: 'default' }}>
              {plan.buttonText}
            </button>
          </div>
          )
        })}
      </div>
    </div>
  )
}

// ── CTA ───────────────────────────────────────────────────────────────────────
export function CTARenderer({ block, editing, onUpdate }: RendererProps) {
  const {
    title = 'Готовы начать?',
    subtitle = 'Создайте первый сайт бесплатно за 5 минут',
    primaryText = 'Начать бесплатно',
    secondaryText = 'Смотреть примеры',
    background = '#5b6af0',
  } = block.data as Record<string, string>
  const { ref, onBlur, onKeyDown } = useInlineEdit(title, 'title', editing, onUpdate)
  const isDark = background === '#5b6af0' || background === '#0d0d0d' || background.startsWith('#0') || background.startsWith('#1')

  return (
    <div style={{ padding: '80px 40px', background, textAlign: 'center' }}>
      <h2
        ref={ref as React.Ref<HTMLHeadingElement>}
        contentEditable={editing ? true : undefined}
        suppressContentEditableWarning
        onBlur={editing ? onBlur : undefined}
        onKeyDown={editing ? onKeyDown : undefined}
        style={{ fontSize: '40px', fontWeight: 800, color: isDark ? '#fff' : '#1a1a1a', marginBottom: '16px', lineHeight: 1.2, outline: 'none' }}
      >
        {!editing && title}
      </h2>
      {!editing && <p style={{ fontSize: '18px', color: isDark ? 'rgba(255,255,255,0.75)' : '#666', marginBottom: '36px', maxWidth: '500px', margin: '0 auto 36px' }}>{subtitle}</p>}
      {!editing && (
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
          <button style={{ padding: '14px 32px', fontSize: '15px', fontWeight: 600, background: isDark ? '#fff' : '#5b6af0', color: isDark ? '#5b6af0' : '#fff', border: 'none', borderRadius: '8px', cursor: 'default' }}>
            {primaryText}
          </button>
          {secondaryText && (
            <button style={{ padding: '14px 32px', fontSize: '15px', fontWeight: 600, background: 'transparent', color: isDark ? 'rgba(255,255,255,0.8)' : '#5b6af0', border: `1px solid ${isDark ? 'rgba(255,255,255,0.3)' : '#5b6af0'}`, borderRadius: '8px', cursor: 'default' }}>
              {secondaryText}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// ── Quote ─────────────────────────────────────────────────────────────────────
export function QuoteRenderer({ block, editing, onUpdate }: RendererProps) {
  const { text = 'Великие дела начинаются с маленьких шагов.', author = '', source = '' } =
    block.data as Record<string, string>
  const { ref, onBlur, onKeyDown } = useInlineEdit(text, 'text', editing, onUpdate)
  return (
    <div style={{ padding: '32px 40px' }}>
      <blockquote style={{ borderLeft: '4px solid #5b6af0', paddingLeft: '24px', margin: 0 }}>
        <p
          ref={ref as React.Ref<HTMLParagraphElement>}
          contentEditable={editing ? true : undefined}
          suppressContentEditableWarning
          onBlur={editing ? onBlur : undefined}
          onKeyDown={editing ? onKeyDown : undefined}
          style={{ fontSize: '20px', fontStyle: 'italic', color: '#1a1a1a', lineHeight: 1.6, marginBottom: author ? '16px' : 0, outline: 'none' }}
        >
          {!editing && `"${text}"`}
        </p>
        {!editing && author && (
          <footer style={{ fontSize: '14px', color: '#888' }}>
            — {author}{source && <cite style={{ fontStyle: 'normal' }}>, {source}</cite>}
          </footer>
        )}
      </blockquote>
    </div>
  )
}

// ── List ──────────────────────────────────────────────────────────────────────
export function ListRenderer({ block }: RendererProps) {
  const { ordered = false, items = [] } =
    block.data as { ordered: boolean; items: string[] }
  const listItems = Array.isArray(items) && items.length > 0
    ? items
    : ['Первый пункт списка', 'Второй пункт списка', 'Третий пункт списка']
  const Tag = ordered ? 'ol' : 'ul'
  return (
    <div style={{ padding: '12px 40px' }}>
      <Tag style={{ paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {listItems.map((item, i) => (
          <li key={i} style={{ fontSize: '16px', color: '#333', lineHeight: 1.6 }}>{item}</li>
        ))}
      </Tag>
    </div>
  )
}

// ── Divider ───────────────────────────────────────────────────────────────────
export function DividerRenderer({ block }: RendererProps) {
  const { style: lineStyle = 'solid', color = '#e0e0e0', width = '100', thickness = 1 } =
    block.data as Record<string, string | number>
  return (
    <div style={{ padding: '16px 24px' }}>
      <hr style={{
        border: 'none',
        borderTop: `${thickness}px ${lineStyle} ${color}`,
        width: `${width}%`,
        margin: '0 auto',
      }} />
    </div>
  )
}

// ── Spacer ────────────────────────────────────────────────────────────────────
export function SpacerRenderer({ block }: RendererProps) {
  const { height = 60 } = block.data as { height: number }
  return (
    <div style={{ height: `${height}px`, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
      <div style={{ position: 'absolute', width: '100%', borderTop: '1px dashed var(--border)' }} />
      <span style={{ background: 'var(--bg-0, #fff)', padding: '0 8px', fontSize: '11px', color: '#999', position: 'relative', zIndex: 1 }}>
        {height}px
      </span>
    </div>
  )
}

// ── Gallery ───────────────────────────────────────────────────────────────────
export function GalleryRenderer({ block }: RendererProps) {
  const { columns = 3, images = [], gap = 12 } =
    block.data as { columns: number; images: Array<{ src: string; alt: string }>; gap: number }
  const desktopCols = Number(columns) || 3
  const gapPx = Number(gap) || 12
  const { cols } = useDevice()
  const actualCols = cols(desktopCols)
  const placeholders = Array.from({ length: actualCols }, (_, i) => i)
  const imgs = Array.isArray(images) && images.length > 0 ? images : placeholders.map((i) => ({ src: '', alt: `Фото ${i + 1}` }))

  return (
    <div style={{ padding: '8px 24px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${actualCols}, 1fr)`, gap: `${gapPx}px` }}>
        {imgs.slice(0, actualCols * 2).map((img, i) => (
          <div key={i} style={{ aspectRatio: '4/3', background: 'var(--bg-3)', borderRadius: '6px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--border)' }}>
            {img.src
              ? <img src={img.src} alt={img.alt} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <span style={{ fontSize: '20px', color: 'var(--text-muted)' }}>🖼</span>
            }
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Team ──────────────────────────────────────────────────────────────────────
export function TeamRenderer({ block }: RendererProps) {
  const { title = 'Наша команда', members = [] } =
    block.data as { title: string; members: Array<{ name: string; role: string; photo: string; bio: string }> }

  const defaultMembers = [
    { name: 'Алексей Иванов', role: 'CEO & Founder', photo: '', bio: 'Эксперт в области продуктового дизайна' },
    { name: 'Мария Смирнова', role: 'Lead Designer', photo: '', bio: 'Создаёт красивые пользовательские интерфейсы' },
    { name: 'Дмитрий Ков', role: 'CTO', photo: '', bio: 'Архитектор технической части платформы' },
  ]

  const team = Array.isArray(members) && members.length > 0 ? members : defaultMembers
  const { cols } = useDevice()

  return (
    <div style={{ padding: '60px 40px', background: '#fff' }}>
      <h2 style={{ textAlign: 'center', fontSize: '32px', fontWeight: 700, color: '#1a1a1a', marginBottom: '40px' }}>{title}</h2>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols(Math.min(team.length, 4))}, 1fr)`, gap: '32px', maxWidth: '960px', margin: '0 auto' }}>
        {team.map((m, i) => (
          <div key={i} style={{ textAlign: 'center' }}>
            <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: '#f0f0f0', margin: '0 auto 16px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px', fontWeight: 700, color: '#bbb', border: '2px solid #e8e8e8' }}>
              {m.photo ? <img src={m.photo} alt={m.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : m.name[0]}
            </div>
            <div style={{ fontWeight: 600, fontSize: '15px', color: '#1a1a1a', marginBottom: '4px' }}>{m.name}</div>
            <div style={{ fontSize: '12px', color: '#5b6af0', marginBottom: '8px' }}>{m.role}</div>
            <div style={{ fontSize: '13px', color: '#666', lineHeight: 1.5 }}>{m.bio}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Navbar ────────────────────────────────────────────────────────────────────
export function NavbarRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const logo = String(d.logo ?? d.brand ?? 'BST')
  const links = (Array.isArray(d.links) ? d.links : []) as Array<{ label: string; url: string }>
  const ctaText = String(d.ctaText ?? 'Начать')
  const sticky = d.sticky !== false
  const transparent = d.transparent === true
  const ctaBg = String(d.ctaColor ?? d.accentColor ?? '#5b6af0')
  const ctaTextColor = isColorDark(ctaBg) ? '#fff' : '#000'
  const navBg = transparent ? 'transparent' : String(d.background ?? '#fff')
  const logoColor = String(d.logoColor ?? (isColorDark(navBg) ? '#fff' : '#1a1a1a'))
  const linkColor = String(d.linkColor ?? (isColorDark(navBg) ? 'rgba(255,255,255,0.8)' : '#555'))

  const defaultLinks = [{ label: 'Возможности', url: '#' }, { label: 'Тарифы', url: '#' }, { label: 'Блог', url: '#' }]
  const navLinks = links.length > 0 ? links : defaultLinks

  return (
    <div style={{ padding: '0 32px', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: navBg, borderBottom: transparent ? 'none' : `1px solid ${isColorDark(navBg) ? 'rgba(255,255,255,0.08)' : '#f0f0f0'}`, boxShadow: sticky ? '0 2px 8px rgba(0,0,0,0.06)' : 'none' }}>
      <div style={{ fontWeight: 800, fontSize: '20px', letterSpacing: '-0.5px', color: logoColor }}>{logo}</div>
      <nav style={{ display: 'flex', gap: '28px' }}>
        {navLinks.map((link, i) => (
          <span key={i} style={{ fontSize: '14px', color: linkColor, cursor: 'default' }}>{link.label}</span>
        ))}
      </nav>
      {ctaText && (
        <button style={{ padding: '8px 20px', background: ctaBg, color: ctaTextColor, border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: 'default' }}>
          {ctaText}
        </button>
      )}
    </div>
  )
}

// ── Footer ────────────────────────────────────────────────────────────────────
export function FooterRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const logo = String(d.logo ?? d.brand ?? 'BST')
  const tagline = String(d.tagline ?? 'Конструктор сайтов нового поколения')
  const copyright = String(d.copyright ?? `© ${new Date().getFullYear()} BST`)
  const background = String(d.background ?? '#111111')
  const dark = isColorDark(background)
  const textColor = dark ? '#ffffff' : '#111111'
  const mutedColor = dark ? 'rgba(255,255,255,0.45)' : '#888'
  const borderColor = dark ? 'rgba(255,255,255,0.08)' : '#e8e8e8'

  const flatLinks = (Array.isArray(d.links) ? d.links : []) as Array<{ label: string; url?: string }>
  const columns = (Array.isArray(d.columns) ? d.columns : []) as Array<{ title: string; links: Array<{ label: string }> }>

  if (flatLinks.length > 0) {
    return (
      <div style={{ padding: '40px', background, color: textColor }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '24px', marginBottom: '28px' }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: '20px', letterSpacing: '-0.5px', marginBottom: '4px' }}>{logo}</div>
            <p style={{ fontSize: '13px', color: mutedColor, lineHeight: 1.6, margin: 0 }}>{tagline}</p>
          </div>
          <nav style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
            {flatLinks.map((link, i) => (
              <span key={i} style={{ fontSize: '13px', color: mutedColor, cursor: 'default' }}>{link.label}</span>
            ))}
          </nav>
        </div>
        <div style={{ borderTop: `1px solid ${borderColor}`, paddingTop: '16px', fontSize: '12px', color: mutedColor }}>{copyright}</div>
      </div>
    )
  }

  const defaultColumns = [
    { title: 'Продукт', links: [{ label: 'Возможности' }, { label: 'Тарифы' }, { label: 'Шаблоны' }] },
    { title: 'Компания', links: [{ label: 'О нас' }, { label: 'Блог' }, { label: 'Карьера' }] },
    { title: 'Поддержка', links: [{ label: 'Документация' }, { label: 'FAQ' }, { label: 'Контакты' }] },
  ]
  const cols = columns.length > 0 ? columns : defaultColumns

  return (
    <div style={{ padding: '48px 40px 24px', background, color: textColor }}>
      <div style={{ display: 'grid', gridTemplateColumns: `2fr repeat(${cols.length}, 1fr)`, gap: '40px', marginBottom: '40px' }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: '22px', letterSpacing: '-0.5px', marginBottom: '8px' }}>{logo}</div>
          <p style={{ fontSize: '13px', color: mutedColor, lineHeight: 1.6 }}>{tagline}</p>
        </div>
        {cols.map((col, i) => (
          <div key={i}>
            <div style={{ fontWeight: 600, fontSize: '12px', color: mutedColor, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '16px' }}>{col.title}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {col.links.map((link, li) => (
                <span key={li} style={{ fontSize: '13px', color: mutedColor, cursor: 'default' }}>{link.label}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div style={{ borderTop: `1px solid ${borderColor}`, paddingTop: '20px', fontSize: '12px', color: mutedColor }}>{copyright}</div>
    </div>
  )
}

// ── Stats ─────────────────────────────────────────────────────────────────────
export function StatsRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const title = String(d.title ?? '')
  const items = (Array.isArray(d.items) ? d.items : []) as Array<{ value: string; label: string; prefix: string; suffix: string }>
  const background = String(d.background ?? '#ffffff')
  const accentColor = String(d.accentColor ?? '#5b6af0')
  const dark = isColorDark(background)
  const titleColor = dark ? '#ffffff' : '#1a1a1a'
  const labelColor = dark ? 'rgba(255,255,255,0.55)' : '#888'

  const defaultItems = [
    { value: '10 000', label: 'Пользователей', prefix: '', suffix: '+' },
    { value: '500', label: 'Шаблонов', prefix: '', suffix: '' },
    { value: '99.9', label: 'Uptime', prefix: '', suffix: '%' },
    { value: '24', label: 'Поддержка', prefix: '', suffix: '/7' },
  ]
  const stats = items.length > 0 ? items : defaultItems
  const { cols } = useDevice()

  return (
    <div style={{ padding: '60px 40px', background }}>
      {title && <h2 style={{ textAlign: 'center', fontSize: '32px', fontWeight: 700, color: titleColor, marginBottom: '40px' }}>{title}</h2>}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols(Math.min(stats.length, 4))}, 1fr)`, gap: '24px', maxWidth: '960px', margin: '0 auto' }}>
        {stats.map((s, i) => (
          <div key={i} style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', fontWeight: 800, color: accentColor, lineHeight: 1, marginBottom: '8px' }}>
              {s.prefix}{s.value}{s.suffix}
            </div>
            <div style={{ fontSize: '14px', color: labelColor, fontWeight: 500 }}>{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Tabs ──────────────────────────────────────────────────────────────────────
export function TabsRenderer({ block }: RendererProps) {
  const { tabs = [] } =
    block.data as { tabs: Array<{ label: string; content: string }> }
  const [activeIdx, setActiveIdx] = useState(0)

  const defaultTabs = [
    { label: 'Вкладка 1', content: 'Содержимое первой вкладки. Добавьте сюда ваш контент.' },
    { label: 'Вкладка 2', content: 'Содержимое второй вкладки.' },
    { label: 'Вкладка 3', content: 'Содержимое третьей вкладки.' },
  ]
  const tabList = Array.isArray(tabs) && tabs.length > 0 ? tabs : defaultTabs
  const safeIdx = Math.min(activeIdx, tabList.length - 1)

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', gap: '0', borderBottom: '2px solid var(--border)', marginBottom: '24px' }}>
        {tabList.map((tab, i) => (
          <div
            key={i}
            onClick={() => setActiveIdx(i)}
            style={{
              padding: '10px 20px', fontSize: '14px',
              fontWeight: i === safeIdx ? 600 : 400,
              color: i === safeIdx ? '#5b6af0' : 'var(--text-secondary)',
              borderBottom: i === safeIdx ? '2px solid #5b6af0' : '2px solid transparent',
              marginBottom: '-2px', cursor: 'pointer', whiteSpace: 'nowrap',
            }}
          >
            {tab.label}
          </div>
        ))}
      </div>
      <div style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.7 }}>
        {tabList[safeIdx]?.content}
      </div>
    </div>
  )
}

// ── Map ───────────────────────────────────────────────────────────────────────
export function MapRenderer({ block }: RendererProps) {
  const { address = 'Москва, Россия', height = 400, zoom = 14 } =
    block.data as { address: string; height: number; zoom: number }

  return (
    <div style={{ padding: '8px 24px' }}>
      <div style={{
        height: `${height}px`,
        background: '#e8edf2',
        borderRadius: '8px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        border: '1px solid #d0d8e0',
      }}>
        <div style={{ fontSize: '32px' }}>🗺</div>
        <div style={{ fontSize: '14px', fontWeight: 600, color: '#555' }}>{address}</div>
        <div style={{ fontSize: '11px', color: '#999' }}>Zoom: {zoom} · Карта будет отображена при публикации</div>
      </div>
    </div>
  )
}

// ── Social Links ──────────────────────────────────────────────────────────────
export function SocialLinksRenderer({ block }: RendererProps) {
  const { title = '', align = 'center', links = [], size = 40 } =
    block.data as { title: string; align: string; links: Array<{ network: string; url: string }>; size: number }

  const icons: Record<string, string> = {
    telegram: '✈', vk: 'VK', instagram: '◉', youtube: '▶',
    twitter: 'X', facebook: 'f', tiktok: '♪', linkedin: 'in',
    github: '⌥', whatsapp: '✆',
  }
  const defaultLinks = [
    { network: 'telegram', url: '#' },
    { network: 'vk', url: '#' },
    { network: 'instagram', url: '#' },
    { network: 'youtube', url: '#' },
  ]
  const socialLinks = Array.isArray(links) && links.length > 0 ? links : defaultLinks
  const sz = Number(size) || 40

  return (
    <div style={{ padding: '24px', textAlign: align as 'left' | 'center' | 'right' }}>
      {title && <div style={{ fontSize: '13px', color: '#888', marginBottom: '12px' }}>{title}</div>}
      <div style={{ display: 'inline-flex', gap: '10px', flexWrap: 'wrap', justifyContent: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start' }}>
        {socialLinks.map((link, i) => (
          <div key={i} style={{
            width: `${sz}px`,
            height: `${sz}px`,
            borderRadius: '50%',
            background: '#f0f0f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: sz > 32 ? '14px' : '11px',
            fontWeight: 700,
            color: '#555',
            cursor: 'default',
            border: '1px solid #e0e0e0',
          }}>
            {icons[link.network] ?? link.network[0]?.toUpperCase()}
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Accordion ─────────────────────────────────────────────────────────────────
export function AccordionRenderer({ block }: RendererProps) {
  const { items = [], allowMultiple = false } =
    block.data as { items: Array<{ title: string; content: string }>; allowMultiple: boolean }
  const [openSet, setOpenSet] = useState<Set<number>>(new Set([0]))

  const defaultItems = [
    { title: 'Раздел 1', content: 'Содержимое первого раздела.' },
    { title: 'Раздел 2', content: 'Содержимое второго раздела.' },
    { title: 'Раздел 3', content: 'Содержимое третьего раздела.' },
  ]
  const accordionItems = Array.isArray(items) && items.length > 0 ? items : defaultItems

  function toggle(i: number) {
    setOpenSet((prev) => {
      const next = new Set(prev)
      if (next.has(i)) {
        next.delete(i)
      } else {
        if (!allowMultiple) next.clear()
        next.add(i)
      }
      return next
    })
  }

  return (
    <div style={{ padding: '24px 40px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {accordionItems.map((item, i) => {
          const isOpen = openSet.has(i)
          return (
            <div key={i} style={{ border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>
              <div
                onClick={() => toggle(i)}
                style={{ padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: isOpen ? 'var(--bg-2)' : 'var(--bg-1)', cursor: 'pointer' }}
              >
                <span style={{ fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)' }}>{item.title}</span>
                <span style={{ color: '#5b6af0', fontSize: '18px', flexShrink: 0, marginLeft: '12px', transform: isOpen ? 'rotate(45deg)' : 'none', transition: 'transform 0.2s', display: 'inline-block' }}>+</span>
              </div>
              {isOpen && (
                <div style={{ padding: '0 20px 14px', fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.7 }}>{item.content}</div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Progress ──────────────────────────────────────────────────────────────────
export function ProgressRenderer({ block }: RendererProps) {
  const { items = [], color = '#5b6af0', height = 8, showPercent = true } =
    block.data as { items: Array<{ label: string; value: number }>; color: string; height: number; showPercent: boolean }

  const defaultItems = [
    { label: 'HTML / CSS', value: 95 },
    { label: 'JavaScript', value: 82 },
    { label: 'React', value: 78 },
    { label: 'Node.js', value: 65 },
  ]
  const bars = Array.isArray(items) && items.length > 0 ? items : defaultItems
  const [mounted, setMounted] = useState(false)
  useEffect(() => { const t = setTimeout(() => setMounted(true), 50); return () => clearTimeout(t) }, [])
  const h = Number(height) || 8

  return (
    <div style={{ padding: '40px 40px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {bars.map((bar, i) => {
          const pct = Math.min(100, Math.max(0, Number(bar.value) || 0))
          return (
            <div key={i}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)' }}>{bar.label}</span>
                {showPercent !== false && <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{pct}%</span>}
              </div>
              <div style={{ height: `${h}px`, background: 'var(--bg-3)', borderRadius: `${h}px`, overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: mounted ? `${pct}%` : '0%',
                  background: color || '#5b6af0',
                  borderRadius: `${h}px`,
                  transition: 'width 0.8s ease',
                }} />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Timeline ──────────────────────────────────────────────────────────────────
export function TimelineRenderer({ block }: RendererProps) {
  const { title = '', items = [], accent = '#5b6af0' } =
    block.data as { title: string; items: Array<{ date: string; title: string; description: string }>; accent: string }

  const defaultItems = [
    { date: '2020', title: 'Основание компании', description: 'Начало пути — маленькая команда с большой идеей.' },
    { date: '2021', title: 'Первые 1000 клиентов', description: 'Достигли первого значимого milestone.' },
    { date: '2022', title: 'Раунд A', description: 'Привлекли инвестиции для масштабирования продукта.' },
    { date: '2023', title: 'Международный запуск', description: 'Вышли на рынки Европы и Азии.' },
  ]
  const timelineItems = Array.isArray(items) && items.length > 0 ? items : defaultItems
  const accentColor = accent || '#5b6af0'

  return (
    <div style={{ padding: '40px 40px' }}>
      {title && <h2 style={{ fontSize: '28px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '32px', textAlign: 'center' }}>{title}</h2>}
      <div style={{ position: 'relative', maxWidth: '640px', margin: '0 auto' }}>
        <div style={{ position: 'absolute', left: '79px', top: '12px', bottom: '12px', width: '2px', background: 'var(--border)' }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
          {timelineItems.map((item, i) => (
            <div key={i} style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
              <div style={{ width: '60px', textAlign: 'right', flexShrink: 0, paddingTop: '2px' }}>
                <span style={{ fontSize: '12px', fontWeight: 600, color: accentColor }}>{item.date}</span>
              </div>
              <div style={{ position: 'relative', width: '18px', flexShrink: 0, display: 'flex', justifyContent: 'center', paddingTop: '4px' }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: accentColor, border: '2px solid var(--bg-1)', zIndex: 1, flexShrink: 0 }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '4px' }}>{item.title}</div>
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>{item.description}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── Before / After ────────────────────────────────────────────────────────────
export function BeforeAfterRenderer({ block }: RendererProps) {
  const {
    beforeImage = '',
    afterImage = '',
    beforeLabel = 'До',
    afterLabel = 'После',
    initialPosition = 50,
  } = block.data as { beforeImage: string; afterImage: string; beforeLabel: string; afterLabel: string; initialPosition: number }

  const [pos, setPos] = useState(Number(initialPosition) || 50)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const dragging = React.useRef(false)

  function getPos(clientX: number) {
    const rect = containerRef.current?.getBoundingClientRect()
    if (!rect) return pos
    return Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100))
  }

  return (
    <div style={{ padding: '8px 24px' }}>
      <div
        ref={containerRef}
        style={{ position: 'relative', aspectRatio: '16/9', borderRadius: '8px', overflow: 'hidden', cursor: 'ew-resize', userSelect: 'none', background: 'var(--bg-3)', border: '1px solid var(--border)' }}
        onMouseDown={(e) => { dragging.current = true; setPos(getPos(e.clientX)) }}
        onMouseMove={(e) => { if (dragging.current) setPos(getPos(e.clientX)) }}
        onMouseUp={() => { dragging.current = false }}
        onMouseLeave={() => { dragging.current = false }}
      >
        {afterImage
          ? <img src={afterImage} alt={afterLabel} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
          : <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>{afterLabel}</div>
        }
        <div style={{ position: 'absolute', inset: 0, clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
          {beforeImage
            ? <img src={beforeImage} alt={beforeLabel} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
            : <div style={{ position: 'absolute', inset: 0, background: 'var(--bg-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>{beforeLabel}</div>
          }
        </div>
        <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${pos}%`, transform: 'translateX(-50%)', width: '2px', background: '#fff', boxShadow: '0 0 6px rgba(0,0,0,0.4)' }}>
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: '36px', height: '36px', borderRadius: '50%', background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', color: '#555' }}>⇔</div>
        </div>
        <div style={{ position: 'absolute', top: '12px', left: '12px', background: 'rgba(0,0,0,0.5)', color: '#fff', fontSize: '11px', fontWeight: 600, padding: '3px 8px', borderRadius: '4px', pointerEvents: 'none' }}>{beforeLabel}</div>
        <div style={{ position: 'absolute', top: '12px', right: '12px', background: 'rgba(0,0,0,0.5)', color: '#fff', fontSize: '11px', fontWeight: 600, padding: '3px 8px', borderRadius: '4px', pointerEvents: 'none' }}>{afterLabel}</div>
      </div>
    </div>
  )
}

// ── Counter Animation ─────────────────────────────────────────────────────────
export function CounterAnimRenderer({ block }: RendererProps) {
  const { items = [], duration = 2000, color = '#5b6af0' } =
    block.data as { items: Array<{ value: number; label: string; prefix: string; suffix: string }>; duration: number; color: string }

  const defaultItems = [
    { value: 10000, label: 'Клиентов', prefix: '', suffix: '+' },
    { value: 500, label: 'Шаблонов', prefix: '', suffix: '' },
    { value: 99, label: 'Uptime', prefix: '', suffix: '%' },
    { value: 24, label: 'Поддержка', prefix: '', suffix: '/7' },
  ]
  const counterItems = Array.isArray(items) && items.length > 0 ? items : defaultItems
  const [counts, setCounts] = useState<number[]>(counterItems.map(() => 0))
  const dur = Number(duration) || 2000

  useEffect(() => {
    const start = Date.now()
    const targets = counterItems.map((it) => Number(it.value) || 0)
    const tick = () => {
      const elapsed = Date.now() - start
      const progress = Math.min(1, elapsed / dur)
      const eased = 1 - Math.pow(1 - progress, 3)
      setCounts(targets.map((t) => Math.round(t * eased)))
      if (progress < 1) requestAnimationFrame(tick)
    }
    const raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [dur, counterItems])

  const { cols } = useDevice()

  return (
    <div style={{ padding: '60px 40px', background: 'var(--bg-1)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols(Math.min(counterItems.length, 4))}, 1fr)`, gap: '24px', maxWidth: '960px', margin: '0 auto' }}>
        {counterItems.map((item, i) => (
          <div key={i} style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '52px', fontWeight: 800, color: color || '#5b6af0', lineHeight: 1, marginBottom: '8px' }}>
              {item.prefix}{counts[i]?.toLocaleString('ru-RU')}{item.suffix}
            </div>
            <div style={{ fontSize: '14px', color: 'var(--text-secondary)', fontWeight: 500 }}>{item.label}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── HTML Embed ────────────────────────────────────────────────────────────────
export function HtmlEmbedRenderer({ block }: RendererProps) {
  const { html = '', caption = '', height = 400 } =
    block.data as { html: string; caption: string; height: number }
  const h = Number(height) || 400

  if (!html) {
    return (
      <div style={{ padding: '8px 24px' }}>
        <div style={{ height: `${h}px`, background: 'var(--bg-3)', borderRadius: '8px', border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', color: 'var(--text-muted)' }}>
          <div style={{ fontSize: '32px' }}>{'</>'}</div>
          <div style={{ fontSize: '13px' }}>HTML-вставка</div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', opacity: 0.6 }}>Вставьте HTML-код в свойствах блока</div>
        </div>
        {caption && <p style={{ fontSize: '12px', color: '#888', textAlign: 'center', marginTop: '8px' }}>{caption}</p>}
      </div>
    )
  }

  return (
    <div style={{ padding: '8px 24px' }}>
      <div
        style={{ height: `${h}px`, borderRadius: '8px', overflow: 'hidden', border: '1px solid var(--border)', position: 'relative' }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {caption && <p style={{ fontSize: '12px', color: '#888', textAlign: 'center', marginTop: '8px' }}>{caption}</p>}
    </div>
  )
}

// ── Hero Split ────────────────────────────────────────────────────────────────
export function HeroSplitRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const title = String(d.title ?? 'Создайте сайт за 30 секунд')
  const subtitle = String(d.subtitle ?? 'Всё что нужно для современного веб-сайта в одном конструкторе.')
  const pbRaw = d.primaryButton
  const sbRaw = d.secondaryButton
  const primaryButton = typeof pbRaw === 'object' && pbRaw !== null ? String((pbRaw as Record<string,unknown>).text ?? '') : String(pbRaw ?? 'Попробовать бесплатно')
  const secondaryButton = typeof sbRaw === 'object' && sbRaw !== null ? String((sbRaw as Record<string,unknown>).text ?? '') : String(sbRaw ?? 'Смотреть демо')
  const imagePosition = String(d.imagePosition ?? d.reverse === true ? 'left' : 'right')
  const image = String(d.image ?? '')
  const { isMobile } = useDevice()

  return (
    <div style={{
      display: 'flex',
      flexDirection: isMobile ? 'column' : imagePosition === 'left' ? 'row-reverse' : 'row',
      minHeight: '480px',
      background: 'linear-gradient(135deg, #0d0d0d 0%, #1a1a2e 100%)',
      color: '#fff',
    }}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: isMobile ? '48px 24px' : '60px 48px', gap: '24px' }}>
        <h1 style={{ fontSize: isMobile ? '32px' : '44px', fontWeight: 800, lineHeight: 1.15, letterSpacing: '-0.5px', margin: 0 }}>{title}</h1>
        <p style={{ fontSize: '17px', color: '#aaa', lineHeight: 1.6, margin: 0, maxWidth: '480px' }}>{subtitle}</p>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {primaryButton && <button style={{ padding: '13px 28px', fontSize: '15px', fontWeight: 600, background: '#5b6af0', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'default' }}>{primaryButton}</button>}
          {secondaryButton && <button style={{ padding: '13px 28px', fontSize: '15px', fontWeight: 600, background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.25)', borderRadius: '8px', cursor: 'default' }}>{secondaryButton}</button>}
        </div>
      </div>
      <div style={{ flex: 1, minHeight: isMobile ? '240px' : undefined, background: 'rgba(255,255,255,0.05)', overflow: 'hidden', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {image
          ? <img src={image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0 }} />
          : <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.2)' }}>
              <div style={{ fontSize: '48px', marginBottom: '8px' }}>🖼</div>
              <div style={{ fontSize: '13px' }}>Добавьте изображение</div>
            </div>
        }
      </div>
    </div>
  )
}

// ── Logo Strip ────────────────────────────────────────────────────────────────
export function LogoStripRenderer({ block }: RendererProps) {
  const { title = 'Нам доверяют', logos = [] } = block.data as { title: string; logos: Array<{ name: string; image: string }> }
  const defaultLogos = ['Яндекс', 'Сбербанк', 'Mail.ru', 'VK', 'Ozon', 'Avito'].map(n => ({ name: n, image: '' }))
  const items = Array.isArray(logos) && logos.length > 0 ? logos : defaultLogos

  return (
    <div style={{ padding: '32px 40px', background: '#fafafa', borderTop: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0' }}>
      {title && <div style={{ textAlign: 'center', fontSize: '12px', fontWeight: 600, color: '#aaa', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '24px' }}>{title}</div>}
      <div style={{ display: 'flex', gap: '32px', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
        {items.map((logo, i) => (
          <div key={i} style={{ height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.5, filter: 'grayscale(1)' }}>
            {logo.image
              ? <img src={logo.image} alt={logo.name} style={{ height: '100%', objectFit: 'contain', maxWidth: '100px' }} />
              : <span style={{ fontSize: '15px', fontWeight: 700, color: '#555', letterSpacing: '-0.5px' }}>{logo.name}</span>
            }
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Process Steps ─────────────────────────────────────────────────────────────
export function ProcessStepsRenderer({ block }: RendererProps) {
  const { title = 'Как это работает', steps = [] } = block.data as { title: string; steps: Array<{ title: string; description: string }> }
  const defaultSteps = [
    { title: 'Зарегистрируйтесь', description: 'Создайте бесплатный аккаунт за 30 секунд — без карты.' },
    { title: 'Выберите шаблон', description: 'Возьмите готовый шаблон или начните с пустого холста.' },
    { title: 'Настройте дизайн', description: 'Измените цвета, тексты, изображения в визуальном редакторе.' },
    { title: 'Опубликуйте', description: 'Нажмите «Опубликовать» — сайт появится онлайн мгновенно.' },
  ]
  const items = Array.isArray(steps) && steps.length > 0 ? steps : defaultSteps
  const { cols } = useDevice()

  return (
    <div style={{ padding: '60px 40px', background: '#fff' }}>
      {title && <h2 style={{ textAlign: 'center', fontSize: '32px', fontWeight: 700, color: '#1a1a1a', marginBottom: '48px' }}>{title}</h2>}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols(Math.min(items.length, 4))}, 1fr)`, gap: '32px', maxWidth: '960px', margin: '0 auto' }}>
        {items.map((step, i) => (
          <div key={i}>
            <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: '#5b6af0', color: '#fff', fontSize: '16px', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
              {i + 1}
            </div>
            <h3 style={{ fontSize: '17px', fontWeight: 600, color: '#1a1a1a', marginBottom: '8px' }}>{step.title}</h3>
            <p style={{ fontSize: '14px', color: '#666', lineHeight: 1.6 }}>{step.description}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Contact Block ─────────────────────────────────────────────────────────────
export function ContactBlockRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const title = String(d.title ?? 'Свяжитесь с нами')
  const subtitle = String(d.subtitle ?? '')
  const email = String(d.email ?? 'hello@example.com')
  const phone = String(d.phone ?? '+7 (900) 123-45-67')
  const address = String(d.address ?? 'Москва, Красная площадь 1')
  const showForm = d.showForm !== false
  const { isMobile } = useDevice()

  return (
    <div style={{ padding: '60px 40px', background: '#fff' }}>
      <div style={{ display: 'grid', gridTemplateColumns: !isMobile && showForm ? '1fr 1fr' : '1fr', gap: '48px', maxWidth: '960px', margin: '0 auto' }}>
        <div>
          <h2 style={{ fontSize: '32px', fontWeight: 700, color: '#1a1a1a', marginBottom: '16px' }}>{title}</h2>
          {subtitle && <p style={{ fontSize: '16px', color: '#666', marginBottom: '32px', lineHeight: 1.6 }}>{subtitle}</p>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {email && <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}><span style={{ fontSize: '20px' }}>✉</span><span style={{ fontSize: '15px', color: '#333' }}>{email}</span></div>}
            {phone && <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}><span style={{ fontSize: '20px' }}>✆</span><span style={{ fontSize: '15px', color: '#333' }}>{phone}</span></div>}
            {address && <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}><span style={{ fontSize: '20px' }}>📍</span><span style={{ fontSize: '15px', color: '#333' }}>{address}</span></div>}
          </div>
        </div>
        {showForm && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {['Ваше имя', 'Email', 'Телефон'].map((ph) => (
              <div key={ph} style={{ padding: '10px 14px', border: '1px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', color: '#aaa', background: '#fafafa' }}>{ph}</div>
            ))}
            <div style={{ padding: '10px 14px', border: '1px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', color: '#aaa', background: '#fafafa', height: '80px' }}>Сообщение</div>
            <button style={{ padding: '12px', background: '#5b6af0', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 600, cursor: 'default' }}>Отправить сообщение</button>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Newsletter ────────────────────────────────────────────────────────────────
export function NewsletterRenderer({ block, editing, onUpdate }: RendererProps) {
  const { title = 'Будьте в курсе', subtitle = 'Подпишитесь и получайте новости первыми.', buttonText = 'Подписаться', background = '#f5f7ff', placeholder = 'Ваш email адрес' } =
    block.data as Record<string, string>
  const { ref, onBlur, onKeyDown } = useInlineEdit(title, 'title', editing, onUpdate)

  return (
    <div style={{ padding: '64px 40px', background, textAlign: 'center' }}>
      <h2
        ref={ref as React.Ref<HTMLHeadingElement>}
        contentEditable={editing ? true : undefined}
        suppressContentEditableWarning
        onBlur={editing ? onBlur : undefined}
        onKeyDown={editing ? onKeyDown : undefined}
        style={{ fontSize: '32px', fontWeight: 700, color: '#1a1a1a', marginBottom: '12px', outline: 'none' }}
      >
        {!editing && title}
      </h2>
      {!editing && subtitle && <p style={{ fontSize: '16px', color: '#666', margin: '0 auto 32px', maxWidth: '480px' }}>{subtitle}</p>}
      {!editing && (
        <div style={{ display: 'flex', gap: '10px', maxWidth: '480px', margin: '0 auto' }}>
          <div style={{ flex: 1, padding: '11px 16px', border: '1px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', color: '#aaa', background: '#fff', textAlign: 'left' }}>{placeholder}</div>
          <button style={{ padding: '11px 24px', background: '#5b6af0', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'default', flexShrink: 0 }}>{buttonText}</button>
        </div>
      )}
    </div>
  )
}

// ── Table ─────────────────────────────────────────────────────────────────────
export function TableRenderer({ block }: RendererProps) {
  const { headers = [], rows = [], striped = true, bordered = true } = block.data as { headers: string[]; rows: string[][]; striped: boolean; bordered: boolean }
  const ths = Array.isArray(headers) && headers.length > 0 ? headers : ['Параметр', 'Значение', 'Описание']
  const trs = Array.isArray(rows) && rows.length > 0 ? rows : [['Тариф Free', '0 ₽', 'Бесплатно навсегда'], ['Тариф Personal', '790 ₽/мес', 'Для фрилансеров'], ['Тариф Business', '1990 ₽/мес', 'Для компаний']]

  return (
    <div style={{ padding: '24px 40px', overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
        <thead>
          <tr>
            {ths.map((h, i) => (
              <th key={i} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#1a1a1a', background: '#f5f5f5', borderBottom: '2px solid #e0e0e0', border: bordered ? '1px solid #e0e0e0' : undefined }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {trs.map((row, ri) => (
            <tr key={ri} style={{ background: striped && ri % 2 === 1 ? '#fafafa' : '#fff' }}>
              {Array.isArray(row) && row.map((cell, ci) => (
                <td key={ci} style={{ padding: '11px 16px', color: '#444', border: bordered ? '1px solid #e0e0e0' : undefined, borderBottom: '1px solid #f0f0f0' }}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── Icon Features ─────────────────────────────────────────────────────────────
export function IconFeaturesRenderer({ block }: RendererProps) {
  const { title = '', items = [] } = block.data as { title: string; items: Array<{ icon: string; title: string; description: string; color?: string }> }
  const defaultItems = [
    { icon: '🚀', title: 'Быстрый запуск', description: 'Сайт готов за 30 минут без навыков программирования', color: '#5b6af0' },
    { icon: '🎨', title: 'Красивый дизайн', description: 'Профессиональные шаблоны для любого бизнеса', color: '#ec4899' },
    { icon: '📊', title: 'Аналитика', description: 'Понимайте своих посетителей с детальной статистикой', color: '#10b981' },
    { icon: '🔒', title: 'Безопасность', description: 'SSL-сертификат и защита данных включены', color: '#f59e0b' },
  ]
  const feats = Array.isArray(items) && items.length > 0 ? items : defaultItems
  const { cols } = useDevice()

  return (
    <div style={{ padding: '60px 40px', background: '#fff' }}>
      {title && <h2 style={{ textAlign: 'center', fontSize: '32px', fontWeight: 700, color: '#1a1a1a', marginBottom: '48px' }}>{title}</h2>}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols(Math.min(feats.length, 4))}, 1fr)`, gap: '32px', maxWidth: '960px', margin: '0 auto' }}>
        {feats.map((feat, i) => (
          <div key={i} style={{ padding: '28px 24px', borderRadius: '16px', background: '#fafafa', border: '1px solid #f0f0f0' }}>
            <div style={{ width: '52px', height: '52px', borderRadius: '14px', background: feat.color ? `${feat.color}1a` : '#f0f3ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', marginBottom: '16px' }}>{feat.icon}</div>
            <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#1a1a1a', marginBottom: '8px' }}>{feat.title}</h3>
            <p style={{ fontSize: '13px', color: '#666', lineHeight: 1.6 }}>{feat.description}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Banner ────────────────────────────────────────────────────────────────────
export function BannerRenderer({ block, editing, onUpdate }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const text = String(d.text ?? '🎉 Специальное предложение — скидка 30% до конца месяца!')
  const background = String(d.background ?? '#5b6af0')
  const textColor = String(d.textColor ?? '#fff')
  const buttonText = String(d.buttonText ?? 'Узнать больше')
  const { ref, onBlur, onKeyDown } = useInlineEdit(text, 'text', editing, onUpdate)

  return (
    <div style={{ padding: '14px 24px', background, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', flexWrap: 'wrap' }}>
      <span
        ref={ref as React.Ref<HTMLSpanElement>}
        contentEditable={editing ? true : undefined}
        suppressContentEditableWarning
        onBlur={editing ? onBlur : undefined}
        onKeyDown={editing ? onKeyDown : undefined}
        style={{ fontSize: '14px', fontWeight: 500, color: textColor, outline: 'none' }}
      >
        {!editing && text}
      </span>
      {!editing && buttonText && (
        <button style={{ padding: '6px 16px', background: 'rgba(255,255,255,0.2)', color: textColor, border: `1px solid ${textColor}40`, borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: 'default', flexShrink: 0 }}>
          {buttonText}
        </button>
      )}
    </div>
  )
}

// ── Testimonial Single ────────────────────────────────────────────────────────
export function TestimonialSingleRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const text = String(d.text ?? 'BST — лучший конструктор из всех что я пробовал. Запустил сайт за час и получил первых клиентов в тот же день!')
  const name = String(d.name ?? 'Александр Петров')
  const role = String(d.role ?? 'Предприниматель')
  const avatar = String(d.avatar ?? '')
  const rating = Number(d.rating ?? 5)

  return (
    <div style={{ padding: '64px 40px', background: '#fff', textAlign: 'center' }}>
      <div style={{ maxWidth: '680px', margin: '0 auto' }}>
        <div style={{ fontSize: '40px', color: '#5b6af0', marginBottom: '24px', lineHeight: 1 }}>❝</div>
        <p style={{ fontSize: '22px', fontStyle: 'italic', color: '#1a1a1a', lineHeight: 1.6, marginBottom: '32px', fontWeight: 300 }}>{text}</p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '14px' }}>
          <div style={{ width: '52px', height: '52px', borderRadius: '50%', background: '#f0f0f0', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', fontWeight: 700, color: '#bbb', flexShrink: 0 }}>
            {avatar ? <img src={avatar} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : name[0]}
          </div>
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontWeight: 600, fontSize: '15px', color: '#1a1a1a' }}>{name}</div>
            <div style={{ fontSize: '13px', color: '#888' }}>{role}</div>
          </div>
          {rating > 0 && (
            <div style={{ display: 'flex', gap: '3px', marginLeft: '8px' }}>
              {Array.from({ length: rating }).map((_, i) => <span key={i} style={{ color: '#f59e0b', fontSize: '16px' }}>★</span>)}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Video Hero ────────────────────────────────────────────────────────────────
export function VideoHeroRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const title = String(d.title ?? 'Смотрите как это работает')
  const subtitle = String(d.subtitle ?? 'Короткое видео покажет все возможности за 2 минуты')
  const poster = String(d.poster ?? '')
  const h = Number(d.height ?? 500) || 500

  return (
    <div style={{ position: 'relative', background: '#0d0d0d', minHeight: `${h}px`, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
      {poster
        ? <img src={poster} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.4 }} />
        : <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #0d0d0d 0%, #1a1a2e 100%)' }} />
      }
      <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', padding: '40px 24px', maxWidth: '700px' }}>
        <h2 style={{ fontSize: '40px', fontWeight: 800, color: '#fff', marginBottom: '16px', lineHeight: 1.2 }}>{title}</h2>
        {subtitle && <p style={{ fontSize: '17px', color: 'rgba(255,255,255,0.7)', marginBottom: '36px', lineHeight: 1.6 }}>{subtitle}</p>}
        <div style={{ width: '72px', height: '72px', borderRadius: '50%', background: 'rgba(255,255,255,0.15)', border: '2px solid rgba(255,255,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto', cursor: 'default', fontSize: '24px', color: '#fff' }}>
          ▶
        </div>
      </div>
    </div>
  )
}

function ImageTextRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const image = String(d.image ?? 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600&q=80')
  const imageAlt = String(d.imageAlt ?? '')
  const imagePosition = String(d.imagePosition ?? 'left')
  const tag = String(d.tag ?? '')
  const title = String(d.title ?? 'Делаем сложное простым')
  const text = String(d.text ?? 'Наша команда экспертов помогает компаниям трансформироваться и расти.')
  const buttonText = String(d.buttonText ?? 'Узнать больше')
  const buttonUrl = String(d.buttonUrl ?? '#')
  const imgEl = <img src={image} alt={imageAlt} style={{ width: '100%', borderRadius: '12px', objectFit: 'cover', maxHeight: '420px' }} />
  const textEl = (
    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '16px' }}>
      {tag && <span style={{ fontSize: '12px', fontWeight: 600, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{tag}</span>}
      <h2 style={{ fontSize: '32px', fontWeight: 700, color: '#111', lineHeight: 1.25, margin: 0 }}>{title}</h2>
      <p style={{ fontSize: '16px', color: '#555', lineHeight: 1.7, margin: 0 }}>{text}</p>
      {buttonText && (
        <a href={buttonUrl} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: '#6366f1', color: '#fff', padding: '12px 28px', borderRadius: '8px', fontWeight: 600, fontSize: '15px', textDecoration: 'none', width: 'fit-content' }}>
          {buttonText} →
        </a>
      )}
    </div>
  )
  return (
    <div style={{ padding: '64px 24px', maxWidth: '1100px', margin: '0 auto' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '56px', alignItems: 'center' }}>
        {imagePosition === 'left' ? <>{imgEl}{textEl}</> : <>{textEl}{imgEl}</>}
      </div>
    </div>
  )
}

function CardGridRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const title = String(d.title ?? 'Наши услуги')
  const subtitle = String(d.subtitle ?? '')
  const columns = Number(d.columns ?? 3)
  const { cols } = useDevice()
  const items = Array.isArray(d.items) ? d.items as Record<string, unknown>[] : []
  return (
    <div style={{ padding: '64px 24px' }}>
      <div style={{ textAlign: 'center', marginBottom: '48px' }}>
        <h2 style={{ fontSize: '36px', fontWeight: 700, color: '#111', marginBottom: '12px' }}>{title}</h2>
        {subtitle && <p style={{ fontSize: '17px', color: '#666', maxWidth: '600px', margin: '0 auto' }}>{subtitle}</p>}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols(columns)}, 1fr)`, gap: '28px', maxWidth: '1200px', margin: '0 auto' }}>
        {items.map((item, i) => (
          <div key={i} style={{ background: '#fff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 16px rgba(0,0,0,0.08)', transition: 'transform 0.2s' }}>
            {!!item.image && <img src={String(item.image)} alt={String(item.title ?? '')} style={{ width: '100%', height: '200px', objectFit: 'cover' }} />}
            <div style={{ padding: '20px 24px 24px' }}>
              {!!item.tag && <span style={{ fontSize: '11px', fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{String(item.tag)}</span>}
              <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#111', margin: '8px 0 10px' }}>{String(item.title ?? '')}</h3>
              <p style={{ fontSize: '14px', color: '#666', lineHeight: 1.6, margin: '0 0 16px' }}>{String(item.text ?? '')}</p>
              {!!item.linkText && (
                <a href={String(item.linkUrl ?? '#')} style={{ fontSize: '14px', fontWeight: 600, color: '#6366f1', textDecoration: 'none' }}>
                  {String(item.linkText)} →
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function TwoColumnsRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const ratio = String(d.ratio ?? '50/50')
  const leftTitle = String(d.leftTitle ?? '')
  const leftText = String(d.leftText ?? '')
  const rightTitle = String(d.rightTitle ?? '')
  const rightText = String(d.rightText ?? '')
  const gap = Number(d.gap ?? 48)
  const ratioMap: Record<string, string> = {
    '50/50': '1fr 1fr', '40/60': '2fr 3fr', '60/40': '3fr 2fr',
    '33/67': '1fr 2fr', '67/33': '2fr 1fr',
  }
  return (
    <div style={{ padding: '64px 24px', maxWidth: '1100px', margin: '0 auto' }}>
      <div style={{ display: 'grid', gridTemplateColumns: ratioMap[ratio] ?? '1fr 1fr', gap: `${gap}px` }}>
        <div>
          {leftTitle && <h3 style={{ fontSize: '22px', fontWeight: 700, color: '#111', marginBottom: '16px' }}>{leftTitle}</h3>}
          <p style={{ fontSize: '16px', color: '#555', lineHeight: 1.7 }}>{leftText}</p>
        </div>
        <div>
          {rightTitle && <h3 style={{ fontSize: '22px', fontWeight: 700, color: '#111', marginBottom: '16px' }}>{rightTitle}</h3>}
          <p style={{ fontSize: '16px', color: '#555', lineHeight: 1.7 }}>{rightText}</p>
        </div>
      </div>
    </div>
  )
}

function HighlightBoxRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const type = String(d.type ?? 'info')
  const title = String(d.title ?? '')
  const text = String(d.text ?? '')
  const icon = String(d.icon ?? '')
  const styles: Record<string, { bg: string; border: string; iconBg: string; titleColor: string }> = {
    info:    { bg: '#eff6ff', border: '#3b82f6', iconBg: '#dbeafe', titleColor: '#1d4ed8' },
    success: { bg: '#f0fdf4', border: '#22c55e', iconBg: '#dcfce7', titleColor: '#15803d' },
    warning: { bg: '#fffbeb', border: '#f59e0b', iconBg: '#fef3c7', titleColor: '#b45309' },
    error:   { bg: '#fef2f2', border: '#ef4444', iconBg: '#fee2e2', titleColor: '#b91c1c' },
    tip:     { bg: '#faf5ff', border: '#a855f7', iconBg: '#f3e8ff', titleColor: '#7e22ce' },
  }
  const s = styles[type] ?? styles.info
  return (
    <div style={{ padding: '24px 24px', maxWidth: '800px', margin: '0 auto' }}>
      <div style={{ display: 'flex', gap: '16px', padding: '20px 24px', background: s.bg, borderLeft: `4px solid ${s.border}`, borderRadius: '8px' }}>
        {icon && (
          <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: s.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', flexShrink: 0 }}>
            {icon}
          </div>
        )}
        <div>
          {title && <p style={{ fontWeight: 700, color: s.titleColor, fontSize: '15px', margin: '0 0 6px' }}>{title}</p>}
          <p style={{ color: '#444', fontSize: '15px', lineHeight: 1.65, margin: 0 }}>{text}</p>
        </div>
      </div>
    </div>
  )
}

function CodeBlockRenderer({ block }: RendererProps) {
  const [copied, setCopied] = React.useState(false)
  const d = block.data as Record<string, unknown>
  const code = String(d.code ?? '')
  const language = String(d.language ?? 'javascript')
  const filename = String(d.filename ?? '')
  const showLineNumbers = d.showLineNumbers !== false
  const theme = String(d.theme ?? 'dark')
  const isDark = theme === 'dark'
  const lines = code.split('\n')
  const handleCopy = () => {
    navigator.clipboard.writeText(code).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000) }).catch(() => {})
  }
  return (
    <div style={{ padding: '24px 24px', maxWidth: '900px', margin: '0 auto' }}>
      <div style={{ borderRadius: '10px', overflow: 'hidden', fontFamily: 'monospace', boxShadow: '0 4px 24px rgba(0,0,0,0.15)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', background: isDark ? '#1e1e1e' : '#e5e7eb' }}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: '6px' }}>
              {['#ff5f56','#ffbd2e','#27c93f'].map(c => <div key={c} style={{ width: '12px', height: '12px', borderRadius: '50%', background: c }} />)}
            </div>
            {filename && <span style={{ fontSize: '13px', color: isDark ? '#aaa' : '#555' }}>{filename}</span>}
            <span style={{ fontSize: '12px', color: isDark ? '#666' : '#888', padding: '2px 8px', background: isDark ? '#333' : '#d1d5db', borderRadius: '4px' }}>{language}</span>
          </div>
          <button onClick={handleCopy} style={{ fontSize: '12px', color: isDark ? '#aaa' : '#555', background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px 8px' }}>
            {copied ? '✓ Скопировано' : '⎘ Копировать'}
          </button>
        </div>
        <div style={{ background: isDark ? '#282c34' : '#f9fafb', padding: '16px', overflowX: 'auto' }}>
          {lines.map((line, i) => (
            <div key={i} style={{ display: 'flex', gap: '16px', minHeight: '22px' }}>
              {showLineNumbers && <span style={{ color: isDark ? '#636d83' : '#9ca3af', minWidth: '24px', textAlign: 'right', userSelect: 'none', fontSize: '13px', lineHeight: '22px' }}>{i + 1}</span>}
              <span style={{ color: isDark ? '#abb2bf' : '#374151', fontSize: '13px', lineHeight: '22px', whiteSpace: 'pre' }}>{line || ' '}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function MasonryGalleryRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const images = Array.isArray(d.images) ? d.images as Record<string, unknown>[] : []
  const columns = Number(d.columns ?? 3)
  const gap = Number(d.gap ?? 12)
  const { cols } = useDevice()
  return (
    <div style={{ padding: '40px 24px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ columns: cols(columns), columnGap: `${gap}px` }}>
        {images.map((img, i) => (
          <div key={i} style={{ breakInside: 'avoid', marginBottom: `${gap}px` }}>
            <img src={String(img.src ?? '')} alt={String(img.alt ?? '')} style={{ width: '100%', borderRadius: '8px', display: 'block' }} />
          </div>
        ))}
      </div>
    </div>
  )
}

function ComparisonRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const title = String(d.title ?? 'Почему выбирают нас')
  const leftTitle = String(d.leftTitle ?? 'Конкуренты')
  const rightTitle = String(d.rightTitle ?? 'Наш продукт')
  const rightHighlight = d.rightHighlight !== false
  const features = Array.isArray(d.features) ? d.features as Record<string, unknown>[] : []
  return (
    <div style={{ padding: '64px 24px', maxWidth: '800px', margin: '0 auto' }}>
      {title && <h2 style={{ fontSize: '36px', fontWeight: 700, color: '#111', textAlign: 'center', marginBottom: '40px' }}>{title}</h2>}
      <div style={{ borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 32px rgba(0,0,0,0.1)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', background: '#f8f9fa' }}>
          <div style={{ padding: '16px 24px', fontWeight: 700, color: '#111', fontSize: '14px' }}>Характеристика</div>
          <div style={{ padding: '16px 16px', fontWeight: 700, color: '#888', fontSize: '14px', textAlign: 'center' }}>{leftTitle}</div>
          <div style={{ padding: '16px 16px', fontWeight: 700, color: rightHighlight ? '#6366f1' : '#111', fontSize: '14px', textAlign: 'center', background: rightHighlight ? '#f0f0ff' : 'transparent' }}>{rightTitle}</div>
        </div>
        {features.map((f, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', borderTop: '1px solid #f0f0f0', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
            <div style={{ padding: '14px 24px', color: '#333', fontSize: '14px' }}>{String(f.name ?? '')}</div>
            <div style={{ padding: '14px 16px', color: '#888', fontSize: '14px', textAlign: 'center' }}>{String(f.left ?? '')}</div>
            <div style={{ padding: '14px 16px', color: rightHighlight ? '#6366f1' : '#333', fontSize: '14px', textAlign: 'center', fontWeight: 600, background: rightHighlight ? 'rgba(99,102,241,0.04)' : 'transparent' }}>{String(f.right ?? '')}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

function AwardsRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const title = String(d.title ?? 'Нас признают лучшими')
  const items = Array.isArray(d.items) ? d.items as Record<string, unknown>[] : []
  return (
    <div style={{ padding: '64px 24px', textAlign: 'center' }}>
      {title && <h2 style={{ fontSize: '32px', fontWeight: 700, color: '#111', marginBottom: '40px' }}>{title}</h2>}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '32px', justifyContent: 'center', maxWidth: '1000px', margin: '0 auto' }}>
        {items.map((item, i) => (
          <a key={i} href={String(item.link ?? '#')} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', textDecoration: 'none', padding: '24px 32px', background: '#fff', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', minWidth: '180px' }}>
            {!!item.image && <img src={String(item.image)} alt={String(item.name ?? '')} style={{ maxWidth: '120px', maxHeight: '40px', objectFit: 'contain', filter: 'grayscale(1)', opacity: 0.7 }} />}
            {!item.image && <span style={{ fontSize: '18px', fontWeight: 700, color: '#333' }}>{String(item.name ?? '')}</span>}
            <span style={{ fontSize: '13px', color: '#777' }}>{String(item.description ?? '')}</span>
          </a>
        ))}
      </div>
    </div>
  )
}

function GuaranteeBarRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const background = String(d.background ?? '#f8f9fa')
  const items = Array.isArray(d.items) ? d.items as Record<string, unknown>[] : []
  return (
    <div style={{ background, padding: '28px 24px' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', justifyContent: 'center', maxWidth: '1000px', margin: '0 auto' }}>
        {items.map((item, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '22px' }}>{String(item.icon ?? '')}</span>
            <span style={{ fontSize: '14px', fontWeight: 500, color: '#444' }}>{String(item.text ?? '')}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function HeroMinimalRenderer({ block, editing, onUpdate }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const label = String(d.label ?? '')
  const title = String(d.title ?? 'Создавайте\nбез границ')
  const subtitle = String(d.subtitle ?? '')
  const primaryButton = String(d.primaryButton ?? 'Попробовать')
  const secondaryButton = String(d.secondaryButton ?? '')
  const align = String(d.align ?? 'center') as 'center' | 'left'
  const background = String(d.background ?? '#ffffff')
  const titleColor = String(d.titleColor ?? (isColorDark(background) ? '#ffffff' : '#0f0f0f'))
  const subtitleColor = isColorDark(background) ? 'rgba(255,255,255,0.7)' : '#666'
  const accentColor = String(d.accentColor ?? '#5b6af0')
  const accentText = isColorDark(accentColor) ? '#fff' : '#000'
  const secondaryColor = isColorDark(background) ? 'rgba(255,255,255,0.85)' : '#333'
  const secondaryBorder = isColorDark(background) ? 'rgba(255,255,255,0.3)' : '#ddd'
  const { ref: titleRef, onBlur: titleBlur, onKeyDown: titleKey } = useInlineEdit(title, 'title', editing, onUpdate)
  return (
    <div style={{ background, padding: '100px 24px', textAlign: align }}>
      <div style={{ maxWidth: align === 'center' ? '760px' : '900px', margin: align === 'center' ? '0 auto' : '0' }}>
        {label && (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: accentColor, color: accentText, padding: '6px 14px', borderRadius: '100px', fontSize: '13px', fontWeight: 600, marginBottom: '32px' }}>
            <span>✦</span>{label}
          </div>
        )}
        {editing
          ? <div ref={titleRef as React.RefObject<HTMLDivElement>} contentEditable suppressContentEditableWarning onBlur={titleBlur} onKeyDown={titleKey} style={{ fontSize: '72px', fontWeight: 900, color: titleColor, lineHeight: 1.05, letterSpacing: '-0.03em', marginBottom: '24px', outline: 'none', whiteSpace: 'pre-wrap', cursor: 'text' }} />
          : <h1 style={{ fontSize: '72px', fontWeight: 900, color: titleColor, lineHeight: 1.05, letterSpacing: '-0.03em', marginBottom: '24px', whiteSpace: 'pre-wrap' }}>{title}</h1>
        }
        {subtitle && <p style={{ fontSize: '20px', color: subtitleColor, lineHeight: 1.6, marginBottom: '40px', maxWidth: '560px', margin: align === 'center' ? '0 auto 40px' : '0 0 40px' }}>{subtitle}</p>}
        <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', justifyContent: align === 'center' ? 'center' : 'flex-start' }}>
          {primaryButton && (
            <a href="#" style={{ display: 'inline-flex', alignItems: 'center', background: accentColor, color: accentText, padding: '14px 32px', borderRadius: '8px', fontWeight: 600, fontSize: '16px', textDecoration: 'none' }}>
              {primaryButton}
            </a>
          )}
          {secondaryButton && (
            <a href="#" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: secondaryColor, padding: '14px 24px', borderRadius: '8px', fontWeight: 600, fontSize: '16px', textDecoration: 'none', border: `1.5px solid ${secondaryBorder}` }}>
              {secondaryButton} →
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

function TestimonialsCarouselRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const title = String(d.title ?? '')
  const items = Array.isArray(d.items) ? d.items as Record<string, unknown>[] : []
  const [index, setIndex] = React.useState(0)
  const autoplay = d.autoplay !== false
  const delay = Number(d.autoplayDelay ?? 5000)
  React.useEffect(() => {
    if (!autoplay || items.length < 2) return
    const t = setInterval(() => setIndex(i => (i + 1) % items.length), delay)
    return () => clearInterval(t)
  }, [autoplay, delay, items.length])
  const item = items[index] as Record<string, unknown> | undefined
  if (!item) return <div style={{ padding: '40px', textAlign: 'center', color: '#aaa' }}>Добавьте отзывы</div>
  const rating = Number(item.rating ?? 5)
  return (
    <div style={{ padding: '64px 24px', background: '#fafafa', textAlign: 'center' }}>
      {title && <h2 style={{ fontSize: '36px', fontWeight: 700, color: '#111', marginBottom: '48px' }}>{title}</h2>}
      <div style={{ maxWidth: '680px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '4px', marginBottom: '24px' }}>
          {Array.from({ length: 5 }).map((_, i) => <span key={i} style={{ fontSize: '20px', color: i < rating ? '#f59e0b' : '#ddd' }}>★</span>)}
        </div>
        <p style={{ fontSize: '20px', color: '#333', lineHeight: 1.7, marginBottom: '32px', fontStyle: 'italic' }}>"{String(item.text ?? '')}"</p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '14px' }}>
          {!!item.avatar && <img src={String(item.avatar)} alt={String(item.name ?? '')} style={{ width: '52px', height: '52px', borderRadius: '50%', objectFit: 'cover' }} />}
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontWeight: 700, color: '#111' }}>{String(item.name ?? '')}</div>
            <div style={{ fontSize: '13px', color: '#888' }}>{String(item.role ?? '')}{item.company ? `, ${String(item.company)}` : ''}</div>
          </div>
        </div>
        {items.length > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '24px', marginTop: '36px' }}>
            <button onClick={() => setIndex(i => (i - 1 + items.length) % items.length)} style={{ width: '40px', height: '40px', borderRadius: '50%', border: '1.5px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>←</button>
            <div style={{ display: 'flex', gap: '8px' }}>
              {items.map((_, i) => <div key={i} onClick={() => setIndex(i)} style={{ width: '8px', height: '8px', borderRadius: '50%', background: i === index ? '#6366f1' : '#ddd', cursor: 'pointer', transition: 'background 0.2s' }} />)}
            </div>
            <button onClick={() => setIndex(i => (i + 1) % items.length)} style={{ width: '40px', height: '40px', borderRadius: '50%', border: '1.5px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>→</button>
          </div>
        )}
      </div>
    </div>
  )
}

function ImageCarouselRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const images = Array.isArray(d.images) ? d.images as Record<string, unknown>[] : []
  const showCaptions = d.showCaptions !== false
  const showDots = d.showDots !== false
  const showArrows = d.showArrows !== false
  const autoplay = d.autoplay === true
  const aspectRatio = String(d.aspectRatio ?? '16/9')
  const [index, setIndex] = React.useState(0)
  React.useEffect(() => {
    if (!autoplay || images.length < 2) return
    const t = setInterval(() => setIndex(i => (i + 1) % images.length), 4000)
    return () => clearInterval(t)
  }, [autoplay, images.length])
  if (!images.length) return <div style={{ padding: '40px', textAlign: 'center', color: '#aaa' }}>Добавьте изображения</div>
  const img = images[index] as Record<string, unknown>
  const paddingMap: Record<string, string> = { '16/9': '56.25%', '4/3': '75%', '1/1': '100%', '21/9': '42.85%', '3/2': '66.67%' }
  return (
    <div style={{ padding: '40px 24px', maxWidth: '1100px', margin: '0 auto' }}>
      <div style={{ position: 'relative', borderRadius: '12px', overflow: 'hidden' }}>
        <div style={{ paddingBottom: paddingMap[aspectRatio] ?? '56.25%', position: 'relative' }}>
          <img src={String(img.src ?? '')} alt={String(img.alt ?? '')} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', transition: 'opacity 0.3s' }} />
          {showArrows && images.length > 1 && <>
            <button onClick={() => setIndex(i => (i - 1 + images.length) % images.length)} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', width: '44px', height: '44px', borderRadius: '50%', background: 'rgba(0,0,0,0.5)', border: 'none', color: '#fff', fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>‹</button>
            <button onClick={() => setIndex(i => (i + 1) % images.length)} style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', width: '44px', height: '44px', borderRadius: '50%', background: 'rgba(0,0,0,0.5)', border: 'none', color: '#fff', fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>›</button>
          </>}
        </div>
        {showCaptions && !!img.caption && (
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '12px 20px', background: 'linear-gradient(transparent, rgba(0,0,0,0.6))' }}>
            <p style={{ color: '#fff', fontSize: '14px', margin: 0 }}>{String(img.caption)}</p>
          </div>
        )}
      </div>
      {showDots && images.length > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '16px' }}>
          {images.map((_, i) => <div key={i} onClick={() => setIndex(i)} style={{ width: '8px', height: '8px', borderRadius: '50%', background: i === index ? '#6366f1' : '#ddd', cursor: 'pointer' }} />)}
        </div>
      )}
    </div>
  )
}

function ProductShowcaseRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const image = String(d.image ?? 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=600&q=80')
  const imageAlt = String(d.imageAlt ?? '')
  const badge = String(d.badge ?? '')
  const name = String(d.name ?? 'Продукт')
  const description = String(d.description ?? '')
  const price = String(d.price ?? '')
  const oldPrice = String(d.oldPrice ?? '')
  const buttonText = String(d.buttonText ?? 'В корзину')
  const imagePosition = String(d.imagePosition ?? 'left')
  const features = Array.isArray(d.features) ? d.features as Record<string, unknown>[] : []
  const imgEl = (
    <div style={{ position: 'relative' }}>
      {badge && <div style={{ position: 'absolute', top: '16px', left: '16px', zIndex: 1, background: '#ef4444', color: '#fff', padding: '4px 12px', borderRadius: '100px', fontSize: '12px', fontWeight: 700 }}>{badge}</div>}
      <img src={image} alt={imageAlt} style={{ width: '100%', borderRadius: '16px', objectFit: 'cover', maxHeight: '480px' }} />
    </div>
  )
  const infoEl = (
    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '20px' }}>
      <h2 style={{ fontSize: '34px', fontWeight: 800, color: '#111', margin: 0 }}>{name}</h2>
      <p style={{ fontSize: '16px', color: '#555', lineHeight: 1.7, margin: 0 }}>{description}</p>
      {features.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {features.map((f, i) => <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', color: '#333' }}><span style={{ color: '#22c55e', fontWeight: 700 }}>✓</span>{String(f.value ?? '')}</li>)}
        </ul>
      )}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
        <span style={{ fontSize: '36px', fontWeight: 800, color: '#111' }}>{price}</span>
        {oldPrice && <span style={{ fontSize: '20px', color: '#aaa', textDecoration: 'line-through' }}>{oldPrice}</span>}
      </div>
      <button style={{ background: '#111', color: '#fff', padding: '16px 36px', borderRadius: '10px', fontWeight: 700, fontSize: '17px', border: 'none', cursor: 'pointer', width: 'fit-content' }}>{buttonText}</button>
    </div>
  )
  return (
    <div style={{ padding: '64px 24px', maxWidth: '1100px', margin: '0 auto' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '56px', alignItems: 'center' }}>
        {imagePosition === 'left' ? <>{imgEl}{infoEl}</> : <>{infoEl}{imgEl}</>}
      </div>
    </div>
  )
}

function ProductGridRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const title = String(d.title ?? '')
  const columns = Number(d.columns ?? 3)
  const products = Array.isArray(d.products) ? d.products as Record<string, unknown>[] : []
  const { cols } = useDevice()
  return (
    <div style={{ padding: '64px 24px' }}>
      {title && <h2 style={{ fontSize: '36px', fontWeight: 700, color: '#111', textAlign: 'center', marginBottom: '40px' }}>{title}</h2>}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols(columns)}, 1fr)`, gap: '24px', maxWidth: '1200px', margin: '0 auto' }}>
        {products.map((p, i) => (
          <div key={i} style={{ background: '#fff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
            <div style={{ position: 'relative' }}>
              {!!p.badge && <div style={{ position: 'absolute', top: '12px', left: '12px', background: '#ef4444', color: '#fff', padding: '3px 10px', borderRadius: '100px', fontSize: '11px', fontWeight: 700 }}>{String(p.badge)}</div>}
              {!!p.image && <img src={String(p.image)} alt={String(p.name ?? '')} style={{ width: '100%', height: '220px', objectFit: 'cover' }} />}
            </div>
            <div style={{ padding: '16px 20px 20px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#111', margin: '0 0 8px' }}>{String(p.name ?? '')}</h3>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '14px' }}>
                <span style={{ fontSize: '20px', fontWeight: 800, color: '#111' }}>{String(p.price ?? '')}</span>
                {!!p.oldPrice && <span style={{ fontSize: '14px', color: '#aaa', textDecoration: 'line-through' }}>{String(p.oldPrice)}</span>}
              </div>
              <button style={{ width: '100%', background: '#111', color: '#fff', padding: '10px', borderRadius: '8px', fontWeight: 600, fontSize: '14px', border: 'none', cursor: 'pointer' }}>{String(p.buttonText ?? 'В корзину')}</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function PaymentLogosRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const title = String(d.title ?? 'Принимаем к оплате')
  const background = String(d.background ?? '#f8f9fa')
  const methods = [
    { name: 'Visa', color: '#1A1F71', text: 'VISA' },
    { name: 'Mastercard', color: '#EB001B', text: '⬤ MC' },
    { name: 'МИР', color: '#00853e', text: 'МИР' },
    { name: 'SBP', color: '#5b4fcf', text: 'СБП' },
    { name: 'PayPal', color: '#003087', text: 'PayPal' },
    { name: 'Apple Pay', color: '#000', text: '🍎 Pay' },
    { name: 'Google Pay', color: '#4285f4', text: 'G Pay' },
    { name: 'SberPay', color: '#21A038', text: 'Сбер' },
  ]
  return (
    <div style={{ background, padding: '32px 24px' }}>
      {title && <p style={{ textAlign: 'center', fontSize: '13px', color: '#888', fontWeight: 500, marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{title}</p>}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'center' }}>
        {methods.map(m => (
          <div key={m.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px 18px', background: '#fff', borderRadius: '8px', boxShadow: '0 1px 4px rgba(0,0,0,0.1)', minWidth: '80px' }}>
            <span style={{ fontSize: '14px', fontWeight: 800, color: m.color, letterSpacing: '-0.01em' }}>{m.text}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

type QuizOption = string | { id?: string; label?: string; image?: string; nextStepId?: string | null }

function resolveOptionLabel(opt: QuizOption): string {
  if (typeof opt === 'string') return opt
  return opt.label ?? ''
}

function QuizRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const title = String(d.title ?? 'Квиз')
  const subtitle = String(d.subtitle ?? '')
  const background = String(d.background ?? '#f0f4ff')
  const accent = String(d.accentColor ?? '#5b6af0')
  const steps = (d.steps as Array<{ id: string; question: string; options: QuizOption[] }> | undefined) ?? []
  const resultTitle = String(d.resultTitle ?? 'Оставьте контакты')
  const totalSteps = steps.length + 1

  // optionsText overrides step[0].options when present (editor fallback, FG will replace)
  const optionsText = d.optionsText as string | undefined
  const displayOptions: QuizOption[] = optionsText
    ? optionsText.split('\n').filter(Boolean)
    : (steps[0]?.options ?? [])

  return (
    <div style={{ background, padding: '64px 24px' }}>
      <div style={{ maxWidth: '600px', margin: '0 auto', background: '#fff', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.1)' }}>
        {/* Progress bar */}
        <div style={{ height: '4px', background: '#f0f0f0' }}>
          <div style={{ height: '100%', width: `${100 / totalSteps}%`, background: accent, borderRadius: '2px', transition: 'width 0.3s' }} />
        </div>
        <div style={{ padding: '32px' }}>
          <div style={{ textAlign: 'center', marginBottom: '28px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: 700, color: '#111', margin: '0 0 8px' }}>{title}</h2>
            {subtitle && <p style={{ fontSize: '15px', color: '#666', margin: 0 }}>{subtitle}</p>}
          </div>
          {steps[0] && (
            <>
              <p style={{ fontSize: '16px', fontWeight: 600, color: '#111', marginBottom: '16px' }}>{steps[0].question}</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {displayOptions.map((opt, i) => (
                  <button key={i} style={{ padding: '12px 16px', background: '#f8f9fa', border: '2px solid #f0f0f0', borderRadius: '8px', fontSize: '14px', color: '#111', textAlign: 'left', cursor: 'pointer', fontWeight: 500 }}>
                    {resolveOptionLabel(opt)}
                  </button>
                ))}
              </div>
            </>
          )}
          <div style={{ marginTop: '20px', fontSize: '11px', color: '#aaa', textAlign: 'center' }}>
            Шаг 1 из {totalSteps} · Последний шаг: {resultTitle}
          </div>
        </div>
      </div>
    </div>
  )
}

function CalculatorRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const title = String(d.title ?? 'Калькулятор стоимости')
  const subtitle = String(d.subtitle ?? '')
  const background = String(d.background ?? '#fff')
  const accent = String(d.accentColor ?? '#5b6af0')
  const currency = String(d.currency ?? '₽')
  const basePrice = Number(d.basePrice ?? 0)
  const resultLabel = String(d.resultLabel ?? 'Ориентировочная стоимость')
  const ctaText = String(d.ctaText ?? 'Получить расчёт')
  const params = (d.params as Array<{ id: string; label: string; type: string; min?: number; max?: number; step?: number; default?: number | string; options?: string[] }> | undefined) ?? []

  return (
    <div style={{ background, padding: '64px 24px' }}>
      <div style={{ maxWidth: '680px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <h2 style={{ fontSize: '32px', fontWeight: 700, color: '#111', margin: '0 0 10px' }}>{title}</h2>
          {subtitle && <p style={{ fontSize: '16px', color: '#666', margin: 0 }}>{subtitle}</p>}
        </div>
        <div style={{ background: '#f8f9fa', borderRadius: '16px', padding: '32px', display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
          <div style={{ flex: '2 1 300px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {params.map((p, i) => (
              <div key={i}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#555', marginBottom: '8px' }}>{p.label}</label>
                {p.type === 'range' ? (
                  <div>
                    <input type="range" min={p.min} max={p.max} step={p.step} defaultValue={String(p.default ?? p.min)} style={{ width: '100%', accentColor: accent }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#aaa', marginTop: '2px' }}>
                      <span>{p.min}</span><span>{p.max}</span>
                    </div>
                  </div>
                ) : p.type === 'select' ? (
                  <select defaultValue={String(p.default ?? 0)} style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', background: '#fff' }}>
                    {(p.options ?? []).map((opt, j) => <option key={j} value={j}>{opt}</option>)}
                  </select>
                ) : (
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {(p.options ?? []).map((opt, j) => (
                      <button key={j} style={{ padding: '7px 14px', borderRadius: '8px', border: `2px solid ${j === (p.default ?? 0) ? accent : '#ddd'}`, background: j === (p.default ?? 0) ? accent : '#fff', color: j === (p.default ?? 0) ? '#fff' : '#333', fontSize: '13px', cursor: 'pointer', fontWeight: 500 }}>
                        {opt}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div style={{ flex: '1 1 180px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#fff', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize: '12px', color: '#888', marginBottom: '8px', textAlign: 'center' }}>{resultLabel}</div>
            <div style={{ fontSize: '36px', fontWeight: 800, color: accent, lineHeight: 1 }}>
              {basePrice.toLocaleString('ru')}{' '}{currency}
            </div>
            <a href={String(d.ctaUrl ?? '#')} style={{ display: 'block', marginTop: '20px', padding: '10px 18px', background: accent, color: '#fff', borderRadius: '8px', fontSize: '13px', fontWeight: 700, textDecoration: 'none', textAlign: 'center' }}>
              {ctaText}
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Registry ──────────────────────────────────────────────────────────────────
type Renderer = React.FC<RendererProps>

function AnnouncementBarRenderer({ block }: RendererProps) {
  const d = block.data
  const bg = String(d.background ?? '#5b6af0')
  const color = String(d.textColor ?? '#ffffff')
  return (
    <div style={{ background: bg, color, padding: '10px 24px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', fontSize: '14px', fontWeight: 500 }}>
      <span>{String(d.text ?? '')}</span>
      {Boolean(d.link) && Boolean(d.linkText) && (
        <a href={String(d.link)} style={{ color, fontWeight: 700, textDecoration: 'underline', fontSize: '13px' }}>{String(d.linkText)}</a>
      )}
      {Boolean(d.dismissible) && (
        <span style={{ marginLeft: '8px', opacity: 0.6, cursor: 'pointer', fontWeight: 700 }}>×</span>
      )}
    </div>
  )
}

function PopupRenderer({ block }: RendererProps) {
  const d = block.data
  const accent = String(d.accentColor ?? '#5b6af0')
  const trigger = String(d.trigger ?? 'delay')
  return (
    <div style={{ border: '2px dashed #c7d2fe', borderRadius: '12px', padding: '20px', background: '#f8faff' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
        <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', flexShrink: 0 }}>🪟</div>
        <div>
          <div style={{ fontSize: '13px', fontWeight: 700, color: '#333' }}>Попап: {String(d.title ?? '')}</div>
          <div style={{ fontSize: '12px', color: '#888' }}>
            Триггер: {trigger === 'delay' ? `через ${d.delaySeconds ?? 5} сек` : 'при попытке уйти'}
            {d.showOnce ? ' · один раз за сессию' : ''}
          </div>
        </div>
      </div>
      <div style={{ fontSize: '12px', color: '#666', lineHeight: 1.5 }}>{String(d.text ?? '')}</div>
      {Boolean(d.ctaText) && (
        <div style={{ marginTop: '10px' }}>
          <span style={{ display: 'inline-block', padding: '6px 14px', background: accent, color: '#fff', borderRadius: '6px', fontSize: '12px', fontWeight: 700 }}>{String(d.ctaText)}</span>
        </div>
      )}
    </div>
  )
}

function ChatWidgetRenderer({ block }: RendererProps) {
  const d = block.data
  const color = String(d.primaryColor ?? '#25D366')
  const position = String(d.position ?? 'bottom-right')
  const isRight = position === 'bottom-right'

  const channels: Array<{ href: string; label: string; icon: string; color: string }> = []
  if (d.whatsapp) channels.push({ href: `https://wa.me/${String(d.whatsapp).replace(/[^0-9]/g, '')}`, label: 'WhatsApp', icon: '📱', color: '#25D366' })
  if (d.telegram) channels.push({ href: `https://t.me/${String(d.telegram).replace('@', '')}`, label: 'Telegram', icon: '✈', color: '#0088cc' })
  if (d.vk) channels.push({ href: String(d.vk).startsWith('http') ? String(d.vk) : `https://vk.com/${d.vk}`, label: 'ВКонтакте', icon: 'VK', color: '#0077ff' })
  if (d.phone) channels.push({ href: `tel:${d.phone}`, label: 'Позвонить', icon: '📞', color: '#555' })

  return (
    <div style={{ background: '#f8faff', border: '1px dashed #c7d2fe', borderRadius: '12px', padding: '20px 24px', display: 'flex', alignItems: 'center', gap: '16px' }}>
      <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', flexShrink: 0 }}>💬</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '13px', fontWeight: 700, color: '#333', marginBottom: '4px' }}>Виджет связи ({isRight ? 'справа' : 'слева'})</div>
        <div style={{ fontSize: '12px', color: '#888', lineHeight: 1.6 }}>
          {channels.length === 0 ? 'Добавьте контакты в настройках блока' : channels.map(c => c.label).join(' · ')}
        </div>
      </div>
      {channels.length > 0 && (
        <div style={{ display: 'flex', gap: '6px' }}>
          {channels.map(c => (
            <div key={c.href} style={{ width: '36px', height: '36px', borderRadius: '50%', background: c.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', color: '#fff', fontWeight: 700, flexShrink: 0 }}>
              {c.icon === 'VK' ? <span style={{ fontSize: '11px' }}>VK</span> : c.icon}
            </div>
          ))}
        </div>
      )}
      <div style={{ fontSize: '11px', color: '#aaa', fontStyle: 'italic', flexShrink: 0 }}>Плавающий</div>
    </div>
  )
}

// ── Swiss Style Block Renderer ────────────────────────────────────────────────

function useSwissColors(data: Record<string, unknown>) {
  const variant = String(data.variant ?? 'bst')
  const palette: Record<string, { bg: string; accent: string; ink: string }> = {
    bst:    { bg: '#F7F6F2', accent: '#E4030E', ink: '#0E0E0E' },
    nord:   { bg: '#F1E9DC', accent: '#C1501F', ink: '#221610' },
    klima:  { bg: '#F2F0E9', accent: '#FF4D00', ink: '#10201A' },
    orbita: { bg: '#0C0D10', accent: '#3B5BFF', ink: '#ECEEF2' },
  }
  return palette[variant] ?? palette.bst
}

const SwissNavbarRenderer: React.FC<{ data: Record<string, unknown> }> = ({ data }) => {
  const c = useSwissColors(data)
  const brand = String(data.brand || 'BST')
  return (
    <div style={{ background: c.bg, color: c.ink, borderBottom: `3px solid ${c.accent}`, padding: '0 20px', height: '52px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontFamily: 'Archivo,sans-serif' }}>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <span style={{ width: '22px', height: '22px', background: c.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: 900, color: '#fff' }}>{brand[0]}</span>
        <strong style={{ fontWeight: 900, fontSize: '12px', letterSpacing: '.04em' }}>{brand}</strong>
      </div>
      <div style={{ display: 'flex', gap: '14px', fontFamily: 'monospace', fontSize: '9px', letterSpacing: '.1em', textTransform: 'uppercase', opacity: .75 }}>
        {['01 Манифест', '03 Услуги', '04 Работы', '06 Контакт'].map(l => <span key={l}>{l}</span>)}
      </div>
      <span style={{ fontFamily: 'monospace', fontSize: '9px', opacity: .7 }}>ZH --:--:--</span>
    </div>
  )
}

const SwissHeroRenderer: React.FC<{ data: Record<string, unknown> }> = ({ data }) => {
  const c = useSwissColors(data)
  return (
    <div style={{ background: c.bg, color: c.ink, padding: '24px 20px 16px', fontFamily: 'Archivo,sans-serif' }}>
      <div style={{ fontSize: '8px', color: c.accent, letterSpacing: '.16em', textTransform: 'uppercase', marginBottom: '6px', fontFamily: 'monospace' }}>Бюро визуальных систем · Цюрих</div>
      <div style={{ fontWeight: 800, fontSize: '32px', lineHeight: .9, textTransform: 'uppercase', letterSpacing: '-.02em' }}>
        ФОРМА<br/>СЛЕДУЕТ<span style={{ color: c.accent }}>.</span>
      </div>
      <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
        <span style={{ padding: '5px 12px', border: `1px solid ${c.ink}`, fontSize: '9px', fontWeight: 700, letterSpacing: '.08em' }}>РАБОТЫ →</span>
        <span style={{ padding: '5px 12px', background: c.accent, color: '#fff', fontSize: '9px', fontWeight: 700, letterSpacing: '.08em' }}>ПРОЕКТ →</span>
      </div>
    </div>
  )
}

const SwissMarqueeRenderer: React.FC<{ data: Record<string, unknown> }> = ({ data }) => {
  const c = useSwissColors(data)
  return (
    <div style={{ background: c.accent, color: '#fff', padding: '10px 20px', overflow: 'hidden', whiteSpace: 'nowrap', fontFamily: 'Archivo,sans-serif' }}>
      <span style={{ fontWeight: 800, fontSize: '13px', letterSpacing: '.06em', textTransform: 'uppercase' }}>
        СЕТКА · ТИПОГРАФИКА · СИСТЕМА ● BST — ZÜRICH ● СЕТКА · ТИПОГРАФИКА · СИСТЕМА ●
      </span>
    </div>
  )
}

const SwissManifestRenderer: React.FC<{ data: Record<string, unknown> }> = ({ data }) => {
  const c = useSwissColors(data)
  return (
    <div style={{ background: c.bg, color: c.ink, padding: '20px', borderTop: `1px solid rgba(0,0,0,0.12)`, fontFamily: 'Archivo,sans-serif' }}>
      <div style={{ fontSize: '8px', letterSpacing: '.16em', textTransform: 'uppercase', opacity: .6, marginBottom: '12px', fontFamily: 'monospace' }}>01 — Манифест</div>
      <div style={{ display: 'flex', gap: '16px' }}>
        <div style={{ fontSize: '28px', fontWeight: 900, color: c.accent, lineHeight: 1 }}>1957</div>
        <div style={{ fontSize: '12px', lineHeight: 1.4, opacity: .85 }}>
          Бюро основано в Цюрихе. Дизайн — инструмент коммуникации, а не украшение.
        </div>
      </div>
    </div>
  )
}

const SwissStatsRenderer: React.FC<{ data: Record<string, unknown> }> = ({ data }) => {
  const c = useSwissColors(data)
  const stats = [['67', 'лет практики'], ['214', 'проектов'], ['16', 'наград'], ['4', 'города']]
  return (
    <div style={{ background: c.bg, color: c.ink, borderTop: `1px solid rgba(0,0,0,0.12)`, display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', fontFamily: 'Archivo,sans-serif' }}>
      {stats.map(([n, l]) => (
        <div key={l} style={{ padding: '12px', borderLeft: `1px solid rgba(0,0,0,0.12)` }}>
          <div style={{ fontWeight: 800, fontSize: '22px', letterSpacing: '-.02em', lineHeight: 1 }}>{n}</div>
          <div style={{ fontFamily: 'monospace', fontSize: '8px', letterSpacing: '.1em', textTransform: 'uppercase', opacity: .6, marginTop: '4px' }}>{l}</div>
        </div>
      ))}
    </div>
  )
}

const SwissServicesRenderer: React.FC<{ data: Record<string, unknown> }> = ({ data }) => {
  const c = useSwissColors(data)
  const svcs = ['Айдентика', 'Типографика', 'Диджитал', 'Арт-дирекшн']
  return (
    <div style={{ background: c.bg, color: c.ink, borderTop: `1px solid rgba(0,0,0,0.12)`, fontFamily: 'Archivo,sans-serif' }}>
      {svcs.map((s, i) => (
        <div key={s} style={{ display: 'flex', gap: '12px', alignItems: 'center', padding: '9px 16px', borderTop: i === 0 ? `1px solid rgba(0,0,0,0.12)` : undefined }}>
          <span style={{ fontFamily: 'monospace', fontSize: '9px', color: c.accent }}>0{i + 1}</span>
          <span style={{ fontWeight: 800, fontSize: '13px', textTransform: 'uppercase', letterSpacing: '-.01em' }}>{s}</span>
          <span style={{ marginLeft: 'auto', fontSize: '12px', opacity: .5 }}>→</span>
        </div>
      ))}
    </div>
  )
}

const SwissWorksRenderer: React.FC<{ data: Record<string, unknown> }> = ({ data }) => {
  const c = useSwissColors(data)
  return (
    <div style={{ background: c.bg, color: c.ink, borderTop: `1px solid rgba(0,0,0,0.12)`, fontFamily: 'Archivo,sans-serif' }}>
      <div style={{ fontSize: '8px', letterSpacing: '.16em', textTransform: 'uppercase', opacity: .6, padding: '10px 16px', fontFamily: 'monospace' }}>04 — Работы</div>
      <div style={{ display: 'grid', gridTemplateColumns: '7fr 5fr', gap: '1px', background: `rgba(0,0,0,0.12)` }}>
        <div style={{ background: c.bg, padding: '10px', fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em' }}>Архив плаката · 2024</div>
        <div style={{ background: c.accent, color: '#fff', padding: '10px', fontSize: '14px', fontWeight: 900, textTransform: 'uppercase' }}>Raster</div>
      </div>
    </div>
  )
}

const SwissQuoteRenderer: React.FC<{ data: Record<string, unknown> }> = ({ data }) => {
  const c = useSwissColors(data)
  return (
    <div style={{ background: c.bg, color: c.ink, padding: '20px', borderTop: `1px solid rgba(0,0,0,0.12)`, fontFamily: 'Archivo,sans-serif' }}>
      <div style={{ borderLeft: `6px solid ${c.accent}`, paddingLeft: '14px' }}>
        <div style={{ fontSize: '13px', fontWeight: 600, lineHeight: 1.3 }}>«Мы убираем всё, что мешает сообщению. То, что остаётся, и есть бренд.»</div>
        <div style={{ fontFamily: 'monospace', fontSize: '8px', letterSpacing: '.12em', textTransform: 'uppercase', opacity: .6, marginTop: '8px' }}>Бруно Хасслер — арт-директор</div>
      </div>
    </div>
  )
}

const SwissContactRenderer: React.FC<{ data: Record<string, unknown> }> = ({ data }) => {
  const c = useSwissColors(data)
  return (
    <div style={{ background: c.bg, color: c.ink, padding: '20px', borderTop: `1px solid rgba(0,0,0,0.12)`, fontFamily: 'Archivo,sans-serif' }}>
      <div style={{ fontWeight: 900, fontSize: '36px', textTransform: 'uppercase', letterSpacing: '-.03em', lineHeight: .9 }}>
        Начнём<br/>проект<span style={{ color: c.accent }}>.</span>
      </div>
      <div style={{ marginTop: '12px', fontSize: '10px', fontFamily: 'monospace', borderBottom: `2px solid ${c.ink}`, display: 'inline-block', paddingBottom: '2px' }}>hello@bst.ch</div>
    </div>
  )
}

const SwissFooterRenderer: React.FC<{ data: Record<string, unknown> }> = ({ data }) => {
  const c = useSwissColors(data)
  const brand = String(data.brand || 'BST')
  return (
    <div style={{ background: c.bg, color: c.ink, borderTop: `1px solid rgba(0,0,0,0.12)`, padding: '16px 20px 0', fontFamily: 'Archivo,sans-serif' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '12px', fontSize: '9px', opacity: .7, fontFamily: 'monospace', letterSpacing: '.05em', marginBottom: '8px' }}>
        <div><strong style={{ color: c.accent, display: 'block', marginBottom: '4px' }}>Адрес</strong>Limmatstr. 21<br/>Zürich</div>
        <div><strong style={{ color: c.accent, display: 'block', marginBottom: '4px' }}>Связь</strong>hello@bst.ch<br/>+41 44 000</div>
        <div><strong style={{ color: c.accent, display: 'block', marginBottom: '4px' }}>Соцсети</strong>Instagram ↗<br/>Behance ↗</div>
        <div><strong style={{ color: c.accent, display: 'block', marginBottom: '4px' }}>Колофон</strong>Archivo / Plex<br/>Swiss Style</div>
      </div>
      <div style={{ fontWeight: 900, fontSize: '36px', textTransform: 'uppercase', letterSpacing: '-.04em', lineHeight: .82, marginBottom: '-4px' }}>
        {brand}<span style={{ color: c.accent }}>.</span>
      </div>
    </div>
  )
}

function SwissGhostElement({ type, scale }: { type: string; scale: number }) {
  const s = Math.max(scale, 0.3)
  if (type === 'button-outline') return (
    <div style={{ border: `${Math.round(1)}px solid #0E0E0E`, padding: `${Math.round(13*s)}px ${Math.round(24*s)}px`, fontSize: `${Math.round(11*s)}px`, fontFamily: 'IBM Plex Mono, monospace', letterSpacing: '0.1em', textTransform: 'uppercase', whiteSpace: 'nowrap', background: '#F7F6F2', color: '#0E0E0E' }}>
      КНОПКА
    </div>
  )
  if (type === 'button-solid') return (
    <div style={{ background: '#E4030E', color: '#fff', padding: `${Math.round(13*s)}px ${Math.round(24*s)}px`, fontSize: `${Math.round(11*s)}px`, fontFamily: 'IBM Plex Mono, monospace', letterSpacing: '0.1em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
      КНОПКА
    </div>
  )
  if (type === 'heading') return (
    <div style={{ fontSize: `${Math.round(48*s)}px`, fontWeight: 800, fontFamily: 'Archivo, sans-serif', textTransform: 'uppercase', letterSpacing: '-0.02em', lineHeight: 1, whiteSpace: 'nowrap', color: '#0E0E0E' }}>
      Заголовок
    </div>
  )
  return (
    <div style={{ fontSize: `${Math.round(16*s)}px`, fontFamily: 'Archivo, sans-serif', whiteSpace: 'nowrap', color: '#0E0E0E' }}>
      Текст для вставки
    </div>
  )
}

// ── Scroll Snap ───────────────────────────────────────────────────────────────
function ScrollSnapRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const slides = Array.isArray(d.slides) ? d.slides as Record<string, unknown>[] : []
  const textColor = String(d.textColor ?? '#ffffff')
  const height = Number(d.height ?? 100)
  const unit = String(d.heightUnit ?? 'vh')
  return (
    <div style={{ position: 'relative', overflow: 'hidden' }}>
      {(slides.length > 0 ? slides : [{ background: '#0f0f0f', title: 'Первый экран', subtitle: '' }]).map((slide, i) => (
        <div key={i} style={{ minHeight: `${height}${unit}`, background: String(slide.background ?? '#0f0f0f'), backgroundImage: slide.image ? `url(${slide.image})` : undefined, backgroundSize: 'cover', backgroundPosition: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 40px', textAlign: 'center', color: textColor }}>
          {!!slide.title && <h2 style={{ fontSize: '48px', fontWeight: 800, lineHeight: 1.15, marginBottom: '16px', margin: 0 }}>{String(slide.title)}</h2>}
          {!!slide.subtitle && <p style={{ fontSize: '20px', opacity: 0.8, margin: '16px 0 24px' }}>{String(slide.subtitle)}</p>}
          {!!slide.buttonText && <button style={{ padding: '12px 28px', background: 'rgba(255,255,255,0.2)', color: textColor, border: '1px solid rgba(255,255,255,0.4)', borderRadius: '8px', fontSize: '15px', fontWeight: 600, cursor: 'default' }}>{String(slide.buttonText)}</button>}
        </div>
      ))}
      <div style={{ position: 'absolute', right: '20px', top: '50%', transform: 'translateY(-50%)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {slides.map((_, i) => <div key={i} style={{ width: '8px', height: '8px', borderRadius: '50%', background: i === 0 ? textColor : 'rgba(255,255,255,0.3)' }} />)}
      </div>
    </div>
  )
}

// ── Parallax ──────────────────────────────────────────────────────────────────
function ParallaxRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const image = String(d.image ?? '')
  const height = Number(d.height ?? 500)
  const overlay = String(d.overlay ?? 'rgba(0,0,0,0.45)')
  const title = String(d.title ?? '')
  const subtitle = String(d.subtitle ?? '')
  const textColor = String(d.textColor ?? '#ffffff')
  const align = String(d.align ?? 'center') as 'center' | 'left'
  return (
    <div style={{ position: 'relative', minHeight: `${height}px`, backgroundImage: image ? `url(${image})` : undefined, backgroundColor: image ? undefined : '#444', backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed', display: 'flex', alignItems: 'center', justifyContent: align === 'center' ? 'center' : 'flex-start' }}>
      <div style={{ position: 'absolute', inset: 0, background: overlay }} />
      <div style={{ position: 'relative', zIndex: 1, padding: '48px 60px', textAlign: align, maxWidth: '760px', marginLeft: align === 'center' ? 'auto' : undefined, marginRight: align === 'center' ? 'auto' : undefined }}>
        {title && <h2 style={{ fontSize: '48px', fontWeight: 800, color: textColor, lineHeight: 1.15, margin: 0 }}>{title}</h2>}
        {subtitle && <p style={{ fontSize: '20px', color: textColor, opacity: 0.85, margin: '16px 0 0', lineHeight: 1.6 }}>{subtitle}</p>}
      </div>
    </div>
  )
}

// ── Typewriter ─────────────────────────────────────────────────────────────────
function TypewriterRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const prefix = String(d.prefix ?? 'Делаем всё')
  const words = Array.isArray(d.words) ? d.words as string[] : ['Профессионально', 'Быстро', 'Красиво']
  const subtitle = String(d.subtitle ?? '')
  const buttonText = String(d.buttonText ?? '')
  const background = String(d.background ?? '#0f0f0f')
  const textColor = String(d.textColor ?? '#ffffff')
  const accentColor = String(d.accentColor ?? '#5b6af0')
  const fontSize = Number(d.fontSize ?? 64)
  const align = String(d.align ?? 'center') as 'center' | 'left'
  const [wordIdx, setWordIdx] = useState(0)
  useEffect(() => { const t = setInterval(() => setWordIdx(i => (i + 1) % words.length), 1800); return () => clearInterval(t) }, [words.length])
  return (
    <div style={{ background, padding: '80px 40px', minHeight: '480px', display: 'flex', flexDirection: 'column', alignItems: align === 'center' ? 'center' : 'flex-start', justifyContent: 'center', textAlign: align }}>
      <h1 style={{ fontSize: `${fontSize}px`, fontWeight: 900, color: textColor, lineHeight: 1.1, margin: 0 }}>
        {prefix}{' '}<span style={{ color: accentColor }}>{words[wordIdx] ?? ''}</span><span style={{ color: accentColor, animation: 'blink 0.7s step-end infinite' }}>|</span>
      </h1>
      {subtitle && <p style={{ fontSize: '20px', color: textColor, opacity: 0.7, margin: '20px 0 0', maxWidth: '600px' }}>{subtitle}</p>}
      {buttonText && <button style={{ marginTop: '32px', padding: '14px 32px', background: accentColor, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 600, cursor: 'default' }}>{buttonText}</button>}
    </div>
  )
}

// ── Gradient Hero ─────────────────────────────────────────────────────────────
function GradientHeroRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const title = String(d.title ?? 'Будущее начинается здесь')
  const subtitle = String(d.subtitle ?? '')
  const buttonText = String(d.buttonText ?? '')
  const button2Text = String(d.button2Text ?? '')
  const color1 = String(d.color1 ?? '#5b6af0')
  const color2 = String(d.color2 ?? '#ec4899')
  const color3 = String(d.color3 ?? '#f59e0b')
  const textColor = String(d.textColor ?? '#ffffff')
  const height = Number(d.height ?? 540)
  return (
    <div style={{ minHeight: `${height}px`, background: `linear-gradient(135deg, ${color1}, ${color2}, ${color3})`, backgroundSize: '200% 200%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 40px', textAlign: 'center', color: textColor }}>
      <h1 style={{ fontSize: '52px', fontWeight: 900, lineHeight: 1.1, margin: 0, maxWidth: '800px' }}>{title}</h1>
      {subtitle && <p style={{ fontSize: '20px', opacity: 0.9, margin: '20px 0 0', maxWidth: '600px' }}>{subtitle}</p>}
      {(buttonText || button2Text) && (
        <div style={{ display: 'flex', gap: '14px', marginTop: '36px', flexWrap: 'wrap', justifyContent: 'center' }}>
          {buttonText && <button style={{ padding: '14px 32px', background: '#fff', color: color1, border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 700, cursor: 'default' }}>{buttonText}</button>}
          {button2Text && <button style={{ padding: '14px 32px', background: 'transparent', color: textColor, border: '1.5px solid rgba(255,255,255,0.6)', borderRadius: '8px', fontSize: '16px', fontWeight: 600, cursor: 'default' }}>{button2Text}</button>}
        </div>
      )}
    </div>
  )
}

// ── Side-by-side CTA ──────────────────────────────────────────────────────────
function SideBySideCtaRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const left = (d.left ?? {}) as Record<string, unknown>
  const right = (d.right ?? {}) as Record<string, unknown>
  const minHeight = Number(d.minHeight ?? 500)
  const half = (side: Record<string, unknown>) => (
    <div style={{ flex: 1, minHeight: `${minHeight}px`, background: String(side.background ?? '#0f0f23'), display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '60px 48px' }}>
      {!!side.eyebrow && <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: String(side.buttonBg ?? '#5b6af0'), marginBottom: '16px' }}>{String(side.eyebrow)}</div>}
      <h2 style={{ fontSize: '36px', fontWeight: 800, color: String(side.textColor ?? '#fff'), lineHeight: 1.2, margin: 0 }}>{String(side.title ?? '')}</h2>
      {!!side.text && <p style={{ fontSize: '16px', color: String(side.textColor ?? '#fff'), opacity: 0.7, margin: '16px 0 0', lineHeight: 1.6 }}>{String(side.text)}</p>}
      {!!side.buttonText && <button style={{ marginTop: '28px', padding: '13px 28px', background: String(side.buttonBg ?? '#5b6af0'), color: '#fff', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 700, cursor: 'default', alignSelf: 'flex-start' }}>{String(side.buttonText)}</button>}
    </div>
  )
  return <div style={{ display: 'flex' }}>{half(left)}{half(right)}</div>
}

// ── Hero Gradient ─────────────────────────────────────────────────────────────
function HeroGradientRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const title = String(d.title ?? 'Создавайте. Вдохновляйте. Побеждайте.')
  const subtitle = String(d.subtitle ?? '')
  const buttonText = String(d.buttonText ?? '')
  const buttonText2 = String(d.buttonText2 ?? '')
  const gradientFrom = String(d.gradientFrom ?? '#667eea')
  const gradientTo = String(d.gradientTo ?? '#764ba2')
  const textColor = String(d.textColor ?? '#ffffff')
  const minHeight = Number(d.minHeight ?? 600)
  return (
    <div style={{ minHeight: `${minHeight}px`, background: `linear-gradient(135deg, ${gradientFrom}, ${gradientTo})`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 40px', textAlign: 'center', color: textColor }}>
      <h1 style={{ fontSize: '54px', fontWeight: 900, lineHeight: 1.1, margin: 0, maxWidth: '820px' }}>{title}</h1>
      {subtitle && <p style={{ fontSize: '20px', opacity: 0.85, margin: '20px 0 0', maxWidth: '600px' }}>{subtitle}</p>}
      {(buttonText || buttonText2) && (
        <div style={{ display: 'flex', gap: '14px', marginTop: '36px', flexWrap: 'wrap', justifyContent: 'center' }}>
          {buttonText && <button style={{ padding: '14px 32px', background: '#fff', color: gradientFrom, border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 700, cursor: 'default' }}>{buttonText}</button>}
          {buttonText2 && <button style={{ padding: '14px 32px', background: 'transparent', color: textColor, border: '1.5px solid rgba(255,255,255,0.5)', borderRadius: '8px', fontSize: '16px', fontWeight: 600, cursor: 'default' }}>{buttonText2}</button>}
        </div>
      )}
    </div>
  )
}

// ── Gradient Blob Hero ────────────────────────────────────────────────────────
function GradientBlobHeroRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const title = String(d.title ?? 'Создавайте сайты нового поколения')
  const subtitle = String(d.subtitle ?? '')
  const primaryButtonText = String(d.primaryButtonText ?? 'Начать бесплатно')
  const secondaryButtonText = String(d.secondaryButtonText ?? '')
  const badge = String(d.badge ?? '')
  const color1 = String(d.color1 ?? '#818cf8')
  const color2 = String(d.color2 ?? '#34d399')
  const color3 = String(d.color3 ?? '#fb7185')
  const background = String(d.background ?? '#030712')
  const textColor = String(d.textColor ?? '#ffffff')
  return (
    <div style={{ position: 'relative', minHeight: '560px', background, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 40px', textAlign: 'center', overflow: 'hidden', color: textColor }}>
      <div style={{ position: 'absolute', width: '400px', height: '400px', borderRadius: '50%', background: color1, filter: 'blur(80px)', opacity: 0.25, top: '-100px', left: '-80px' }} />
      <div style={{ position: 'absolute', width: '350px', height: '350px', borderRadius: '50%', background: color2, filter: 'blur(80px)', opacity: 0.2, bottom: '-80px', right: '-60px' }} />
      <div style={{ position: 'absolute', width: '300px', height: '300px', borderRadius: '50%', background: color3, filter: 'blur(80px)', opacity: 0.15, top: '40%', right: '20%' }} />
      <div style={{ position: 'relative', zIndex: 1 }}>
        {badge && <div style={{ display: 'inline-block', padding: '6px 16px', borderRadius: '100px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', fontSize: '13px', fontWeight: 600, marginBottom: '24px' }}>{badge}</div>}
        <h1 style={{ fontSize: '56px', fontWeight: 900, lineHeight: 1.05, margin: 0, maxWidth: '780px' }}>{title}</h1>
        {subtitle && <p style={{ fontSize: '20px', opacity: 0.7, margin: '20px auto 0', maxWidth: '600px', lineHeight: 1.6 }}>{subtitle}</p>}
        {(primaryButtonText || secondaryButtonText) && (
          <div style={{ display: 'flex', gap: '14px', marginTop: '36px', justifyContent: 'center', flexWrap: 'wrap' }}>
            {primaryButtonText && <button style={{ padding: '14px 32px', background: color1, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 600, cursor: 'default' }}>{primaryButtonText}</button>}
            {secondaryButtonText && <button style={{ padding: '14px 32px', background: 'transparent', color: textColor, border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', fontSize: '16px', fontWeight: 600, cursor: 'default' }}>{secondaryButtonText}</button>}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Split Hero V2 ─────────────────────────────────────────────────────────────
function SplitHeroV2Renderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const eyebrow = String(d.eyebrow ?? '')
  const title = String(d.title ?? 'Создавайте сайты быстрее, чем когда-либо')
  const subtitle = String(d.subtitle ?? '')
  const primaryCta = String(d.primaryCta ?? 'Начать бесплатно')
  const secondaryCta = String(d.secondaryCta ?? '')
  const image = String(d.image ?? '')
  const imagePosition = String(d.imagePosition ?? 'left')
  const badge = String(d.badge ?? '')
  const features = Array.isArray(d.features) ? d.features as string[] : []
  const background = String(d.background ?? '#ffffff')
  const accentColor = String(d.accentColor ?? '#5b6af0')
  const isDark = isColorDark(background)
  const textColor = isDark ? '#fff' : '#111'
  const { isMobile } = useDevice()
  const textContent = (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: isMobile ? '48px 24px' : '60px 48px', gap: '20px', background }}>
      {eyebrow && <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: accentColor }}>{eyebrow}</div>}
      <h1 style={{ fontSize: isMobile ? '32px' : '44px', fontWeight: 800, color: textColor, lineHeight: 1.15, margin: 0 }}>{title}</h1>
      {subtitle && <p style={{ fontSize: '17px', color: textColor, opacity: 0.7, margin: 0, lineHeight: 1.6 }}>{subtitle}</p>}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        {primaryCta && <button style={{ padding: '13px 28px', background: accentColor, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 600, cursor: 'default' }}>{primaryCta}</button>}
        {secondaryCta && <button style={{ padding: '13px 28px', background: 'transparent', color: textColor, border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.3)' : '#ddd'}`, borderRadius: '8px', fontSize: '15px', fontWeight: 600, cursor: 'default' }}>{secondaryCta}</button>}
      </div>
      {badge && <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', background: `${accentColor}22`, borderRadius: '100px', fontSize: '13px', fontWeight: 600, color: accentColor, alignSelf: 'flex-start' }}>{badge}</div>}
      {features.length > 0 && <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>{features.map((f, i) => <span key={i} style={{ fontSize: '13px', color: textColor, opacity: 0.65, display: 'flex', alignItems: 'center', gap: '4px' }}>✓ {f}</span>)}</div>}
    </div>
  )
  const imageContent = (
    <div style={{ flex: 1, minHeight: isMobile ? '240px' : '480px', background: image ? undefined : `${accentColor}22`, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
      {image ? <img src={image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0 }} /> : <div style={{ fontSize: '48px', opacity: 0.3 }}>🖼</div>}
    </div>
  )
  return (
    <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : imagePosition === 'left' ? 'row' : 'row-reverse', minHeight: '480px' }}>
      {imageContent}{textContent}
    </div>
  )
}

// ── Typewriter Hero ────────────────────────────────────────────────────────────
function TypewriterHeroRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const prefix = String(d.prefix ?? 'Мы делаем')
  const words = Array.isArray(d.words) ? d.words as string[] : ['сайты быстрее', 'дизайн проще']
  const subtitle = String(d.subtitle ?? '')
  const ctaText = String(d.ctaText ?? 'Попробовать бесплатно')
  const background = String(d.background ?? '#0f172a')
  const textColor = String(d.textColor ?? '#ffffff')
  const accentColor = String(d.accentColor ?? '#5b6af0')
  const [wordIdx, setWordIdx] = useState(0)
  useEffect(() => { const t = setInterval(() => setWordIdx(i => (i + 1) % words.length), 1800); return () => clearInterval(t) }, [words.length])
  return (
    <div style={{ background, minHeight: '520px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '80px 40px', color: textColor }}>
      <h1 style={{ fontSize: '60px', fontWeight: 900, lineHeight: 1.1, margin: 0 }}>
        {prefix}{' '}<span style={{ color: accentColor }}>{words[wordIdx] ?? ''}</span><span style={{ color: accentColor }}>|</span>
      </h1>
      {subtitle && <p style={{ fontSize: '20px', opacity: 0.65, margin: '20px 0 0', maxWidth: '560px' }}>{subtitle}</p>}
      {ctaText && <button style={{ marginTop: '36px', padding: '14px 36px', background: accentColor, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 600, cursor: 'default' }}>{ctaText}</button>}
    </div>
  )
}

// ── Floating Badge Hero ───────────────────────────────────────────────────────
function FloatingBadgeHeroRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const eyebrow = String(d.eyebrow ?? '')
  const title = String(d.title ?? 'Создавайте сайты, которые продают')
  const subtitle = String(d.subtitle ?? '')
  const ctaText = String(d.ctaText ?? 'Попробовать бесплатно')
  const secondaryCta = String(d.secondaryCta ?? '')
  const image = String(d.image ?? '')
  const background = String(d.background ?? '#f0f4ff')
  const accentColor = String(d.accentColor ?? '#5b6af0')
  const isDark = isColorDark(background)
  const textColor = isDark ? '#fff' : '#111'
  const { isMobile } = useDevice()
  return (
    <div style={{ background, minHeight: '540px', display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: 'stretch', overflow: 'hidden' }}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: isMobile ? '48px 24px' : '60px 60px', gap: '20px' }}>
        {eyebrow && <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: accentColor }}>{eyebrow}</div>}
        <h1 style={{ fontSize: isMobile ? '32px' : '46px', fontWeight: 900, color: textColor, lineHeight: 1.1, margin: 0 }}>{title}</h1>
        {subtitle && <p style={{ fontSize: '18px', color: textColor, opacity: 0.65, margin: 0, lineHeight: 1.6 }}>{subtitle}</p>}
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {ctaText && <button style={{ padding: '13px 28px', background: accentColor, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 600, cursor: 'default' }}>{ctaText}</button>}
          {secondaryCta && <button style={{ padding: '13px 28px', background: 'transparent', color: textColor, border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.3)' : '#ddd'}`, borderRadius: '8px', fontSize: '15px', fontWeight: 600, cursor: 'default' }}>{secondaryCta}</button>}
        </div>
      </div>
      <div style={{ flex: 1, position: 'relative', minHeight: isMobile ? '280px' : undefined }}>
        {image ? <img src={image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} /> : <div style={{ width: '100%', height: '100%', background: `${accentColor}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '64px', opacity: 0.3 }}>🖼</div>}
      </div>
    </div>
  )
}

// ── App Download Hero ─────────────────────────────────────────────────────────
function AppDownloadHeroRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const eyebrow = String(d.eyebrow ?? 'Доступно на iOS и Android')
  const title = String(d.title ?? '')
  const subtitle = String(d.subtitle ?? '')
  const screenshot = String(d.screenshot ?? '')
  const features = Array.isArray(d.features) ? d.features as string[] : []
  const background = String(d.background ?? '#f0f4ff')
  const accentColor = String(d.accentColor ?? '#5b6af0')
  const appStoreRating = String(d.appStoreRating ?? '4.8')
  const googlePlayRating = String(d.googlePlayRating ?? '4.7')
  const isDark = isColorDark(background)
  const textColor = isDark ? '#fff' : '#111'
  const { isMobile } = useDevice()
  return (
    <div style={{ background, minHeight: '500px', display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: 'center', gap: '40px', padding: isMobile ? '48px 24px' : '60px 60px' }}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {eyebrow && <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: accentColor }}>{eyebrow}</div>}
        {title && <h1 style={{ fontSize: isMobile ? '32px' : '44px', fontWeight: 900, color: textColor, lineHeight: 1.15, margin: 0 }}>{title}</h1>}
        {subtitle && <p style={{ fontSize: '17px', color: textColor, opacity: 0.7, lineHeight: 1.6, margin: 0 }}>{subtitle}</p>}
        {features.length > 0 && <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '8px' }}>{features.map((f, i) => <li key={i} style={{ fontSize: '15px', color: textColor, display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ color: accentColor, fontWeight: 700 }}>✓</span>{f}</li>)}</ul>}
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button style={{ padding: '12px 20px', background: '#000', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 600, cursor: 'default', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.3 }}><span style={{ fontSize: '10px', opacity: 0.7 }}>★ {appStoreRating}</span><span>App Store</span></button>
          <button style={{ padding: '12px 20px', background: '#000', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 600, cursor: 'default', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.3 }}><span style={{ fontSize: '10px', opacity: 0.7 }}>★ {googlePlayRating}</span><span>Google Play</span></button>
        </div>
      </div>
      <div style={{ flex: '0 0 auto', width: isMobile ? '200px' : '260px', display: 'flex', justifyContent: 'center' }}>
        {screenshot ? <img src={screenshot} alt="" style={{ width: '100%', borderRadius: '24px', boxShadow: '0 24px 48px rgba(0,0,0,0.2)' }} /> : <div style={{ width: '200px', height: '360px', background: `${accentColor}22`, borderRadius: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '40px', opacity: 0.4 }}>📱</div>}
      </div>
    </div>
  )
}

// ── Mega Hero ─────────────────────────────────────────────────────────────────
function MegaHeroRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const title = String(d.title ?? 'Создавайте сайты быстрее чем когда-либо')
  const subtitle = String(d.subtitle ?? '')
  const primaryCta = String(d.primaryCta ?? 'Начать бесплатно')
  const secondaryCta = String(d.secondaryCta ?? '')
  const posterImage = String(d.posterImage ?? '')
  const background = String(d.background ?? '#0f172a')
  const textColor = String(d.textColor ?? '#ffffff')
  const accentColor = String(d.accentColor ?? '#5b6af0')
  const overlay = Number(d.overlay ?? 0.5)
  return (
    <div style={{ position: 'relative', minHeight: '600px', background: posterImage ? undefined : background, backgroundImage: posterImage ? `url(${posterImage})` : undefined, backgroundSize: 'cover', backgroundPosition: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '80px 40px', color: textColor }}>
      {posterImage && <div style={{ position: 'absolute', inset: 0, background: `rgba(0,0,0,${overlay})` }} />}
      <div style={{ position: 'relative', zIndex: 1, maxWidth: '860px' }}>
        <h1 style={{ fontSize: '64px', fontWeight: 900, lineHeight: 1.05, margin: 0, letterSpacing: '-0.02em' }}>{title}</h1>
        {subtitle && <p style={{ fontSize: '22px', opacity: 0.75, margin: '24px 0 0', lineHeight: 1.6 }}>{subtitle}</p>}
        <div style={{ display: 'flex', gap: '16px', marginTop: '40px', justifyContent: 'center', flexWrap: 'wrap' }}>
          {primaryCta && <button style={{ padding: '16px 36px', background: accentColor, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '17px', fontWeight: 700, cursor: 'default' }}>{primaryCta}</button>}
          {secondaryCta && <button style={{ padding: '16px 36px', background: 'rgba(255,255,255,0.15)', color: textColor, border: '1.5px solid rgba(255,255,255,0.35)', borderRadius: '8px', fontSize: '17px', fontWeight: 600, cursor: 'default' }}>{secondaryCta}</button>}
        </div>
      </div>
    </div>
  )
}

// ── Product Hero ──────────────────────────────────────────────────────────────
function ProductHeroRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const badge = String(d.badge ?? '')
  const title = String(d.title ?? 'Умные наушники Pro X')
  const subtitle = String(d.subtitle ?? '')
  const price = String(d.price ?? '')
  const oldPrice = String(d.oldPrice ?? '')
  const rating = Number(d.rating ?? 0)
  const reviewCount = Number(d.reviewCount ?? 0)
  const image = String(d.image ?? '')
  const ctaText = String(d.ctaText ?? 'Купить сейчас')
  const secondaryCta = String(d.secondaryCta ?? '')
  const features = Array.isArray(d.features) ? d.features as string[] : []
  const background = String(d.background ?? '#ffffff')
  const accentColor = String(d.accentColor ?? '#5b6af0')
  const isDark = isColorDark(background)
  const textColor = isDark ? '#fff' : '#111'
  const { isMobile } = useDevice()
  return (
    <div style={{ background, padding: isMobile ? '40px 24px' : '60px', display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: 'center', gap: '48px' }}>
      <div style={{ flex: '0 0 auto', width: isMobile ? '100%' : '420px', height: isMobile ? '280px' : '420px', background: image ? undefined : `${accentColor}15`, borderRadius: '16px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {image ? <img src={image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: '80px', opacity: 0.3 }}>📦</span>}
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {badge && <span style={{ display: 'inline-block', padding: '4px 12px', background: accentColor, color: '#fff', borderRadius: '4px', fontSize: '12px', fontWeight: 700, alignSelf: 'flex-start' }}>{badge}</span>}
        <h1 style={{ fontSize: '36px', fontWeight: 900, color: textColor, lineHeight: 1.15, margin: 0 }}>{title}</h1>
        {rating > 0 && <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><span style={{ color: '#f59e0b', fontSize: '16px' }}>{'★'.repeat(Math.round(rating))}</span><span style={{ fontSize: '14px', color: textColor, opacity: 0.6 }}>{rating.toFixed(1)} ({reviewCount} отзывов)</span></div>}
        {subtitle && <p style={{ fontSize: '16px', color: textColor, opacity: 0.7, margin: 0, lineHeight: 1.6 }}>{subtitle}</p>}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
          {price && <span style={{ fontSize: '36px', fontWeight: 900, color: textColor }}>{price}</span>}
          {oldPrice && <span style={{ fontSize: '20px', color: textColor, opacity: 0.4, textDecoration: 'line-through' }}>{oldPrice}</span>}
        </div>
        {features.length > 0 && <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>{features.map((f, i) => <span key={i} style={{ padding: '4px 10px', background: `${accentColor}15`, color: accentColor, borderRadius: '4px', fontSize: '13px', fontWeight: 600 }}>{f}</span>)}</div>}
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {ctaText && <button style={{ padding: '14px 32px', background: accentColor, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 700, cursor: 'default' }}>{ctaText}</button>}
          {secondaryCta && <button style={{ padding: '14px 24px', background: 'transparent', color: textColor, border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.3)' : '#ddd'}`, borderRadius: '8px', fontSize: '16px', fontWeight: 600, cursor: 'default' }}>{secondaryCta}</button>}
        </div>
      </div>
    </div>
  )
}

// ── Photo Hero Split ──────────────────────────────────────────────────────────
function PhotoHeroSplitRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const title = String(d.title ?? 'Создайте сайт который работает')
  const subtitle = String(d.subtitle ?? '')
  const image = String(d.image ?? '')
  const imagePosition = String(d.imagePosition ?? 'right')
  const ctaText = String(d.ctaText ?? 'Начать бесплатно')
  const secondaryCta = String(d.secondaryCta ?? '')
  const features = Array.isArray(d.features) ? d.features as string[] : []
  const background = String(d.background ?? '#ffffff')
  const accentColor = String(d.accentColor ?? '#5b6af0')
  const isDark = isColorDark(background)
  const textColor = isDark ? '#fff' : '#111'
  const { isMobile } = useDevice()
  const textContent = (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: isMobile ? '48px 24px' : '60px 48px', gap: '20px', background }}>
      <h1 style={{ fontSize: isMobile ? '32px' : '46px', fontWeight: 800, color: textColor, lineHeight: 1.15, margin: 0 }}>{title}</h1>
      {subtitle && <p style={{ fontSize: '18px', color: textColor, opacity: 0.7, margin: 0, lineHeight: 1.6 }}>{subtitle}</p>}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        {ctaText && <button style={{ padding: '13px 28px', background: accentColor, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 600, cursor: 'default' }}>{ctaText}</button>}
        {secondaryCta && <button style={{ padding: '13px 24px', background: 'transparent', color: textColor, border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.3)' : '#ddd'}`, borderRadius: '8px', fontSize: '15px', fontWeight: 600, cursor: 'default' }}>{secondaryCta}</button>}
      </div>
      {features.length > 0 && <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>{features.map((f, i) => <span key={i} style={{ fontSize: '13px', color: textColor, opacity: 0.6 }}>✓ {f}</span>)}</div>}
    </div>
  )
  const imageContent = (
    <div style={{ flex: 1, minHeight: isMobile ? '240px' : undefined, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: image ? undefined : `${accentColor}15` }}>
      {image ? <img src={image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: '64px', opacity: 0.25 }}>🖼</span>}
    </div>
  )
  return <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : imagePosition === 'left' ? 'row' : 'row-reverse', minHeight: '480px' }}>{imageContent}{textContent}</div>
}

// ── Hero Split V2 ─────────────────────────────────────────────────────────────
function HeroSplitV2Renderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const badge = String(d.badge ?? '')
  const title = String(d.title ?? 'Создайте что-то невероятное')
  const subtitle = String(d.subtitle ?? '')
  const primaryCta = String(d.primaryCta ?? 'Начать бесплатно')
  const secondaryCta = String(d.secondaryCta ?? '')
  const image = String(d.image ?? '')
  const imagePosition = String(d.imagePosition ?? 'right')
  const background = String(d.background ?? '#0f172a')
  const accentColor = String(d.accentColor ?? '#5b6af0')
  const textColor = '#ffffff'
  const { isMobile } = useDevice()
  const textContent = (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: isMobile ? '48px 24px' : '60px 48px', gap: '20px', background }}>
      {badge && <span style={{ display: 'inline-block', padding: '5px 14px', background: `${accentColor}33`, color: accentColor, borderRadius: '100px', fontSize: '12px', fontWeight: 700, alignSelf: 'flex-start' }}>{badge}</span>}
      <h1 style={{ fontSize: isMobile ? '32px' : '46px', fontWeight: 900, color: textColor, lineHeight: 1.1, margin: 0 }}>{title}</h1>
      {subtitle && <p style={{ fontSize: '17px', color: textColor, opacity: 0.65, margin: 0, lineHeight: 1.6 }}>{subtitle}</p>}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        {primaryCta && <button style={{ padding: '13px 28px', background: accentColor, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 600, cursor: 'default' }}>{primaryCta}</button>}
        {secondaryCta && <button style={{ padding: '13px 24px', background: 'transparent', color: textColor, border: '1px solid rgba(255,255,255,0.25)', borderRadius: '8px', fontSize: '15px', fontWeight: 600, cursor: 'default' }}>{secondaryCta}</button>}
      </div>
    </div>
  )
  const imageContent = (
    <div style={{ flex: 1, minHeight: isMobile ? '240px' : undefined, background: image ? undefined : `${accentColor}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
      {image ? <img src={image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: '64px', opacity: 0.25 }}>🖼</span>}
    </div>
  )
  return <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : imagePosition === 'left' ? 'row' : 'row-reverse', minHeight: '480px' }}>{imageContent}{textContent}</div>
}

// ── CTA Video ─────────────────────────────────────────────────────────────────
function CtaVideoRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const badge = String(d.badge ?? '')
  const title = String(d.title ?? 'Готовы начать?')
  const subtitle = String(d.subtitle ?? '')
  const primaryCta = String(d.primaryCta ?? 'Попробовать бесплатно')
  const secondaryCta = String(d.secondaryCta ?? '')
  const image = String(d.image ?? '')
  const overlayOpacity = parseFloat(String(d.overlayOpacity ?? '0.65'))
  return (
    <div style={{ position: 'relative', minHeight: '480px', background: image ? undefined : '#0f172a', backgroundImage: image ? `url(${image})` : undefined, backgroundSize: 'cover', backgroundPosition: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '60px 40px', color: '#fff' }}>
      <div style={{ position: 'absolute', inset: 0, background: `rgba(0,0,0,${overlayOpacity})` }} />
      <div style={{ position: 'relative', zIndex: 1, maxWidth: '700px' }}>
        {badge && <div style={{ display: 'inline-block', padding: '5px 16px', borderRadius: '100px', background: 'rgba(255,255,255,0.15)', fontSize: '13px', fontWeight: 600, marginBottom: '20px' }}>{badge}</div>}
        <h2 style={{ fontSize: '48px', fontWeight: 900, lineHeight: 1.1, margin: 0 }}>{title}</h2>
        {subtitle && <p style={{ fontSize: '20px', opacity: 0.75, margin: '16px 0 0' }}>{subtitle}</p>}
        <div style={{ display: 'flex', gap: '14px', marginTop: '32px', justifyContent: 'center', flexWrap: 'wrap' }}>
          {primaryCta && <button style={{ padding: '14px 32px', background: '#fff', color: '#0f172a', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 700, cursor: 'default' }}>{primaryCta}</button>}
          {secondaryCta && <button style={{ padding: '14px 24px', background: 'transparent', color: '#fff', border: '1.5px solid rgba(255,255,255,0.4)', borderRadius: '8px', fontSize: '16px', fontWeight: 600, cursor: 'default' }}>{secondaryCta}</button>}
        </div>
      </div>
    </div>
  )
}

// ── Hero With Stats ───────────────────────────────────────────────────────────
function HeroWithStatsRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const badge = String(d.badge ?? '')
  const title = String(d.title ?? 'Растите быстрее с нашей платформой')
  const subtitle = String(d.subtitle ?? '')
  const primaryCta = String(d.primaryCta ?? 'Попробовать бесплатно')
  const secondaryCta = String(d.secondaryCta ?? '')
  const stats = Array.isArray(d.stats) ? d.stats as Record<string, unknown>[] : []
  const background = String(d.background ?? '#0f172a')
  const accentColor = String(d.accentColor ?? '#5b6af0')
  return (
    <div style={{ background, minHeight: '560px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '80px 40px', color: '#fff' }}>
      {badge && <div style={{ display: 'inline-block', padding: '6px 18px', borderRadius: '100px', background: `${accentColor}33`, border: `1px solid ${accentColor}66`, color: accentColor, fontSize: '13px', fontWeight: 700, marginBottom: '24px' }}>{badge}</div>}
      <h1 style={{ fontSize: '54px', fontWeight: 900, lineHeight: 1.1, margin: 0, maxWidth: '800px' }}>{title}</h1>
      {subtitle && <p style={{ fontSize: '20px', opacity: 0.65, margin: '20px 0 0', maxWidth: '600px', lineHeight: 1.6 }}>{subtitle}</p>}
      <div style={{ display: 'flex', gap: '14px', marginTop: '36px', justifyContent: 'center', flexWrap: 'wrap' }}>
        {primaryCta && <button style={{ padding: '14px 32px', background: accentColor, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 600, cursor: 'default' }}>{primaryCta}</button>}
        {secondaryCta && <button style={{ padding: '14px 24px', background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.25)', borderRadius: '8px', fontSize: '16px', fontWeight: 600, cursor: 'default' }}>{secondaryCta}</button>}
      </div>
      {stats.length > 0 && (
        <div style={{ display: 'flex', gap: '0', marginTop: '60px', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '40px', width: '100%', maxWidth: '800px', flexWrap: 'wrap', justifyContent: 'center' }}>
          {stats.map((stat, i) => (
            <div key={i} style={{ flex: '1 0 140px', padding: '0 24px', borderRight: i < stats.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none' }}>
              <div style={{ fontSize: '36px', fontWeight: 900, color: accentColor }}>{String(stat.value ?? '')}</div>
              <div style={{ fontSize: '13px', color: '#fff', opacity: 0.5, marginTop: '4px' }}>{String(stat.label ?? '')}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Hero Video BG ─────────────────────────────────────────────────────────────
function HeroVideoBgRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const badge = String(d.badge ?? '')
  const title = String(d.title ?? 'Создайте сайт, который работает')
  const subtitle = String(d.subtitle ?? '')
  const ctaText = String(d.ctaText ?? '')
  const secondaryText = String(d.secondaryText ?? '')
  const scrollText = String(d.scrollText ?? '')
  const overlayColor = String(d.overlayColor ?? 'rgba(0,0,0,0.55)')
  const accentColor = String(d.accentColor ?? '#f59e0b')
  return (
    <div style={{ position: 'relative', minHeight: '600px', background: '#111', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '80px 40px', color: '#fff', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(135deg, #0f0f1a, #1a1a2e)` }} />
      <div style={{ position: 'absolute', inset: 0, background: overlayColor }} />
      <div style={{ position: 'absolute', top: '20%', left: '10%', width: '300px', height: '300px', borderRadius: '50%', background: accentColor, filter: 'blur(100px)', opacity: 0.15 }} />
      <div style={{ position: 'relative', zIndex: 1, maxWidth: '760px' }}>
        {badge && <div style={{ display: 'inline-block', padding: '6px 18px', borderRadius: '100px', background: `${accentColor}33`, color: accentColor, fontSize: '13px', fontWeight: 700, marginBottom: '24px', border: `1px solid ${accentColor}66` }}>{badge}</div>}
        <h1 style={{ fontSize: '60px', fontWeight: 900, lineHeight: 1.05, margin: 0, letterSpacing: '-0.02em' }}>{title}</h1>
        {subtitle && <p style={{ fontSize: '20px', opacity: 0.7, margin: '20px 0 0', lineHeight: 1.6 }}>{subtitle}</p>}
        <div style={{ display: 'flex', gap: '14px', marginTop: '36px', justifyContent: 'center', flexWrap: 'wrap' }}>
          {ctaText && <button style={{ padding: '14px 32px', background: accentColor, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 700, cursor: 'default' }}>{ctaText}</button>}
          {secondaryText && <button style={{ padding: '14px 24px', background: 'transparent', color: '#fff', border: '1.5px solid rgba(255,255,255,0.3)', borderRadius: '8px', fontSize: '16px', fontWeight: 600, cursor: 'default' }}>{secondaryText}</button>}
        </div>
        {scrollText && <div style={{ marginTop: '48px', fontSize: '12px', opacity: 0.4, letterSpacing: '2px', textTransform: 'uppercase' }}>{scrollText} ↓</div>}
      </div>
    </div>
  )
}

// ── CTA Gradient ──────────────────────────────────────────────────────────────
function CtaGradientRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const badge = String(d.badge ?? '')
  const title = String(d.title ?? 'Начните прямо сейчас')
  const subtitle = String(d.subtitle ?? '')
  const primaryCta = String(d.primaryCta ?? 'Попробовать бесплатно')
  const secondaryCta = String(d.secondaryCta ?? '')
  const gradientFrom = String(d.gradientFrom ?? '#5b6af0')
  const gradientTo = String(d.gradientTo ?? '#8b5cf6')
  return (
    <div style={{ background: `linear-gradient(135deg, ${gradientFrom}, ${gradientTo})`, padding: '80px 40px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', color: '#fff' }}>
      {badge && <div style={{ display: 'inline-block', padding: '5px 16px', borderRadius: '100px', background: 'rgba(255,255,255,0.2)', fontSize: '13px', fontWeight: 600, marginBottom: '20px' }}>{badge}</div>}
      <h2 style={{ fontSize: '48px', fontWeight: 900, lineHeight: 1.1, margin: 0, maxWidth: '700px' }}>{title}</h2>
      {subtitle && <p style={{ fontSize: '20px', opacity: 0.85, margin: '16px 0 0', maxWidth: '560px' }}>{subtitle}</p>}
      <div style={{ display: 'flex', gap: '14px', marginTop: '36px', flexWrap: 'wrap', justifyContent: 'center' }}>
        {primaryCta && <button style={{ padding: '14px 32px', background: '#fff', color: gradientFrom, border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 700, cursor: 'default' }}>{primaryCta}</button>}
        {secondaryCta && <button style={{ padding: '14px 24px', background: 'transparent', color: '#fff', border: '1.5px solid rgba(255,255,255,0.5)', borderRadius: '8px', fontSize: '16px', fontWeight: 600, cursor: 'default' }}>{secondaryCta}</button>}
      </div>
    </div>
  )
}

// ── Image Banner ──────────────────────────────────────────────────────────────
function ImageBannerRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const background = String(d.background ?? '#1e293b')
  const accentColor = String(d.accentColor ?? '#f59e0b')
  const textSide = String(d.textSide ?? 'left')
  const badge = String(d.badge ?? '')
  const title = String(d.title ?? '')
  const subtitle = String(d.subtitle ?? '')
  const ctaText = String(d.ctaText ?? '')
  const image = String(d.image ?? '')
  const { isMobile } = useDevice()
  const textContent = (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: isMobile ? '48px 24px' : '60px 48px', gap: '16px' }}>
      {badge && <span style={{ display: 'inline-block', padding: '4px 12px', background: accentColor, color: '#fff', borderRadius: '4px', fontSize: '12px', fontWeight: 700, alignSelf: 'flex-start' }}>{badge}</span>}
      {title && <h2 style={{ fontSize: isMobile ? '28px' : '42px', fontWeight: 900, color: '#fff', lineHeight: 1.15, margin: 0 }}>{title}</h2>}
      {subtitle && <p style={{ fontSize: '17px', color: '#fff', opacity: 0.7, margin: 0, lineHeight: 1.6 }}>{subtitle}</p>}
      {ctaText && <button style={{ padding: '12px 28px', background: accentColor, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 700, cursor: 'default', alignSelf: 'flex-start', marginTop: '8px' }}>{ctaText}</button>}
    </div>
  )
  const imageContent = (
    <div style={{ flex: 1, minHeight: isMobile ? '240px' : undefined, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: image ? undefined : `${accentColor}22` }}>
      {image ? <img src={image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: '64px', opacity: 0.25 }}>🖼</span>}
    </div>
  )
  return (
    <div style={{ background, display: 'flex', flexDirection: isMobile ? 'column' : textSide === 'left' ? 'row' : 'row-reverse', minHeight: '400px' }}>
      {textContent}{imageContent}
    </div>
  )
}

// ── Hero Dark ─────────────────────────────────────────────────────────────────
function HeroDarkRenderer({ block }: RendererProps) {
  const d = block.data as Record<string, unknown>
  const background = String(d.background ?? '#0a0a0a')
  const accentColor = String(d.accentColor ?? '#a78bfa')
  const badge = String(d.badge ?? '')
  const title = String(d.title ?? 'Технологии будущего уже здесь')
  const subtitle = String(d.subtitle ?? '')
  const ctaText = String(d.ctaText ?? '')
  const secondaryText = String(d.secondaryText ?? '')
  const metrics = Array.isArray(d.metrics) ? d.metrics as Record<string, unknown>[] : []
  const showMetrics = Boolean(d.showMetrics ?? true)
  return (
    <div style={{ background, minHeight: '600px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '80px 40px', color: '#fff', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: '-100px', left: '50%', transform: 'translateX(-50%)', width: '600px', height: '400px', background: accentColor, filter: 'blur(120px)', opacity: 0.1, borderRadius: '50%' }} />
      <div style={{ position: 'relative', zIndex: 1, maxWidth: '820px' }}>
        {badge && <div style={{ display: 'inline-block', padding: '6px 18px', borderRadius: '100px', background: `${accentColor}22`, border: `1px solid ${accentColor}44`, color: accentColor, fontSize: '13px', fontWeight: 700, marginBottom: '24px', letterSpacing: '0.5px' }}>{badge}</div>}
        <h1 style={{ fontSize: '62px', fontWeight: 900, lineHeight: 1.05, margin: 0, letterSpacing: '-0.02em' }}>{title}</h1>
        {subtitle && <p style={{ fontSize: '20px', opacity: 0.6, margin: '20px 0 0', lineHeight: 1.6 }}>{subtitle}</p>}
        <div style={{ display: 'flex', gap: '14px', marginTop: '36px', justifyContent: 'center', flexWrap: 'wrap' }}>
          {ctaText && <button style={{ padding: '14px 32px', background: accentColor, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 600, cursor: 'default' }}>{ctaText}</button>}
          {secondaryText && <button style={{ padding: '14px 24px', background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', fontSize: '16px', fontWeight: 600, cursor: 'default' }}>{secondaryText}</button>}
        </div>
        {showMetrics && metrics.length > 0 && (
          <div style={{ display: 'flex', gap: '0', marginTop: '56px', paddingTop: '36px', borderTop: '1px solid rgba(255,255,255,0.08)', flexWrap: 'wrap', justifyContent: 'center' }}>
            {metrics.map((m, i) => (
              <div key={i} style={{ flex: '1 0 120px', padding: '0 20px', borderRight: i < metrics.length - 1 ? '1px solid rgba(255,255,255,0.08)' : 'none' }}>
                <div style={{ fontSize: '28px', fontWeight: 900, color: accentColor, fontVariantNumeric: 'tabular-nums' }}>{String(m.value ?? '')}</div>
                <div style={{ fontSize: '12px', opacity: 0.4, marginTop: '4px', textTransform: 'uppercase', letterSpacing: '1px' }}>{String(m.label ?? '')}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

const renderers: Record<string, Renderer> = {
  heading: HeadingRenderer,
  paragraph: ParagraphRenderer,
  button: ButtonRenderer,
  'hero-centered': HeroCenteredRenderer,
  slider: SliderRenderer,
  countdown: CountdownRenderer,
  form: FormRenderer,
  image: ImageRenderer,
  video: VideoRenderer,
  features: FeaturesRenderer,
  testimonials: TestimonialsRenderer,
  faq: FAQRenderer,
  pricing: PricingRenderer,
  cta: CTARenderer,
  quote: QuoteRenderer,
  list: ListRenderer,
  divider: DividerRenderer,
  spacer: SpacerRenderer,
  gallery: GalleryRenderer,
  team: TeamRenderer,
  navbar: NavbarRenderer,
  footer: FooterRenderer,
  stats: StatsRenderer,
  tabs: TabsRenderer,
  map: MapRenderer,
  'social-links': SocialLinksRenderer,
  accordion: AccordionRenderer,
  progress: ProgressRenderer,
  timeline: TimelineRenderer,
  'before-after': BeforeAfterRenderer,
  'counter-anim': CounterAnimRenderer,
  'html-embed': HtmlEmbedRenderer,
  'hero-split': HeroSplitRenderer,
  'logo-strip': LogoStripRenderer,
  'process-steps': ProcessStepsRenderer,
  'contact-block': ContactBlockRenderer,
  newsletter: NewsletterRenderer,
  table: TableRenderer,
  'icon-features': IconFeaturesRenderer,
  banner: BannerRenderer,
  'testimonial-single': TestimonialSingleRenderer,
  'video-hero': VideoHeroRenderer,
  'image-text': ImageTextRenderer,
  'card-grid': CardGridRenderer,
  'two-columns': TwoColumnsRenderer,
  'highlight-box': HighlightBoxRenderer,
  'code-block': CodeBlockRenderer,
  'masonry-gallery': MasonryGalleryRenderer,
  comparison: ComparisonRenderer,
  awards: AwardsRenderer,
  'guarantee-bar': GuaranteeBarRenderer,
  'hero-minimal': HeroMinimalRenderer,
  'testimonials-carousel': TestimonialsCarouselRenderer,
  'image-carousel': ImageCarouselRenderer,
  'product-showcase': ProductShowcaseRenderer,
  'product-grid': ProductGridRenderer,
  'payment-logos': PaymentLogosRenderer,
  quiz: QuizRenderer,
  calculator: CalculatorRenderer,
  'chat-widget': ChatWidgetRenderer,
  'announcement-bar': AnnouncementBarRenderer,
  popup: PopupRenderer,
  'scroll-snap': ScrollSnapRenderer,
  parallax: ParallaxRenderer,
  typewriter: TypewriterRenderer,
  'gradient-hero': GradientHeroRenderer,
  'side-by-side-cta': SideBySideCtaRenderer,
  'hero-gradient': HeroGradientRenderer,
  'gradient-blob-hero': GradientBlobHeroRenderer,
  'split-hero-v2': SplitHeroV2Renderer,
  'typewriter-hero': TypewriterHeroRenderer,
  'floating-badge-hero': FloatingBadgeHeroRenderer,
  'app-download-hero': AppDownloadHeroRenderer,
  'mega-hero': MegaHeroRenderer,
  'product-hero': ProductHeroRenderer,
  'photo-hero-split': PhotoHeroSplitRenderer,
  'hero-split-v2': HeroSplitV2Renderer,
  'cta-video': CtaVideoRenderer,
  'hero-with-stats': HeroWithStatsRenderer,
  'hero-video-bg': HeroVideoBgRenderer,
  'cta-gradient': CtaGradientRenderer,
  'image-banner': ImageBannerRenderer,
  'hero-dark': HeroDarkRenderer,
  // Swiss Style — монолитный блок
  'swiss-page': ({ block }) => {
    const swissData = useMemo(() => mergeSwissData(block.data as Record<string, unknown>), [block.data])
    const PREVIEW_WIDTH = 1440
    const [height, setHeight] = useState(4200)
    const [containerWidth, setContainerWidth] = useState(900)
    const [insertMode, setInsertMode] = useState<string | null>(null)
    const [ghostPos, setGhostPos] = useState<{ x: number; y: number } | null>(null)
    const iframeRef = useRef<HTMLIFrameElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const isPreview = typeof window !== 'undefined' && window.location.pathname === '/preview'
    const srcDoc = useMemo(() => getSwissPageHtml(swissData, isPreview ? 'preview' : 'canvas'), [swissData, isPreview])
    const selectBlock = useEditorStore(s => s.selectBlock)
    const scale = containerWidth / PREVIEW_WIDTH

    const handleLoad = useCallback(() => {
      setTimeout(() => {
        try {
          const h = iframeRef.current?.contentDocument?.documentElement?.scrollHeight
          if (h && h > 500) setHeight(h)
        } catch {}
      }, 600)
    }, [])

    // Dynamic scale — measure container width
    useEffect(() => {
      if (isPreview || !containerRef.current) return
      const initial = containerRef.current.getBoundingClientRect().width
      if (initial > 100) setContainerWidth(initial)
      const ro = new ResizeObserver(entries => {
        const w = entries[0]?.contentRect.width
        if (w && w > 100) setContainerWidth(w)
      })
      ro.observe(containerRef.current)
      return () => ro.disconnect()
    }, [isPreview])

    // Relay iframe click → open field accordion
    useEffect(() => {
      if (isPreview) return
      const handler = (e: MessageEvent) => {
        if (e.data?.type === 'bst-swiss-click') {
          selectBlock(block.id)
          if (e.data.field) {
            window.dispatchEvent(new CustomEvent('swiss-field-click', { detail: e.data.field }))
          }
        }
      }
      window.addEventListener('message', handler)
      return () => window.removeEventListener('message', handler)
    }, [block.id, selectBlock, isPreview])

    // Listen for insert-start / cancel from SwissPageProperties
    useEffect(() => {
      if (isPreview) return
      const onStart = (e: Event) => {
        const d = (e as CustomEvent<{ blockId: string; elementType: string }>).detail
        if (d.blockId !== block.id) return
        setInsertMode(d.elementType)
        setGhostPos(null)
      }
      const onCancel = (e: Event) => {
        const d = (e as CustomEvent<{ blockId: string }>).detail
        if (d.blockId !== block.id) return
        setInsertMode(null)
        setGhostPos(null)
      }
      window.addEventListener('bst-swiss-insert-start', onStart as EventListener)
      window.addEventListener('bst-swiss-cancel-insert', onCancel as EventListener)
      return () => {
        window.removeEventListener('bst-swiss-insert-start', onStart as EventListener)
        window.removeEventListener('bst-swiss-cancel-insert', onCancel as EventListener)
      }
    }, [block.id, isPreview])

    // ESC cancels insert
    useEffect(() => {
      if (!insertMode) return
      const handler = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          setInsertMode(null)
          setGhostPos(null)
          window.dispatchEvent(new CustomEvent('bst-swiss-insert-cancelled', { detail: { blockId: block.id } }))
        }
      }
      window.addEventListener('keydown', handler)
      return () => window.removeEventListener('keydown', handler)
    }, [block.id, insertMode])

    const handleOverlayMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
      if (!containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()
      setGhostPos({ x: e.clientX - rect.left, y: e.clientY - rect.top })
    }, [])

    const handleOverlayClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
      if (!insertMode || !containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()
      const pageX = Math.round((e.clientX - rect.left) / scale)
      const pageY = Math.round((e.clientY - rect.top) / scale)
      window.dispatchEvent(new CustomEvent('bst-swiss-element-placed', {
        detail: { blockId: block.id, elementType: insertMode, pageX, pageY }
      }))
      setInsertMode(null)
      setGhostPos(null)
    }, [block.id, insertMode, scale])

    if (isPreview) {
      return (
        <iframe
          srcDoc={srcDoc}
          style={{ width: '100%', height: 'calc(100vh - 40px)', border: 'none', display: 'block' }}
          title="Swiss Style Preview"
        />
      )
    }

    return (
      <div ref={containerRef} style={{ width: '100%', position: 'relative', background: 'var(--bg-0, #f0f0f0)', padding: '20px 0', overflow: 'hidden' }}>
        <div style={{
          width: `${containerWidth}px`,
          height: `${Math.round(height * scale)}px`,
          overflow: 'hidden',
          boxShadow: '0 2px 20px rgba(0,0,0,0.15)',
        }}>
          <iframe
            ref={iframeRef}
            key={swissData.variant}
            srcDoc={srcDoc}
            style={{
              width: `${PREVIEW_WIDTH}px`,
              height: `${height}px`,
              border: 'none',
              display: 'block',
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
            }}
            onLoad={handleLoad}
            title="Swiss Style Preview"
          />
        </div>
        {insertMode && (
          <div
            style={{ position: 'absolute', inset: 0, zIndex: 100, cursor: 'crosshair' }}
            onMouseMove={handleOverlayMouseMove}
            onClick={handleOverlayClick}
          >
            {ghostPos && (
              <div style={{
                position: 'absolute',
                left: ghostPos.x,
                top: ghostPos.y,
                pointerEvents: 'none',
                opacity: 0.8,
                transform: 'translate(-50%, -50%)',
              }}>
                <SwissGhostElement type={insertMode} scale={scale} />
              </div>
            )}
          </div>
        )}
      </div>
    )
  },
  // Modern SaaS / Glassmorphism — monolithic block
  'glassmorphism-page': ({ block, selected, onUpdate }) => <FullPageIframeBlock block={block} selected={selected} onUpdate={onUpdate} getHtml={(b, m) => getGlassmorphPageHtml({ brand: b as any }, m)} defaultBrand="pulse" title="Modern SaaS Landing" />,
  'swiss-landing-page':    ({ block, selected, onUpdate }) => <FullPageIframeBlock block={block} selected={selected} onUpdate={onUpdate} getHtml={(b, m) => getSwissLandingPageHtml({ brand: b as any }, m)} defaultBrand="bst" title="Swiss Landing" />,
  'brutal-landing-page':   ({ block, selected, onUpdate }) => <FullPageIframeBlock block={block} selected={selected} onUpdate={onUpdate} getHtml={(b, m) => getBrutalLandingPageHtml({ brand: b as any }, m)} defaultBrand="acid" title="Brutal Landing" />,
  'serif-landing-page':    ({ block, selected, onUpdate }) => <FullPageIframeBlock block={block} selected={selected} onUpdate={onUpdate} getHtml={(b, m) => getSerifLandingPageHtml({ brand: b as any }, m)} defaultBrand="noir" title="Serif Landing" />,
  'cinema-landing-page':   ({ block, selected, onUpdate }) => <FullPageIframeBlock block={block} selected={selected} onUpdate={onUpdate} getHtml={(b, m) => getCinemaLandingPageHtml({ brand: b as any }, m)} defaultBrand="noir" title="Cinema Landing" />,
  'maximal-landing-page':  ({ block, selected, onUpdate }) => <FullPageIframeBlock block={block} selected={selected} onUpdate={onUpdate} getHtml={(b, m) => getMaximalLandingPageHtml({ brand: b as any }, m)} defaultBrand="brutal" title="Maximal Landing" />,
  'y2k-landing-page':      ({ block, selected, onUpdate }) => <FullPageIframeBlock block={block} selected={selected} onUpdate={onUpdate} getHtml={(b, m) => getY2kLandingPageHtml({ brand: b as any }, m)} defaultBrand="core" title="Y2K Landing" />,
  'organic-landing-page':  ({ block, selected, onUpdate }) => <FullPageIframeBlock block={block} selected={selected} onUpdate={onUpdate} getHtml={(b, m) => getOrganicLandingPageHtml({ brand: b as any }, m)} defaultBrand="botanica" title="Organic Landing" />,
  'film-landing-page':     ({ block, selected, onUpdate }) => <FullPageIframeBlock block={block} selected={selected} onUpdate={onUpdate} getHtml={(b, m) => getFilmLandingPageHtml({ brand: b as any }, m)} defaultBrand="noir" title="Film Landing" />,
  'bento-landing-page':    ({ block, selected, onUpdate }) => <FullPageIframeBlock block={block} selected={selected} onUpdate={onUpdate} getHtml={(b, m) => getBentoLandingPageHtml({ brand: b as any }, m)} defaultBrand="noir" title="Bento Landing" />,
  'bento-portfolio-page':  ({ block, selected, onUpdate }) => <FullPageIframeBlock block={block} selected={selected} onUpdate={onUpdate} getHtml={(_b, m) => getBentoPortfolioPageHtml({}, m)} title="Bento Portfolio" />,
  'cinema-portfolio-page': ({ block, selected, onUpdate }) => <FullPageIframeBlock block={block} selected={selected} onUpdate={onUpdate} getHtml={(b, m) => getCinemaPortfolioPageHtml({ brand: b as any }, m)} defaultBrand="capital" title="Cinema Portfolio" />,
  'film-portfolio-page':   ({ block, selected, onUpdate }) => <FullPageIframeBlock block={block} selected={selected} onUpdate={onUpdate} getHtml={(_b, m) => getFilmPortfolioPageHtml({}, m)} title="Film Portfolio" />,
  'minimal-portfolio-page':({ block, selected, onUpdate }) => <FullPageIframeBlock block={block} selected={selected} onUpdate={onUpdate} getHtml={(b, m) => getMinimalPortfolioPageHtml({ brand: b as any }, m)} defaultBrand="acid" title="Minimal Portfolio" />,
  'organic-portfolio-page':({ block, selected, onUpdate }) => <FullPageIframeBlock block={block} selected={selected} onUpdate={onUpdate} getHtml={(_b, m) => getOrganicPortfolioPageHtml({}, m)} title="Organic Portfolio" />,
  'swiss-portfolio-page':  ({ block, selected, onUpdate }) => <FullPageIframeBlock block={block} selected={selected} onUpdate={onUpdate} getHtml={(b, m) => getSwissPortfolioPageHtml({ brand: b as any }, m)} defaultBrand="t1" title="Swiss Portfolio" />,
  'brutal-portfolio-page': ({ block, selected, onUpdate }) => <FullPageIframeBlock block={block} selected={selected} onUpdate={onUpdate} getHtml={(b, m) => getBrutalPortfolioPageHtml({ brand: b as any }, m)} defaultBrand="acid" title="Brutal Portfolio" />,
  'immersive-portfolio-page':({ block, selected, onUpdate }) => <FullPageIframeBlock block={block} selected={selected} onUpdate={onUpdate} getHtml={(b, m) => getImmersivePortfolioPageHtml({ brand: b as any }, m)} defaultBrand="noir" title="Immersive Portfolio" />,
  'ai-studio-page':        ({ block, selected, onUpdate }) => <FullPageIframeBlock block={block} selected={selected} onUpdate={onUpdate} getHtml={() => studioPageHtml(block.data as Record<string, unknown>, 'canvas')} title="AI-дизайн" />,
  'neo-portfolio-page':    ({ block, selected, onUpdate }) => <FullPageIframeBlock block={block} selected={selected} onUpdate={onUpdate} getHtml={(b, m) => getNeoPortfolioPageHtml({ brand: b as any }, m)} defaultBrand="noir" title="Neo Portfolio" />,
  // Swiss Style — 10 блоков (старые сайты)
  'swiss-navbar':   ({ block }) => <SwissNavbarRenderer   data={block.data} />,
  'swiss-hero':     ({ block }) => <SwissHeroRenderer     data={block.data} />,
  'swiss-marquee':  ({ block }) => <SwissMarqueeRenderer  data={block.data} />,
  'swiss-manifest': ({ block }) => <SwissManifestRenderer data={block.data} />,
  'swiss-stats':    ({ block }) => <SwissStatsRenderer    data={block.data} />,
  'swiss-services': ({ block }) => <SwissServicesRenderer data={block.data} />,
  'swiss-works':    ({ block }) => <SwissWorksRenderer    data={block.data} />,
  'swiss-quote':    ({ block }) => <SwissQuoteRenderer    data={block.data} />,
  'swiss-contact':  ({ block }) => <SwissContactRenderer  data={block.data} />,
  'swiss-footer':   ({ block }) => <SwissFooterRenderer   data={block.data} />,
}

function CustomBlockRenderer({ block }: RendererProps) {
  const html = String(block.data.html ?? '')
  const css = String(block.data.css ?? '')
  const js = String(block.data.js ?? '')
  const fields = (block.data.fields ?? []) as Array<{ name: string; default: string }>

  const rendered = fields.reduce((h, f) => {
    const value = String(block.data[f.name] ?? f.default ?? '')
    return h.replaceAll(`{{${f.name}}}`, value)
  }, html)

  const scopeClass = `cbst-${block.id}`

  return (
    <div className={scopeClass}>
      {css && (
        <style>{`.${scopeClass} { ${css} }`}</style>
      )}
      <div dangerouslySetInnerHTML={{ __html: rendered }} />
      {js && (
        <script dangerouslySetInnerHTML={{ __html: js }} />
      )}
    </div>
  )
}

export function BlockRenderer(props: RendererProps) {
  if (props.block.type === 'custom') return <CustomBlockRenderer {...props} />
  const Component = renderers[props.block.type]
  if (!Component) {
    return (
      <div style={{ padding: '20px 24px', color: '#888', fontStyle: 'italic', fontSize: '13px' }}>
        Блок: {props.block.type}
      </div>
    )
  }
  return <Component {...props} />
}
